import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Editor } from "@tiptap/react";
import {
  applyCommentMarkToRange,
  findTargetTextRangeInEditor,
  removeCommentMarkFromEditorByCommentId,
  scrollAndFlashChapterComment,
} from "./utils/commentEditorMarks";
import { marked } from "marked";
import { ensureConnectorStates, normalizePhase3State } from "./lib/phaseRuntime";
import { tipTapJsonToPlainText, tryParseStringifiedTipTapDoc } from "../shared/tipTapPlainText";
import { normalizeDesktopCommentType } from "../shared/chapterCommentLabels";
import {
  storyCraftComments,
  unresolvedCommentsForVariation,
  withoutStoryCraftComments,
} from "../shared/chapterCommentContext";
import type { DesktopStoryCraftFeedback } from "../shared/storyCraftFeedback";
import {
  averageStoryCraftScore,
  loadPersistedStoryCraftFeedback,
  storyCraftAnalyzingPlaceholder,
  storyCraftErrorFeedback,
  buildStoryCraftChecklistVariationInstructions,
  createStoryCraftChecklistItem,
} from "../shared/storyCraftFeedback";
import { useManuscriptEditing } from "./hooks/useManuscriptEditing";
import { useWorkbenchPersona } from "./hooks/useWorkbenchPersona";
import { cloudAuthDesktopV2, signInForCurrentRuntime } from "./services/cloudAuthDesktopV2";
import { searchWritingResourcesForChat } from "./services/writingResourcesClient";
import {
  formatStoryCraftForChat,
  formatWritingResourcesForChat,
} from "../shared/storyCraftChatContext";
import { STORY_CRAFT_AREA_IDS, type StoryCraftAreaId } from "../shared/craftAreas";
import {
  type AttachedChatVersion,
  currentVersionIdFromMetadata,
  displayVersionLabel,
  excerptForChat,
  formatAttachedVersionContext,
  versionsFromBookMetadata,
} from "../shared/chatAttachVersion";
import {
  normalizeScoreBlend,
  resolveChapterListScore,
  type ScoreBlendSettings,
} from "../shared/scoreBlend";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./components/ui/alert-dialog";
import { ConflictReview } from "./components/workbench/ConflictReview";
import {
  applyPendingConflictResolutions,
  bookHasUnpushedEdits,
  seamlessConflictResolutions,
} from "../shared/desktopV2DbSync";
import type { ThreeWayMergeResult } from "../shared/chapterSync/types";
import type { ChapterConflictResolution } from "../shared/chapterSync/types";
import {
  buildOverwriteAnnotationPayload,
  buildOverwriteSummary,
  regionsFromHunkResolutions,
} from "@repo/shared/overwriteAnnotations";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./components/ui/dialog";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import type { Book, Chapter, ChapterVariation } from "./types";
import { createDefaultBook } from "../shared/sbkCompat";
import {
  bibliographyChapterFields,
  collectCitationUsesFromContent,
  createCitationUse,
  formatCitationLibraryForChat,
  formatInTextCitation,
  getCitationState,
  replaceChapterCitationUses,
  withCitationsInMetadata,
  type Citation,
} from "../shared/citations";
import {
  createResearchAsset,
  formatResearchCatalogForChat,
  getResearchState,
  upsertResearchAsset,
  withResearchInMetadata,
  type ResearchAsset,
} from "../shared/researchAssets";
import {
  defaultBodyTitle,
  isEditorTabEnabled,
  profileFromBookMetadata,
  type CreateBookOptions,
  type EditorTabId,
} from "../shared/bookType";
import { normalizeWritingGoalWords } from "../shared/models";
import { normalizeThemeMode, resolveThemeDataAttribute } from "../shared/uiThemes";
import { ActivityBar, type WorkbenchView } from "./components/workbench/ActivityBar";
import {
  AssistantPanel,
  AssistantPanelRail,
  type AssistantPanelTab,
  type ChatMessage,
  type SceneToolId,
} from "./components/workbench/AssistantPanel";
import { EditorWorkbench } from "./components/workbench/EditorWorkbench";
import { PlanningPanel, type PlanningMode } from "./components/workbench/PlanningPanel";
import { PlotGridPanel } from "./components/workbench/PlotGridPanel";
import { CodexPanel } from "./components/workbench/CodexPanel";
import { buildSceneToolPrompt } from "../shared/sceneTools";
import { buildCodexContextSnippet, getCodex, seedCodexFromRosters } from "../shared/codex";
import {
  setChapterScenes,
  splitScenesFromContent,
  updateScene,
} from "../shared/chapterScenes";
import { trackDesktopActivation } from "./lib/activationEvents";
import { isAccountPrimaryRuntime, isWebRuntime } from "./lib/runtime";
import { userFacingError } from "../shared/userFacingError";
import { AudioPanel } from "./components/workbench/AudioPanel";
import {
  CHAPTER_AUDIO_VOICE_FIELD,
  normalizeBookAudioSettings,
  readBookAudioSettings,
} from "../shared/bookAudioSettings";
import { StoryCraftChapterPanel } from "./components/workbench/StoryCraftChapterPanel";
import { ChapterBriefPanel } from "./components/workbench/ChapterBriefPanel";
import { ChapterCharactersPanel } from "./components/workbench/ChapterCharactersPanel";
import { CharactersGuidePanel } from "./components/workbench/CharactersGuidePanel";
import { NarratorModePanel } from "./components/workbench/NarratorModePanel";
import { RemoveEntityDialog } from "./components/workbench/RemoveEntityDialog";
import { VariationReasonDialog } from "./components/workbench/VariationReasonDialog";
import { ChapterLocationsPanel } from "./components/workbench/ChapterLocationsPanel";
import { LocationsGuidePanel } from "./components/workbench/LocationsGuidePanel";
import { ChapterTimelinePanel } from "./components/workbench/ChapterTimelinePanel";
import { TimelineGuidePanel } from "./components/workbench/TimelineGuidePanel";
import { ChapterThemesPanel } from "./components/workbench/ChapterThemesPanel";
import { ThemesGuidePanel } from "./components/workbench/ThemesGuidePanel";
import {
  chapterCharacterTabText,
  createBlankLevers,
  generateCharacterId,
  getCharactersFromBookMetadata,
  getCharacterRemovalsFromMetadata,
  createCharacterRemovalRecord,
  mergeExtractedCharactersIntoRoster,
  mergeHolisticCharactersIntoRoster,
  normalizeBookCharacter,
  seedRosterFromChapterTabs,
  sortCharactersMajorFirst,
  withCharactersInMetadata,
  withCharacterRemovalsInMetadata,
  type BookCharacter,
  type CharacterRole,
} from "../shared/characterRoster";
import {
  ensureCharacterColors,
  getNarrationFromBookMetadata,
} from "../shared/bookNarration";
import {
  chapterLocationTabText,
  createLocationRemovalRecord,
  generateLocationId,
  getLocationRelationshipsFromMetadata,
  getLocationRemovalsFromMetadata,
  getLocationsFromBookMetadata,
  mergeExtractedLocationsIntoRoster,
  mergeExtractedRelationships,
  mergeHolisticLocationsIntoRoster,
  normalizeBookLocation,
  seedLocationsFromChapterTabs,
  sortLocations,
  withLocationRemovalsInMetadata,
  withLocationsInMetadata,
  type BookLocation,
  type LocationRelationship,
} from "../shared/locationRoster";
import {
  chapterTimelineTabText,
  createTimelineRemovalRecord,
  generateTimelineBeatId,
  getTimelineBeatsFromBookMetadata,
  getTimelineIssuesFromBookMetadata,
  getTimelineRemovalsFromMetadata,
  mergeExtractedBeatsIntoRoster,
  mergeExtractedIssuesIntoRoster,
  mergeHolisticBeatsIntoRoster,
  seedBeatsFromChapterTabs,
  sortBeatsChronologically,
  withTimelineInMetadata,
  withTimelineRemovalsInMetadata,
  type BookTimelineBeat,
} from "../shared/timelineRoster";
import {
  chapterThemesMotifsTabText,
  createMotifRemovalRecord,
  createSymbolRemovalRecord,
  createThemeRemovalRecord,
  generateMotifId,
  generateSymbolId,
  generateThemeId,
  getMotifRemovalsFromMetadata,
  getMotifsFromBookMetadata,
  getSymbolRemovalsFromMetadata,
  getSymbolsFromBookMetadata,
  getThemeRemovalsFromMetadata,
  getThemesFromBookMetadata,
  mergeExtractedMotifsIntoRoster,
  mergeExtractedSymbolsIntoRoster,
  mergeExtractedThemesIntoRoster,
  mergeHolisticMotifsIntoRoster,
  mergeHolisticSymbolsIntoRoster,
  mergeHolisticThemesIntoRoster,
  normalizeBookMotif,
  normalizeBookSymbol,
  normalizeBookTheme,
  sortByName,
  sortThemesMajorFirst,
  themesMotifsRosterAsString,
  withMotifRemovalsInMetadata,
  withSymbolRemovalsInMetadata,
  withThemeRemovalsInMetadata,
  withThemesMotifsInMetadata,
  type BookMotif,
  type BookSymbol,
  type BookTheme,
  type ThemeKind,
} from "../shared/themeRoster";
import {
  ANTHROPIC_CHAT_MODELS,
  llmProviderForModel,
  OPENAI_CHAT_MODELS,
  readPersistedDefaultChatModel,
  writePersistedDefaultChatModel,
  type LlmModelValue,
} from "../shared/llmModels";
import {
  getBookAnalysisModel,
  withBookAnalysisModel,
} from "../shared/bookAnalysisModel";
import {
  storyCraftUsesFullManuscript,
  type StoryCraftManuscriptChapter,
} from "../shared/storyCraftManuscript";
import { isTextEditingTarget } from "./components/workbench/focusGuards";
import type { IdeTabItem } from "./components/workbench/IdeTabStrip";
import {
  clampLeftWidth,
  clampRightWidth,
  DEFAULT_LEFT_PANEL_WIDTH,
  DEFAULT_RIGHT_PANEL_WIDTH,
} from "./components/workbench/panelSizing";
import { SidebarChaptersPanel, SidebarChaptersRail, type ChapterStatus } from "./components/workbench/SidebarChaptersPanel";
import { TopTitleBar } from "./components/workbench/TopTitleBar";
import { WorkbenchLayout } from "./components/workbench/WorkbenchLayout";
import { SettingsPanel } from "./components/workbench/SettingsPanel";
import { BetaReadersPanel } from "./components/workbench/BetaReadersPanel";
import { AdminFeedbackPanel } from "./components/workbench/AdminFeedbackPanel";
import { checkSysAdmin } from "./services/adminFeedback";
import { PublishPanel } from "./components/workbench/PublishPanel";
import { ChapterChangeHistoryPanel } from "./components/workbench/ChapterChangeHistoryPanel";
import { usePremierLiveCollab } from "./hooks/usePremierLiveCollab";
import { TranslateBookDialog } from "./components/workbench/TranslateBookDialog";
import { BookshelfDialog } from "./components/workbench/BookshelfDialog";
import { editorFeedbackFromChapterFields } from "./components/workbench/EditorScoreForm";
import type { StoryCraftAssessment } from "../shared/storyCraftFeedback";
import { WelcomeScreen } from "./components/workbench/WelcomeScreen";
import { BookTypePickerDialog } from "./components/workbench/BookTypePickerDialog";
import { ResearchPanel } from "./components/workbench/ResearchPanel";
import { rememberCloudBookLocalPath } from "./services/cloudBookPaths";
import { registerDesktopLocalCopy } from "./services/desktopLocalCopy";
import { autoSyncBook, noteChapterDeleted, pullRemoteOverrideLocal } from "./services/desktopV2AutoSync";
import { bookChapterSync } from "./services/bookChapterSync";
import { cacheWebBookDraft, cloudVirtualPath, upsertBookshelfSummary } from "./services/webIdbStore";
import { loadBookFromBackupBytes, writeDesktopBackup } from "./services/desktopBackup";
import { PREMIER_SEAMLESS_SYNC } from "./lib/premierFlags";
import {
  canDeleteDesktopChapterComment,
  canEditDesktopChapterComment,
  canResolveDesktopChapterComment,
} from "../shared/chapterCommentPermissions";
import {
  appendChatMessage,
  addChatThread,
  archiveChatThread,
  clearChatThread,
  createEmptyChatThread,
  getActiveChatThread,
  normalizeDesktopAssistantState,
  restoreChatThread,
  type DesktopAssistantState,
} from "../shared/desktopChatThreads";
import {
  buildChapterCatalog,
  formatChaptersForAssistantContext,
  reorderChaptersById,
  resolveChaptersFromRequest,
  type ChapterReorderPosition,
  type ChatBookChapter,
  type ChatBookMutation,
  type ChatBookSnapshot,
} from "../shared/chatChapterTools";
import { ManuscriptCommentToolbar } from "./components/workbench/ManuscriptCommentToolbar";
import { ManuscriptCommentComposer } from "./components/workbench/ManuscriptCommentComposer";
import { MoveChapterDialog } from "./components/workbench/MoveChapterDialog";
import {
  fetchCloudBookDesktop,
  isCloudOpenAvailable,
  openCloudBookToDisk,
} from "./services/openFromCloud";
import {
  chapterContentToPlainText,
  normalizeChapterContentForStorage,
  plainTextToChapterDoc,
  serializeChapterContent,
} from "../shared/chapterContent";
import {
  buildAppliedChapterContentFromMerge,
  buildVariationContentFromRewrite,
  refreshVariationContentFormatting,
  snapshotVariationBaseContent,
} from "../shared/chapterVariationRichText";
import {
  defaultTitleForKind,
  withSpineOrderAndNumbers,
  type BookContentKind,
} from "../shared/bookSpine";
import {
  captureCommentSelection,
  chapterContentBodyChanged,
  chapterPlainTextForSync,
  chapterPlainTextForDiff,
} from "./utils/manuscriptEditorHelpers";
import { blockIdAtPos } from "@repo/shared/blockIdAtPos";
import {
  flattenReplyParentId,
  removeCommentOrThread,
} from "@repo/shared/chapterCommentThreads";
import {
  CHAPTER_VARIATION_GENERATE_PROMPT,
  CHAPTER_VARIATION_START_PROMPT,
} from "../shared/chapterVariationIntent";
import { getMasterPrompt, resolvePromptFor } from "../shared/masterPrompt";
import type { ParagraphDiffHunk } from "../shared/chapterParagraphDiff";

const TABS = [
  { id: "manuscript", label: "Manuscript" },
  { id: "summary", label: "Chapter Brief" },
  { id: "storyCraft", label: "Story Craft" },
  { id: "history", label: "History" },
  { id: "characters", label: "Characters" },
  { id: "locations", label: "Locations" },
  { id: "timeline", label: "Timeline" },
  { id: "themesMotifs", label: "Themes & Motifs" },
] as const satisfies ReadonlyArray<{ id: EditorTabId; label: string }>;
type TabId = (typeof TABS)[number]["id"];

// Mirrors the web app feedback form (apps/web/app/(guest)/app-feedback/page.tsx).
const FEEDBACK_TYPE_OPTIONS = [
  { value: "bug", label: "Bug" },
  { value: "feature_request", label: "Feature request" },
  { value: "general", label: "General feedback" },
  { value: "ui_ux", label: "UI/UX" },
  { value: "performance", label: "Performance" },
] as const;
const FEEDBACK_RATING_OPTIONS = [1, 2, 3, 4, 5] as const;

type ChatTurn = {
  role: "user" | "assistant";
  content: string;
};

type UploadedChatImage = {
  name: string;
  mimeType: string;
  dataUrl: string;
};

const OPENAI_CHAT_MODELS_LIST = OPENAI_CHAT_MODELS;
const ANTHROPIC_CHAT_MODELS_LIST = ANTHROPIC_CHAT_MODELS;

type ChatModelValue = LlmModelValue;

const EDITOR_TAB_ITEMS: IdeTabItem<TabId>[] = TABS.map((tab) => ({ id: tab.id, label: tab.label }));

const CHAT_STARTER_PROMPTS = {
  review: "Review my current chapter and suggest improvements",
  grammar: "Fix any grammar or spelling issues in this chapter",
  dialogue: "Make the dialogue more natural in this chapter",
  rewrite: "Rewrite the opening paragraph to increase tension while keeping voice consistent",
  continuity: "Check this chapter for continuity issues against earlier chapters",
  variation: CHAPTER_VARIATION_START_PROMPT,
  variationGenerate: CHAPTER_VARIATION_GENERATE_PROMPT,
} as const;

const PANEL_STORAGE_KEYS = {
  showChapters: "desktop-v2.panel.showChapters",
  showAssistant: "desktop-v2.panel.showAssistant",
  leftWidth: "desktop-v2.panel.leftWidth",
  rightWidth: "desktop-v2.panel.rightWidth",
} as const;

function readPersistedBoolean(key: string, fallback: boolean): boolean {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === "true") return true;
    if (raw === "false") return false;
    return fallback;
  } catch {
    return fallback;
  }
}

function readPersistedNumber(key: string, fallback: number): number {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return fallback;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function writePersisted(key: string, value: string | number | boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // storage may be unavailable; ignore
  }
}

type AssistantState = ReturnType<typeof normalizeDesktopAssistantState> & {
  storyCraftByChapter: Record<string, DesktopStoryCraftFeedback>;
};

type VariationGenerationOptions = {
  source?: ChapterVariation["source"];
  includeFullManuscript?: boolean;
};

type PendingVariationReason =
  | {
      kind: "generate";
      instructions: string;
      options?: VariationGenerationOptions;
    }
  | {
      kind: "save";
      mutation: Extract<ChatBookMutation, { type: "create_chapter_variation" }>;
      meta?: { prompt?: string; model?: string; variationSource?: ChapterVariation["source"] };
    };

function mergeChatIntoAssistant(
  assistant: AssistantState,
  chatPatch: Pick<DesktopAssistantState, "chatThreads" | "activeChatThreadId">
): AssistantState {
  return { ...assistant, ...chatPatch };
}

function nowIso(): string {
  return new Date().toISOString();
}

function generationRequestId(kind: "chat" | "variation"): string {
  return `${kind}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function isGenerationAbort(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === "AbortError") ||
    (error instanceof Error && /abort|cancel/i.test(`${error.name} ${error.message}`))
  );
}

function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

function normalizeCommentAnchor(value: unknown): { from: number; to: number; excerpt?: string } | undefined {
  if (!value || typeof value !== "object") return undefined;
  const raw = value as Record<string, unknown>;
  const from = Number(raw.from);
  const to = Number(raw.to);
  if (!Number.isFinite(from) || !Number.isFinite(to) || from < 0 || to < 0) return undefined;
  const clampedFrom = Math.max(0, Math.floor(from));
  const clampedTo = Math.max(clampedFrom, Math.floor(to));
  const excerpt = typeof raw.excerpt === "string" ? raw.excerpt.trim().slice(0, 200) : "";
  return {
    from: clampedFrom,
    to: clampedTo,
    excerpt: excerpt || undefined,
  };
}

function normalizeChapterComments(value: unknown): Chapter["comments"] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is Record<string, unknown> => typeof item === "object" && item !== null)
    .map((item, index) => {
      const text = typeof item.text === "string" ? item.text.trim() : "";
      const fallbackText = text || "Comment";
      const createdAt =
        typeof item.createdAt === "string" && item.createdAt.trim() ? item.createdAt : nowIso();
      const rec = item as Record<string, unknown>;
      const anchorFromFile = normalizeCommentAnchor(item.anchor);
      const rf = Number(rec.rangeFrom);
      const rt = Number(rec.rangeTo);
      const anchorFromLegacy =
        !anchorFromFile && Number.isFinite(rf) && Number.isFinite(rt) && rf >= 0 && rt > rf
          ? normalizeCommentAnchor({ from: rf, to: rt })
          : undefined;
      const anchor = anchorFromFile ?? anchorFromLegacy;
      const rawTarget = typeof item.targetText === "string" ? item.targetText.trim() : "";
      const targetFromFile = rawTarget || anchor?.excerpt?.trim() || undefined;
      const rawType = typeof item.type === "string" ? item.type.trim() : "";
      const type = rawType ? normalizeDesktopCommentType(rawType) : "note";
      const resolved = typeof item.resolved === "boolean" ? item.resolved : false;
      const authorDisplayName =
        typeof item.authorDisplayName === "string" && item.authorDisplayName.trim()
          ? String(item.authorDisplayName).trim()
          : undefined;
      const authorUserId =
        typeof rec.authorUserId === "string" && rec.authorUserId.trim()
          ? String(rec.authorUserId).trim()
          : undefined;
      const rawCreatedBy = rec.createdBy;
      const createdBy =
        rawCreatedBy === "ai" || rawCreatedBy === "AI"
          ? ("ai" as const)
          : rawCreatedBy === "user" || rawCreatedBy === "User"
            ? ("user" as const)
            : undefined;
      const isStale = typeof item.isStale === "boolean" ? item.isStale : false;
      const parentRaw = rec.parentCommentId;
      const parentCommentId =
        typeof parentRaw === "string" && parentRaw.trim() ? String(parentRaw).trim() : null;
      const blockRaw = rec.blockId;
      const blockId = typeof blockRaw === "string" && blockRaw.trim() ? String(blockRaw).trim() : null;
      return {
        id:
          typeof item.id === "string" && item.id.trim()
            ? item.id
            : `comment-${createdAt}-${index}`,
        text: fallbackText,
        createdAt,
        type,
        resolved,
        createdBy,
        targetText: targetFromFile,
        isStale,
        authorUserId,
        authorDisplayName,
        parentCommentId,
        blockId,
        anchor,
      };
    });
}

function getDocText(editor: Editor): string {
  let text = "";
  editor.state.doc.descendants((node) => {
    if (node.isText) {
      text += node.text ?? "";
    }
    return true;
  });
  return text;
}

function mapTextOffsetToDocPos(editor: Editor, offset: number): number {
  const docSize = editor.state.doc.content.size;
  if (docSize <= 1) return 1;
  const target = Math.max(0, Math.floor(offset));
  let consumed = 0;
  let resolved = 1;
  let found = false;
  editor.state.doc.descendants((node, pos) => {
    if (!node.isText) return true;
    const text = node.text ?? "";
    const next = consumed + text.length;
    if (target <= next) {
      resolved = pos + Math.max(0, target - consumed);
      found = true;
      return false;
    }
    consumed = next;
    resolved = pos + text.length;
    return true;
  });
  if (!found) return docSize;
  return Math.max(1, Math.min(resolved, docSize));
}

function resolveCommentSelection(editor: Editor, comment: Chapter["comments"][number]): { from: number; to: number } | null {
  const docSize = editor.state.doc.content.size;
  if (docSize <= 1) return null;
  const anchor = comment.anchor;
  if (anchor && Number.isFinite(anchor.from) && Number.isFinite(anchor.to)) {
    const from = Math.max(1, Math.min(Math.floor(anchor.from), docSize));
    const to = Math.max(from + 1, Math.min(Math.floor(anchor.to), docSize));
    if (to > from) return { from, to };
  }
  const target = (comment.targetText || "").trim();
  if (target) {
    const byTarget = findTargetTextRangeInEditor(editor, target);
    if (byTarget && byTarget.to > byTarget.from) return byTarget;
  }
  const needle = (anchor?.excerpt || comment.text || "").trim();
  if (needle.length >= 2) {
    const byExcerpt = findTargetTextRangeInEditor(editor, needle);
    if (byExcerpt && byExcerpt.to > byExcerpt.from) return byExcerpt;
  }
  if (needle.length >= 2) {
    const docText = getDocText(editor);
    const index = docText.toLowerCase().indexOf(needle.toLowerCase());
    if (index >= 0) {
      const from = mapTextOffsetToDocPos(editor, index);
      const to = mapTextOffsetToDocPos(editor, index + needle.length);
      if (to > from) return { from: Math.max(1, from), to: Math.max(from, to) };
    }
  }
  return null;
}

function renderMarkdown(text: string): string {
  marked.setOptions({ breaks: true, gfm: true });
  const rendered = marked.parse(text ?? "");
  return typeof rendered === "string" ? rendered : "";
}

function totalWords(book: Book): number {
  return book.chapters.reduce((sum, chapter) => sum + countWords(chapter.content), 0);
}

function statusLabel(status: ChapterStatus): string {
  if (status === "in_review") return "In review";
  if (status === "final") return "Final";
  return "Draft";
}

function normalizeChapterVariations(value: unknown): ChapterVariation[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is Record<string, unknown> => typeof item === "object" && item !== null)
    .map((item, index) => {
      const id =
        typeof item.id === "string" && item.id.trim() ? item.id.trim() : `variation-${index + 1}`;
      const createdAt =
        typeof item.createdAt === "string" && item.createdAt.trim() ? item.createdAt : nowIso();
      const source =
        item.source === "manual"
          ? "manual"
          : item.source === "storyCraft"
            ? "storyCraft"
            : "chat";
      const status =
        item.status === "applied" || item.status === "dismissed" ? item.status : "open";
      return {
        id,
        createdAt,
        source,
        prompt: typeof item.prompt === "string" ? item.prompt : "",
        baseContent: typeof item.baseContent === "string" ? item.baseContent : "",
        content: typeof item.content === "string" ? item.content : "",
        model: typeof item.model === "string" ? item.model : undefined,
        note: typeof item.note === "string" ? item.note : undefined,
        status,
      };
    });
}

/** Something went wrong and the author may need to act. */
function isStatusProblem(status: string): boolean {
  return /fail|error|unable|couldn't|could not|didn't reach|try again|sign in again|offline/i.test(
    status
  );
}

/** Worth reading, but the manuscript is not at risk — don't dress it up as a failure. */
function isStatusNotice(status: string): boolean {
  return /didn't sync/i.test(status);
}

function ensureBookShape(book: Book): Book {
  const chapters = withSpineOrderAndNumbers(
    (book.chapters ?? []).map((chapter, index) => ({
      ...chapter,
      caption: typeof chapter.caption === "string" ? chapter.caption : "",
      content: normalizeChapterContentForStorage(chapter.content),
      order: chapter.order ?? index + 1,
      kind: chapter.kind ?? "chapter",
      comments: normalizeChapterComments(chapter.comments),
      tabData: {
        summary: chapter.tabData?.summary ?? "",
        storyCraft: chapter.tabData?.storyCraft ?? "",
        characters: chapter.tabData?.characters ?? "",
        locations: chapter.tabData?.locations ?? "",
        timeline: chapter.tabData?.timeline ?? "",
        themesMotifs: chapter.tabData?.themesMotifs ?? "",
      },
      metadata: {
        status: chapter.metadata?.status ?? "draft",
        tags: Array.isArray(chapter.metadata?.tags) ? chapter.metadata.tags : [],
        keywords: Array.isArray(chapter.metadata?.keywords) ? chapter.metadata.keywords : [],
        label: chapter.metadata?.label ?? "",
        customFields: chapter.metadata?.customFields ?? {},
      },
      variations: normalizeChapterVariations(chapter.variations),
    }))
  );

  const meta = (book.metadata ?? {}) as Record<string, unknown>;
  let characters = getCharactersFromBookMetadata(meta);
  const removals = getCharacterRemovalsFromMetadata(meta);
  if (characters.length === 0) {
    characters = seedRosterFromChapterTabs(chapters, removals);
  }
  let nextMeta =
    characters.length > 0
      ? (withCharactersInMetadata(meta, characters) as Book["metadata"])
      : (book.metadata ?? {});
  if (characters.length > 0) {
    for (const chapter of chapters) {
      const derived = chapterCharacterTabText(characters, chapter.id);
      if (derived && !chapter.tabData.characters.trim()) {
        chapter.tabData.characters = derived;
      }
    }
  }

  let locations = getLocationsFromBookMetadata(nextMeta as Record<string, unknown>);
  const locationRemovals = getLocationRemovalsFromMetadata(nextMeta as Record<string, unknown>);
  if (locations.length === 0) {
    locations = seedLocationsFromChapterTabs(chapters, locationRemovals);
  }
  if (locations.length > 0) {
    const relationships = getLocationRelationshipsFromMetadata(nextMeta as Record<string, unknown>);
    nextMeta = withLocationsInMetadata(
      nextMeta as Record<string, unknown>,
      locations,
      relationships
    ) as Book["metadata"];
    for (const chapter of chapters) {
      const derived = chapterLocationTabText(locations, chapter.id);
      if (derived && !chapter.tabData.locations.trim()) {
        chapter.tabData.locations = derived;
      }
    }
  }

  let timelineBeats = getTimelineBeatsFromBookMetadata(nextMeta as Record<string, unknown>);
  const timelineRemovalsForSeed = getTimelineRemovalsFromMetadata(
    nextMeta as Record<string, unknown>
  );
  if (timelineBeats.length === 0) {
    timelineBeats = seedBeatsFromChapterTabs(chapters, timelineRemovalsForSeed);
  }
  if (timelineBeats.length > 0) {
    nextMeta = withTimelineInMetadata(
      nextMeta as Record<string, unknown>,
      timelineBeats,
      getTimelineIssuesFromBookMetadata(nextMeta as Record<string, unknown>),
      timelineRemovalsForSeed
    ) as Book["metadata"];
    for (const chapter of chapters) {
      const derived = chapterTimelineTabText(timelineBeats, chapter.id);
      if (derived && !chapter.tabData.timeline.trim()) {
        chapter.tabData.timeline = derived;
      }
    }
  }

  return {
    ...book,
    metadata: nextMeta,
    settings: {
      theme: normalizeThemeMode(book.settings?.theme),
      writingGoalWords: normalizeWritingGoalWords(book.settings?.writingGoalWords),
    },
    chapters,
    phase2: {
      ...book.phase2,
      snapshots: Array.isArray(book.phase2?.snapshots) ? book.phase2.snapshots : [],
      connectorStates: ensureConnectorStates(book.phase2?.connectorStates),
      lastPreflight: book.phase2?.lastPreflight ?? null,
      pendingConflict: book.phase2?.pendingConflict ?? null,
    },
    phase3: normalizePhase3State(book.phase3),
  };
}

function isRecoverableTabId(value: unknown): value is TabId {
  return typeof value === "string" && TABS.some((tab) => tab.id === value);
}

/**
 * Crash recovery JSON can lag behind the real `.sbk`. When we still have `filePath`, reload chapters
 * from disk and only layer recovery metadata (assistant, tab panels, per-chapter metadata) on top.
 */
function mergeDiskBookWithRecoverySession(diskBook: Book, recoveryBook: Book): Book {
  const disk = ensureBookShape(diskBook);
  const rec = ensureBookShape(recoveryBook);
  const recMeta = (rec.metadata ?? {}) as Record<string, unknown>;
  const diskMeta = (disk.metadata ?? {}) as Record<string, unknown>;
  const mergedMetadata: Book["metadata"] = {
    ...diskMeta,
    ...recMeta,
    desktopAssistant: recMeta.desktopAssistant ?? diskMeta.desktopAssistant,
  };
  const recById = new Map(rec.chapters.map((chapter) => [chapter.id, chapter]));
  const mergedChapters = disk.chapters.map((diskChapter) => {
    const recChapter = recById.get(diskChapter.id);
    if (!recChapter) return diskChapter;
    return {
      ...diskChapter,
      ...recChapter,
      title: recChapter.title ?? diskChapter.title,
      caption: recChapter.caption ?? diskChapter.caption,
      content: recChapter.content ?? diskChapter.content,
      metadata: { ...diskChapter.metadata, ...recChapter.metadata },
      tabData: { ...diskChapter.tabData, ...recChapter.tabData },
    };
  });
  return {
    ...disk,
    metadata: mergedMetadata,
    chapters: mergedChapters,
  };
}

function collectPreviousPromisesForChapter(
  currentBook: Book,
  assistant: AssistantState,
  activeChapter: Chapter
): Array<{ id: string; type: string; description: string; chapterId: string; chapterTitle: string }> {
  const out: Array<{ id: string; type: string; description: string; chapterId: string; chapterTitle: string }> =
    [];
  const sorted = [...currentBook.chapters].sort((a, b) => a.order - b.order);
  for (const ch of sorted) {
    if (ch.order >= activeChapter.order) break;
    const fb = assistant.storyCraftByChapter[ch.id];
    if (!fb?.promisesMade?.length) continue;
    for (const p of fb.promisesMade) {
      out.push({
        id: p.id,
        type: p.type,
        description: p.description,
        chapterId: ch.id,
        chapterTitle: ch.title,
      });
    }
  }
  return out;
}

function buildAllChaptersManuscript(
  currentBook: Book,
  focusChapterId: string,
  textForChapter: (chapter: Chapter) => string
): StoryCraftManuscriptChapter[] {
  const maxCharsPerChapter = 120_000;
  return [...currentBook.chapters]
    .sort((a, b) => a.order - b.order)
    .map((ch) => {
      let text = textForChapter(ch).trim();
      if (text.length > maxCharsPerChapter) {
        text = `${text.slice(0, maxCharsPerChapter)}\n\n[…truncated for analysis…]`;
      }
      return {
        chapterId: ch.id,
        title: ch.title,
        order: ch.order,
        text,
      };
    })
    .filter((ch) => ch.chapterId === focusChapterId || ch.text.length > 0);
}

function buildAllChaptersForStoryCraft(currentBook: Book, assessedChapterId: string): StoryCraftManuscriptChapter[] {
  return buildAllChaptersManuscript(currentBook, assessedChapterId, (ch) =>
    chapterPlainTextForSync(ch.content)
  );
}

function buildAllChaptersForVariation(currentBook: Book, rewriteChapterId: string): StoryCraftManuscriptChapter[] {
  return buildAllChaptersManuscript(currentBook, rewriteChapterId, (ch) =>
    ch.id === rewriteChapterId ? chapterPlainTextForDiff(ch.content) : chapterPlainTextForSync(ch.content)
  );
}

function getAssistantState(book: Book): AssistantState {
  const raw = (book.metadata as Record<string, unknown>).desktopAssistant as
    | Partial<AssistantState>
    | undefined;
  const titleById = new Map(book.chapters.map((c) => [c.id, c.title]));
  const chatBase = normalizeDesktopAssistantState(raw, titleById);

  const storyCraftByChapter: Record<string, DesktopStoryCraftFeedback> = {};
  const storyCraftInput =
    raw?.storyCraftByChapter && typeof raw.storyCraftByChapter === "object"
      ? (raw.storyCraftByChapter as Record<string, unknown>)
      : null;
  if (storyCraftInput) {
    for (const [chapterId, report] of Object.entries(storyCraftInput)) {
      const chapterTitle = titleById.get(chapterId) ?? "Chapter";
      const loaded = loadPersistedStoryCraftFeedback(chapterId, chapterTitle, report);
      if (loaded) {
        storyCraftByChapter[chapterId] = loaded;
      }
    }
  }

  return {
    ...chatBase,
    storyCraftByChapter,
  };
}

function withAssistantState(book: Book, nextAssistant: AssistantState): Book {
  return {
    ...book,
    metadata: {
      ...(book.metadata ?? {}),
      desktopAssistant: nextAssistant,
    },
  };
}

export function App() {
  const [book, setBook] = useState<Book | null>(null);
  const [filePath, setFilePath] = useState<string | null>(null);
  const [status, setStatus] = useState("Loading project...");
  const [sessionReady, setSessionReady] = useState(false);
  const [activeChapterId, setActiveChapterId] = useState("");
  const [activeTab, setActiveTab] = useState<TabId>("manuscript");
  const [chapterSearch, setChapterSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | ChapterStatus>("all");
  const [tagFilter, setTagFilter] = useState("all");
  const [showChaptersPanel, setShowChaptersPanel] = useState<boolean>(() =>
    readPersistedBoolean(PANEL_STORAGE_KEYS.showChapters, true)
  );
  const [showAssistantPanel, setShowAssistantPanel] = useState<boolean>(() =>
    readPersistedBoolean(PANEL_STORAGE_KEYS.showAssistant, true)
  );
  const [workbenchView, setWorkbenchView] = useState<WorkbenchView>("book");
  const [newBookPickerOpen, setNewBookPickerOpen] = useState(false);
  const [findReplaceOpen, setFindReplaceOpen] = useState(false);
  const [openaiApiKeyDraft, setOpenaiApiKeyDraft] = useState("");
  const [anthropicApiKeyDraft, setAnthropicApiKeyDraft] = useState("");
  const [apiKeysSaved, setApiKeysSaved] = useState(false);
  const [planningMode, setPlanningMode] = useState<PlanningMode | undefined>(undefined);
  const [translateDialogOpen, setTranslateDialogOpen] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const [narratorMode, setNarratorMode] = useState(false);
  const [zoomPercent, setZoomPercent] = useState(100);
  const [chatDraft, setChatDraft] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [variationBusy, setVariationBusy] = useState(false);
  const [pendingVariationReason, setPendingVariationReason] =
    useState<PendingVariationReason | null>(null);
  const [storyCraftBusy, setStoryCraftBusy] = useState(false);
  const [summaryBusy, setSummaryBusy] = useState(false);
  const [characterExtractBusy, setCharacterExtractBusy] = useState(false);
  const [wholeBookCharacterBusy, setWholeBookCharacterBusy] = useState(false);
  const [selectedCharacterId, setSelectedCharacterId] = useState<string | null>(null);
  const [pendingCharacterRemoval, setPendingCharacterRemoval] = useState<BookCharacter | null>(null);
  const [locationExtractBusy, setLocationExtractBusy] = useState(false);
  const [wholeBookLocationBusy, setWholeBookLocationBusy] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [pendingLocationRemoval, setPendingLocationRemoval] = useState<BookLocation | null>(null);
  const [timelineExtractBusy, setTimelineExtractBusy] = useState(false);
  const [wholeBookTimelineBusy, setWholeBookTimelineBusy] = useState(false);
  const [selectedTimelineBeatId, setSelectedTimelineBeatId] = useState<string | null>(null);
  const [pendingTimelineBeatRemoval, setPendingTimelineBeatRemoval] =
    useState<BookTimelineBeat | null>(null);
  const [themeExtractBusy, setThemeExtractBusy] = useState(false);
  const [wholeBookThemeBusy, setWholeBookThemeBusy] = useState(false);
  const [selectedThemeRef, setSelectedThemeRef] = useState<{
    kind: ThemeKind;
    id: string;
  } | null>(null);
  const [pendingThemeRemoval, setPendingThemeRemoval] = useState<{
    kind: ThemeKind;
    theme?: BookTheme;
    motif?: BookMotif;
    symbol?: BookSymbol;
  } | null>(null);
  const [chatProvider, setChatProvider] = useState<"openai" | "anthropic">(() =>
    llmProviderForModel(readPersistedDefaultChatModel())
  );
  const [chatModel, setChatModel] = useState<ChatModelValue>(() => readPersistedDefaultChatModel());
  const analysisModel = useMemo(
    () => getBookAnalysisModel((book?.metadata ?? {}) as Record<string, unknown>),
    [book?.metadata]
  );
  const analysisProvider = useMemo(() => llmProviderForModel(analysisModel), [analysisModel]);
  const [uploadedChatImage, setUploadedChatImage] = useState<UploadedChatImage | null>(null);
  const [attachedChatVersion, setAttachedChatVersion] = useState<AttachedChatVersion | null>(null);
  const [chatVersionOptions, setChatVersionOptions] = useState<AttachedChatVersion[]>([]);
  const [assistantPanelTab, setAssistantPanelTab] = useState<AssistantPanelTab>("chat");
  const [leftPanelWidth, setLeftPanelWidth] = useState<number>(() =>
    readPersistedNumber(PANEL_STORAGE_KEYS.leftWidth, 280)
  );
  const [rightPanelWidth, setRightPanelWidth] = useState<number>(() =>
    readPersistedNumber(PANEL_STORAGE_KEYS.rightWidth, 380)
  );
  const [resizingPanel, setResizingPanel] = useState<"left" | "right" | null>(null);
  const [resizingOriginX, setResizingOriginX] = useState(0);
  const [resizingStartWidth, setResizingStartWidth] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackType, setFeedbackType] = useState("general");
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackEmail, setFeedbackEmail] = useState("");
  const [feedbackRating, setFeedbackRating] = useState("");
  const [feedbackAttachScreenshot, setFeedbackAttachScreenshot] = useState(false);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [showShortcutHelp, setShowShortcutHelp] = useState(false);
  const [showOpenFromCloud, setShowOpenFromCloud] = useState(false);
  const [moveChapterSourceId, setMoveChapterSourceId] = useState<string | null>(null);
  const [openingCloudBookId, setOpeningCloudBookId] = useState<string | null>(null);
  const [showLibraryOverrideModal, setShowLibraryOverrideModal] = useState(false);
  const [libraryOverrideBusy, setLibraryOverrideBusy] = useState(false);
  const [showConflictReview, setShowConflictReview] = useState(false);
  const [libraryOverrideError, setLibraryOverrideError] = useState<string | null>(null);
  const [cloudSaveEnabled, setCloudSaveEnabled] = useState(false);
  const [online, setOnline] = useState(() => typeof navigator === "undefined" || navigator.onLine);
  const [isSysAdmin, setIsSysAdmin] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void checkSysAdmin().then((ok) => {
      if (!cancelled) setIsSysAdmin(ok);
    });
    return () => {
      cancelled = true;
    };
  }, [cloudSaveEnabled]);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  const [selectedChapterCommentId, setSelectedChapterCommentId] = useState<string | null>(null);
  const [commentComposerOpen, setCommentComposerOpen] = useState(false);
  const [activeCompareVariationId, setActiveCompareVariationId] = useState<string | null>(null);
  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return true;
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });
  const [dragChapterId, setDragChapterId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const dirtyRef = useRef(false);
  const filePathRef = useRef(filePath);
  const activeTabRecoveryRef = useRef(activeTab);
  filePathRef.current = filePath;
  activeTabRecoveryRef.current = activeTab;
  const chatInputRef = useRef<HTMLTextAreaElement | null>(null);
  const chatImageInputRef = useRef<HTMLInputElement | null>(null);
  const chatStickToBottomRef = useRef(true);
  const activeChatRequestRef = useRef<string | null>(null);
  const activeVariationRequestRef = useRef<{
    requestId: string;
    instructions: string;
    reason: string;
    options?: VariationGenerationOptions;
  } | null>(null);
  const chapterSearchRef = useRef<HTMLInputElement | null>(null);
  const bookRef = useRef<Book | null>(null);
  const autoSyncInFlightRef = useRef(false);
  /** After opening/reloading a local `.sbk`, push disk to library — do not pull remote over it. */
  const preferLocalDiskOverCloudRef = useRef(false);
  /** First library call after open/remount must push this document, never pull over it. */
  const bootPushOnlyRef = useRef(true);
  const autosaveTimerRef = useRef<number | null>(null);
  const cloudPushTimerRef = useRef<number | null>(null);
  const autosaveInFlightRef = useRef(false);
  const pendingAutosaveRef = useRef(false);
  const pendingCloudPushRef = useRef(false);
  /** Silent local disk/IDB cache — frequent, never flashes UI. */
  const LOCAL_CACHE_DEBOUNCE_MS = 2000;
  /**
   * Library/DB structure push. Prose no longer rides on this timer — it goes
   * through the durable per-chapter outbox — but a shorter window still means
   * less structure drift to reconcile after a crash.
   */
  const CLOUD_PUSH_DEBOUNCE_MS = 3_000;
  const selectChapterCommentFromEditorRef = useRef<(commentId: string | null) => void>(() => undefined);
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;
  const activeChapterIdRef = useRef(activeChapterId);
  activeChapterIdRef.current = activeChapterId;
  const scheduleAutosaveRef = useRef<(kind: "immediate" | "content") => void>(() => undefined);
  const scheduleCloudPushRef = useRef<(kind?: "immediate" | "deferred") => void>(() => undefined);

  useEffect(() => {
    if (!online || !isAccountPrimaryRuntime() || !cloudSaveEnabled) return;
    scheduleCloudPushRef.current("deferred");
  }, [online, cloudSaveEnabled]);

  const handleAnalysisModelChange = useCallback((model: string, _provider?: "openai" | "anthropic") => {
    updateBook((current) => ({
      ...current,
      metadata: withBookAnalysisModel(
        (current.metadata ?? {}) as Record<string, unknown>,
        model as LlmModelValue
      ),
    }));
    setStatus("Analysis model updated for this book");
  }, []);

  useEffect(() => {
    dirtyRef.current = dirty;
  }, [dirty]);

  /** True while any AI request that writes into the book is in flight. */
  const aiWriteBusy =
    chatBusy ||
    variationBusy ||
    storyCraftBusy ||
    summaryBusy ||
    characterExtractBusy ||
    wholeBookCharacterBusy ||
    locationExtractBusy ||
    wholeBookLocationBusy ||
    timelineExtractBusy ||
    wholeBookTimelineBusy ||
    themeExtractBusy ||
    wholeBookThemeBusy;
  const aiWriteBusyRef = useRef(false);
  aiWriteBusyRef.current = aiWriteBusy;

  const {
    manuscriptEditorRef,
    manuscriptReloadToken,
    bumpManuscriptReload,
    getManuscriptEditor,
    undoManuscript,
    redoManuscript,
    flushManuscriptDraft,
    flushRecoverySave,
    flushBookUi,
    persistBookUpdate,
    markBookDirty,
    handleManuscriptTyping,
    handleManuscriptDraftFlush: persistManuscriptDraftFlush,
  } = useManuscriptEditing({
    bookRef,
    filePathRef,
    activeChapterIdRef,
    activeTabRef: activeTabRecoveryRef,
    dirtyRef,
    ensureBookShape,
    nowIso,
    setBook,
    setDirty,
    onContentDraftPersisted: () => scheduleAutosaveRef.current("content"),
    onBookPersisted: (next) => bookChapterSync.enqueueChangedChapters(next),
  });

  const handleManuscriptDraftFlush = useCallback(
    (payload: { chapterId: string; content: string; comments: Chapter["comments"] }) => {
      persistManuscriptDraftFlush(payload);
      const current = bookRef.current;
      if (!current) return;
      const uses = collectCitationUsesFromContent(payload.chapterId, payload.content);
      const nextState = replaceChapterCitationUses(
        getCitationState(current.metadata as Record<string, unknown>),
        payload.chapterId,
        uses
      );
      const withMeta: Book = {
        ...current,
        metadata: withCitationsInMetadata(current.metadata as Record<string, unknown>, nextState) as Book["metadata"],
      };
      const next = applyGeneratedBibliography(withMeta);
      if (next === current) return;
      bookRef.current = next;
      setBook(next);
      const plain = chapterPlainTextForSync(payload.content);
      if (countWords(plain) > 50) {
        void trackDesktopActivation({
          event: "activation.chapter_written",
          bookId: bookRef.current?.id ?? null,
          chapterId: payload.chapterId,
        });
      }
    },
    [persistManuscriptDraftFlush]
  );

  const activeChapter = useMemo(() => {
    if (!book) return null;
    return book.chapters.find((chapter) => chapter.id === activeChapterId) ?? book.chapters[0] ?? null;
  }, [book, activeChapterId]);

  const liveCollab = usePremierLiveCollab({
    bookId: book?.id,
    chapterId: activeChapter?.id,
  });

  const activeCompareVariation = useMemo(() => {
    if (!activeChapter || !activeCompareVariationId) return null;
    return (activeChapter.variations ?? []).find((variation) => variation.id === activeCompareVariationId) ?? null;
  }, [activeChapter, activeCompareVariationId]);

  useEffect(() => {
    if (!activeCompareVariationId || !activeChapter) return;
    const exists = (activeChapter.variations ?? []).some((variation) => variation.id === activeCompareVariationId);
    if (!exists) setActiveCompareVariationId(null);
  }, [activeChapter, activeCompareVariationId]);

  const chapterPlainForCommentStale = useMemo(
    () => (activeChapter ? chapterPlainTextForSync(activeChapter.content) : ""),
    [activeChapter?.id, activeChapter?.content]
  );

  const previousChapterIdRef = useRef("");
  useEffect(() => {
    const previousChapterId = previousChapterIdRef.current;
    previousChapterIdRef.current = activeChapterId;
    flushManuscriptDraft();
    flushBookUi();
    setSelectedChapterCommentId(null);
    // The outgoing editor already flushed its tail into bookRef on unmount;
    // get that last edit into the outbox before attention moves on.
    if (previousChapterId && previousChapterId !== activeChapterId) {
      void bookChapterSync.flushChapter(bookRef.current, previousChapterId);
    }
  }, [activeChapterId, flushManuscriptDraft, flushBookUi]);

  const selectedChapterComment = useMemo(
    () => activeChapter?.comments.find((c) => c.id === selectedChapterCommentId) ?? null,
    [activeChapter, selectedChapterCommentId]
  );
  const currentCloudUserId = cloudAuthDesktopV2.getUserId();

  const getCommentStale = useCallback(
    (comment: Chapter["comments"][number]) => {
      if (comment.isStale) return true;
      const original = (comment.targetText || "").trim();
      if (original.length < 2) return false;
      const hay = chapterPlainForCommentStale.toLowerCase();
      return !hay.includes(original.toLowerCase());
    },
    [chapterPlainForCommentStale]
  );

  const assistantState = useMemo(() => (book ? getAssistantState(book) : null), [book]);

  const allTags = useMemo(() => {
    if (!book) return [];
    return Array.from(new Set(book.chapters.flatMap((chapter) => chapter.metadata.tags))).sort((a, b) =>
      a.localeCompare(b)
    );
  }, [book]);

  const visibleChapters = useMemo(() => {
    if (!book) return [];
    return book.chapters.filter((chapter) => {
      const query = chapterSearch.trim().toLowerCase();
      const matchesSearch =
        !query || `${chapter.title}\n${chapter.content}`.toLowerCase().includes(query);
      const matchesStatus = statusFilter === "all" || chapter.metadata.status === statusFilter;
      const matchesTag = tagFilter === "all" || chapter.metadata.tags.includes(tagFilter);
      return matchesSearch && matchesStatus && matchesTag;
    });
  }, [book, chapterSearch, statusFilter, tagFilter]);

  const storyCraftAverages = useMemo(() => {
    const out: Record<string, number | null> = {};
    if (!book) return out;
    const blend = normalizeScoreBlend(
      (book.metadata as Record<string, unknown> | undefined)?.scoreBlend
    );
    for (const chapter of book.chapters) {
      const sc =
        assistantState?.storyCraftByChapter[chapter.id] != null
          ? averageStoryCraftScore(assistantState.storyCraftByChapter[chapter.id]!)
          : null;
      const rawEditor = chapter.metadata?.customFields?.editorAverage;
      const ed =
        typeof rawEditor === "number" && Number.isFinite(rawEditor) ? rawEditor : null;
      out[chapter.id] = resolveChapterListScore(sc, ed, blend);
    }
    return out;
  }, [assistantState, book]);

  const promiseDebtByChapter = useMemo(() => {
    const out: Record<string, number> = {};
    const byChapter = assistantState?.storyCraftByChapter;
    if (!byChapter) return out;

    const keptIds = new Set<string>();
    for (const fb of Object.values(byChapter)) {
      for (const kept of fb.promisesKept ?? []) {
        if (kept.promiseId) keptIds.add(kept.promiseId);
      }
    }

    for (const [chapterId, fb] of Object.entries(byChapter)) {
      const made = fb.promisesMade ?? [];
      if (made.length === 0) {
        out[chapterId] = 0;
        continue;
      }
      if (keptIds.size === 0) {
        out[chapterId] = made.length;
        continue;
      }
      out[chapterId] = made.filter((p) => !keptIds.has(p.id)).length;
    }
    return out;
  }, [assistantState]);

  const scoreBlend = useMemo(
    () =>
      normalizeScoreBlend(
        book ? (book.metadata as Record<string, unknown>).scoreBlend : undefined
      ),
    [book]
  );

  const bookAudioSettings = useMemo(
    () => readBookAudioSettings(book ? (book.metadata as Record<string, unknown>) : undefined),
    [book]
  );

  function saveBookAudioSettings(next: ReturnType<typeof normalizeBookAudioSettings>): void {
    const normalized = normalizeBookAudioSettings(next);
    updateBook((current) => ({
      ...current,
      metadata: {
        ...(current.metadata as Record<string, unknown>),
        audioSettings: normalized,
      },
    }));
  }

  function saveChapterAudioVoice(chapterId: string, voiceId: string): void {
    updateBook((current) => ({
      ...current,
      chapters: current.chapters.map((chapter) =>
        chapter.id === chapterId
          ? {
              ...chapter,
              metadata: {
                ...chapter.metadata,
                customFields: {
                  ...chapter.metadata.customFields,
                  [CHAPTER_AUDIO_VOICE_FIELD]: voiceId,
                },
              },
              updatedAt: new Date().toISOString(),
            }
          : chapter
      ),
      updatedAt: new Date().toISOString(),
    }));
  }

  function saveScoreBlend(next: ScoreBlendSettings): void {
    const normalized = normalizeScoreBlend(next);
    updateBook((current) => ({
      ...current,
      metadata: {
        ...(current.metadata as Record<string, unknown>),
        scoreBlend: normalized,
      },
    }));
    const bookId = typeof book?.id === "string" ? book.id.trim() : "";
    if (bookId && cloudAuthDesktopV2.isSignedIn()) {
      void cloudAuthDesktopV2.setBookScoreBlend(bookId, normalized).catch(() => {
        /* local metadata is source of truth offline */
      });
    }
  }

  async function saveEditorScoreForActiveChapter(next: {
    assessment: StoryCraftAssessment;
    summary: string | null;
    average: number | null;
  }): Promise<void> {
    if (!activeChapter) return;
    const now = nowIso();
    updateChapter((chapter) => ({
      ...chapter,
      metadata: {
        ...chapter.metadata,
        customFields: {
          ...chapter.metadata.customFields,
          editorAssessment: next.assessment,
          editorSummary: next.summary,
          editorAverage: next.average,
          editorFeedbackUpdatedAt: now,
        },
      },
    }));
    const bookId = typeof book?.id === "string" ? book.id.trim() : "";
    if (bookId && cloudAuthDesktopV2.isSignedIn()) {
      try {
        const saved = await cloudAuthDesktopV2.putEditorChapterFeedback(bookId, activeChapter.id, {
          assessment: next.assessment,
          summary: next.summary,
          checklist: [],
        });
        updateChapter((chapter) => ({
          ...chapter,
          metadata: {
            ...chapter.metadata,
            customFields: {
              ...chapter.metadata.customFields,
              editorAssessment: saved.assessment,
              editorSummary: saved.summary,
              editorAverage: saved.average,
              editorFeedbackUpdatedAt: saved.lastUpdated,
            },
          },
        }));
      } catch {
        /* keep local score */
      }
    }
    setStatus("Editor score saved");
  }

  const activeChatThread = useMemo(() => {
    if (!assistantState) return null;
    return getActiveChatThread(assistantState);
  }, [assistantState]);

  const activeChats = activeChatThread?.messages ?? [];

  const chatThreads = assistantState?.chatThreads.filter((thread) => !thread.archivedAt) ?? [];
  const archivedChatThreads =
    assistantState?.chatThreads.filter((thread) => Boolean(thread.archivedAt)) ?? [];

  const activeStoryCraft = useMemo(() => {
    if (!assistantState || !activeChapter) return null;
    return assistantState.storyCraftByChapter[activeChapter.id] ?? null;
  }, [assistantState, activeChapter]);

  const assistantNotes = useMemo(() => {
    if (!activeChapter) return "";
    const raw = activeChapter.metadata.customFields?.assistantNotes;
    return typeof raw === "string" ? raw : "";
  }, [activeChapter]);

  function updateBook(mutator: (current: Book) => Book): void {
    const current = bookRef.current;
    if (!current) return;
    markBookDirty();
    const touchedAt = nowIso();
    const next = ensureBookShape({ ...mutator(current), updatedAt: touchedAt });
    persistBookUpdate(next, "immediate");
    setBook(next);
    scheduleAutosaveRef.current("immediate");
  }

  /**
   * Put a body kept only in this device's journal back into the editor. It then
   * follows the normal save path, so recovering it also queues it for the server.
   */
  function restoreLocalChapterBody(input: {
    chapterId: string;
    title: string;
    contentJson: string;
  }): void {
    updateBook((current) => ({
      ...current,
      chapters: current.chapters.map((chapter) =>
        chapter.id === input.chapterId
          ? {
              ...chapter,
              title: input.title || chapter.title,
              content: input.contentJson,
              updatedAt: nowIso(),
            }
          : chapter
      ),
    }));
    bumpManuscriptReload();
    setStatus("Restored a version saved on this device");
  }

  /** Merge partial book updates; metadata keys are patched onto current metadata (not replaced). */
  function applyBookUpdates(updates: Partial<Book>): void {
    updateBook((current) => {
      const next: Book = !updates.metadata
        ? { ...current, ...updates }
        : {
            ...current,
            ...updates,
            metadata: {
              ...(current.metadata as Record<string, unknown>),
              ...(updates.metadata as Record<string, unknown>),
            },
          };
      const profile = profileFromBookMetadata(next.metadata as Record<string, unknown>);
      return profile.citations ? applyGeneratedBibliography(next) : next;
    });
  }

  // Premier seamless sync: auto-apply remote-only chunks; never wipe divergent local prose.
  useEffect(() => {
    if (!PREMIER_SEAMLESS_SYNC || !book?.phase2?.pendingConflict) return;
    const pending = book.phase2.pendingConflict;
    const chapterId = pending.chapterId;
    const mergeRaw = pending.mergeJson;
    if (!chapterId) {
      applyBookUpdates({
        phase2: { ...book.phase2, pendingConflict: null },
      });
      return;
    }
    if (!mergeRaw) {
      applyBookUpdates({
        phase2: { ...book.phase2, pendingConflict: null },
      });
      setStatus("Remote sync applied (seamless)");
      return;
    }
    let parsed: ThreeWayMergeResult;
    try {
      parsed = JSON.parse(mergeRaw) as ThreeWayMergeResult;
    } catch {
      applyBookUpdates({
        phase2: { ...book.phase2, pendingConflict: null },
      });
      return;
    }
    const resolutions = seamlessConflictResolutions(parsed.conflicts);
    const next = applyPendingConflictResolutions(book, chapterId, resolutions);
    applyBookUpdates({ chapters: next.chapters, phase2: next.phase2 });
    const keptRemote = resolutions.filter((r) => r.choice === "theirs").length;
    const keptLocal = resolutions.filter((r) => r.choice === "mine").length;
    setStatus(
      keptLocal && keptRemote
        ? "Merged library edits; kept your local changes where both sides edited"
        : keptRemote
          ? "Remote edits applied (seamless sync)"
          : "Kept your local edits (library had overlapping changes)"
    );
    setDirty(true);

    const regions = regionsFromHunkResolutions(parsed.conflicts, resolutions);
    const policy =
      keptLocal && !keptRemote
        ? ("seamless_mine" as const)
        : keptRemote && !keptLocal
          ? ("seamless_theirs" as const)
          : ("seamless_mine" as const);
    const annotations = buildOverwriteAnnotationPayload(regions, policy);
    const bookId = book.id?.trim();
    if (annotations && bookId && cloudAuthDesktopV2.isSignedIn()) {
      void cloudAuthDesktopV2
        .postChapterChangeHistory(bookId, chapterId, {
          kind: "overwrite",
          summary: buildOverwriteSummary(regions),
          annotations,
        })
        .catch(() => {
          /* best-effort history */
        });
    }
  }, [book?.phase2?.pendingConflict?.chapterId, book?.phase2?.pendingConflict?.mergeJson]);

  const workbenchPersona = useWorkbenchPersona(book, applyBookUpdates);
  const bookTypeProfile = useMemo(
    () => profileFromBookMetadata((book?.metadata ?? {}) as Record<string, unknown>),
    [book?.metadata]
  );
  const editorTabItems = useMemo(
    () => TABS.filter((tab) => isEditorTabEnabled(bookTypeProfile, tab.id)).map((tab) => ({ id: tab.id, label: tab.label })),
    [bookTypeProfile]
  );

  useEffect(() => {
    if (!isEditorTabEnabled(bookTypeProfile, activeTab)) {
      setActiveTab("manuscript");
    }
  }, [bookTypeProfile, activeTab]);

  useEffect(() => {
    if (workbenchView !== "adminFeedback" && !bookTypeProfile.views.includes(workbenchView)) {
      setWorkbenchView("book");
    }
  }, [bookTypeProfile, workbenchView]);

  function updateChapter(mutator: (chapter: Chapter) => Chapter): void {
    if (!activeChapter) return;
    const touchedAt = nowIso();
    updateBook((current) => ({
      ...current,
      chapters: current.chapters.map((chapter) =>
        chapter.id === activeChapter.id ? { ...mutator(chapter), updatedAt: touchedAt } : chapter
      ),
    }));
  }

  function createChapter(title?: string, kind: BookContentKind = "chapter"): Chapter {
    const nextOrder = (book?.chapters.length ?? 0) + 1;
    const bodyCount = (book?.chapters.filter((c) => (c.kind ?? "chapter") === "chapter").length ?? 0) + 1;
    const resolvedTitle =
      title?.trim() ||
      (kind === "chapter" ? defaultBodyTitle(bookTypeProfile, bodyCount) : defaultTitleForKind(kind));
    return {
      id: `chapter-${Date.now()}-${Math.random().toString(16).slice(2, 7)}`,
      title: resolvedTitle,
      caption: "",
      content: "",
      order: nextOrder,
      kind,
      chapterNumber: kind === "chapter" ? null : null,
      comments: [],
      notes: [],
      tabData: { summary: "", storyCraft: "", characters: "", locations: "", timeline: "", themesMotifs: "" },
      metadata: { status: "draft", tags: [], keywords: [], label: "", customFields: {} },
      createdAt: nowIso(),
      updatedAt: nowIso(),
    };
  }

  function applyGeneratedBibliography(current: Book): Book {
    const profile = profileFromBookMetadata(current.metadata as Record<string, unknown>);
    if (!profile.citations) return current;
    const state = getCitationState(current.metadata as Record<string, unknown>);
    const fields = bibliographyChapterFields(state);
    const existing = current.chapters.find((c) => c.kind === "bibliography");
    if (existing) {
      if (existing.content === fields.content && existing.title === fields.title) return current;
      return {
        ...current,
        chapters: current.chapters.map((c) =>
          c.id === existing.id
            ? { ...c, title: fields.title, content: fields.content, updatedAt: nowIso() }
            : c
        ),
      };
    }
    const added: Chapter = {
      id: `chapter-${Date.now()}-biblio`,
      title: fields.title,
      caption: "",
      content: fields.content,
      order: current.chapters.length + 1,
      kind: "bibliography",
      chapterNumber: null,
      comments: [],
      notes: [],
      tabData: {
        summary: "",
        storyCraft: "",
        characters: "",
        locations: "",
        timeline: "",
        themesMotifs: "",
      },
      metadata: { status: "draft", tags: [], keywords: [], label: "", customFields: {} },
      createdAt: nowIso(),
      updatedAt: nowIso(),
    };
    return { ...current, chapters: withSpineOrderAndNumbers([...current.chapters, added]) };
  }

  function insertCitationIntoManuscript(citation: Citation) {
    if (!activeChapter) return;
    const state = getCitationState((bookRef.current?.metadata ?? {}) as Record<string, unknown>);
    const markId = `citemark-${Date.now()}-${Math.random().toString(16).slice(2, 6)}`;
    const text = formatInTextCitation(citation, state.style);
    const editor = manuscriptEditorRef.current?.getEditor();
    if (editor && !editor.isDestroyed) {
      editor
        .chain()
        .focus()
        .insertContent({
          type: "text",
          text,
          marks: [{ type: "citation", attrs: { citationId: citation.id, markId, locator: null } }],
        })
        .insertContent(" ")
        .run();
      setWorkbenchView("book");
      setActiveTab("manuscript");
      return;
    }
    updateChapter((chapter) => {
      let doc: { type: string; content?: unknown[] };
      try {
        doc = JSON.parse(chapter.content || '{"type":"doc","content":[]}');
      } catch {
        doc = { type: "doc", content: [] };
      }
      if (!Array.isArray(doc.content)) doc.content = [];
      doc.content.push({
        type: "paragraph",
        attrs: { id: "" },
        content: [
          {
            type: "text",
            text,
            marks: [{ type: "citation", attrs: { citationId: citation.id, markId } }],
          },
        ],
      });
      const content = JSON.stringify(doc);
      const uses = collectCitationUsesFromContent(chapter.id, content);
      queueMicrotask(() => {
        updateBook((current) => {
          const nextState = replaceChapterCitationUses(
            getCitationState(current.metadata as Record<string, unknown>),
            chapter.id,
            uses
          );
          return applyGeneratedBibliography({
            ...current,
            metadata: withCitationsInMetadata(
              current.metadata as Record<string, unknown>,
              nextState
            ) as Book["metadata"],
          });
        });
      });
      return { ...chapter, content };
    });
    setWorkbenchView("book");
    setActiveTab("manuscript");
  }

  function insertImageAssetIntoManuscript(asset: ResearchAsset) {
    const editor = manuscriptEditorRef.current?.getEditor();
    if (editor && !editor.isDestroyed) {
      editor.chain().focus().insertResearchImage({
        assetId: asset.id,
        alt: asset.title,
      }).run();
    } else if (activeChapter) {
      updateChapter((chapter) => {
        let doc: { type: string; content?: unknown[] };
        try {
          doc = JSON.parse(chapter.content || '{"type":"doc","content":[]}');
        } catch {
          doc = { type: "doc", content: [] };
        }
        if (!Array.isArray(doc.content)) doc.content = [];
        doc.content.push({
          type: "image",
          attrs: { id: `img-${asset.id}`, assetId: asset.id, src: "", alt: asset.title },
        });
        return { ...chapter, content: JSON.stringify(doc) };
      });
    }
    setWorkbenchView("book");
    setActiveTab("manuscript");
  }

  function saveActiveChatToResearch() {
    const current = bookRef.current;
    if (!current) return;
    const thread = getActiveChatThread(assistantState ?? normalizeDesktopAssistantState(undefined));
    const messages = thread?.messages ?? [];
    if (!messages.length) {
      setStatus("Nothing in this chat to save.");
      return;
    }
    const extractedText = messages.map((m) => `${m.role === "assistant" ? "Assistant" : "You"}: ${m.content}`).join("\n\n");
    const asset = createResearchAsset({
      kind: "chat_summary",
      title: thread?.title?.trim() || "Chat notes",
      extractedText,
      notes: "Saved from assistant chat",
    });
    const nextResearch = upsertResearchAsset(getResearchState(current.metadata as Record<string, unknown>), asset);
    applyBookUpdates({
      metadata: withResearchInMetadata(current.metadata as Record<string, unknown>, nextResearch) as Book["metadata"],
    });
    setWorkbenchView("research");
    setStatus("Saved chat to Research");
  }

  function insertSpineItem(params: {
    relativeId: string;
    position: "before" | "after";
    kind?: BookContentKind;
  }): void {
    const kind = params.kind ?? "chapter";
    const chapter = createChapter(undefined, kind);
    updateBook((current) => {
      const index = current.chapters.findIndex((c) => c.id === params.relativeId);
      if (index < 0) {
        return { ...current, chapters: withSpineOrderAndNumbers([...current.chapters, chapter]) };
      }
      const insertAt = params.position === "before" ? index : index + 1;
      const chapters = [...current.chapters];
      chapters.splice(insertAt, 0, chapter);
      return { ...current, chapters: withSpineOrderAndNumbers(chapters) };
    });
    setActiveChapterId(chapter.id);
    setStatus(kind === "chapter" ? "Chapter added" : `${defaultTitleForKind(kind)} added`);
  }

  function changeSpineKind(chapterId: string, kind: BookContentKind): void {
    updateBook((current) => ({
      ...current,
      chapters: withSpineOrderAndNumbers(
        current.chapters.map((chapter) =>
          chapter.id === chapterId ? { ...chapter, kind, chapterNumber: null } : chapter
        )
      ),
    }));
    setStatus(`Changed to ${defaultTitleForKind(kind)}`);
  }

  function deleteSpineItem(chapterId: string): void {
    let nextActiveId = "";
    updateBook((current) => {
      if (current.chapters.length <= 1) return current;
      const index = current.chapters.findIndex((chapter) => chapter.id === chapterId);
      const neighbor = current.chapters[index + 1] ?? current.chapters[index - 1];
      nextActiveId = neighbor && neighbor.id !== chapterId ? neighbor.id : "";
      const chapters = withSpineOrderAndNumbers(current.chapters.filter((chapter) => chapter.id !== chapterId));
      if (!nextActiveId) nextActiveId = chapters[0]?.id ?? "";
      noteChapterDeleted(current.id ?? "", chapterId);
      return { ...current, chapters };
    });
    setActiveChapterId((currentId) => (currentId === chapterId ? nextActiveId : currentId));
    setStatus("Item deleted");
  }

  function reorderChapters(chapterId: string, direction: -1 | 1): void {
    updateBook((current) => {
      const index = current.chapters.findIndex((chapter) => chapter.id === chapterId);
      const nextIndex = index + direction;
      if (index < 0 || nextIndex < 0 || nextIndex >= current.chapters.length) return current;
      const chapters = [...current.chapters];
      const [item] = chapters.splice(index, 1);
      chapters.splice(nextIndex, 0, item);
      return {
        ...current,
        chapters: withSpineOrderAndNumbers(chapters),
      };
    });
  }

  function applyChapterReorder(chapterIds: string[]): void {
    updateBook((current) => {
      if (chapterIds.length !== current.chapters.length) return current;
      const byId = new Map(current.chapters.map((chapter) => [chapter.id, chapter]));
      const chapters = chapterIds.map((id) => byId.get(id) ?? null);
      if (chapters.some((chapter) => !chapter)) return current;
      return { ...current, chapters: withSpineOrderAndNumbers(chapters as Chapter[]) };
    });
  }

  function addChapterVariation(params: {
    chapterId: string;
    content: string;
    prompt: string;
    reason: string;
    note?: string;
    model?: string;
    source?: ChapterVariation["source"];
    openCompare?: boolean;
  }): string {
    const variationId = `variation-${Date.now()}`;
    const createdAt = nowIso();
    updateChapterForId(params.chapterId, (chapter) => {
      const baseContent = snapshotVariationBaseContent(chapter.content);
      const content = buildVariationContentFromRewrite(chapter.content, params.content);
      const variation: ChapterVariation = {
        id: variationId,
        createdAt,
        source: params.source ?? "chat",
        prompt: params.prompt,
        baseContent,
        content,
        model: params.model,
        reason: params.reason,
        note: params.note,
        status: "open",
      };
      return {
        ...chapter,
        variations: [...(chapter.variations ?? []), variation],
      };
    });
    if (params.openCompare !== false) {
      setActiveChapterId(params.chapterId);
      setActiveCompareVariationId(variationId);
      setActiveTab("manuscript");
      setAssistantPanelTab("variations");
    }
    return variationId;
  }

  function applyChatBookMutations(
    mutations: ChatBookMutation[] | undefined,
    meta?: {
      prompt?: string;
      model?: string;
      reason?: string;
      variationSource?: ChapterVariation["source"];
    }
  ): void {
    if (!mutations?.length) return;
    for (const mutation of mutations) {
      if (mutation.type === "reorder_chapters") {
        applyChapterReorder(mutation.chapterIds);
        continue;
      }
      if (mutation.type === "create_chapter_variation") {
        if (!meta?.reason?.trim()) {
          setPendingVariationReason({
            kind: "save",
            mutation,
            meta,
          });
          setStatus("Add a reason before saving this variation");
          continue;
        }
        addChapterVariation({
          chapterId: mutation.chapterId,
          content: mutation.content,
          prompt: mutation.prompt || meta?.prompt || "Generate a variation of this chapter",
          reason: meta.reason.trim(),
          note: mutation.note,
          model: meta?.model,
          source: meta?.variationSource ?? "chat",
        });
        setStatus("Chapter variation ready — review changes in the compare view");
      }
    }
  }

  function updateChapterForId(chapterId: string, mutator: (chapter: Chapter) => Chapter): void {
    const touchedAt = nowIso();
    updateBook((current) => ({
      ...current,
      chapters: current.chapters.map((chapter) =>
        chapter.id === chapterId ? { ...mutator(chapter), updatedAt: touchedAt } : chapter
      ),
    }));
  }

  function closeVariationCompare(): void {
    setActiveCompareVariationId(null);
  }

  function updateChapterVariationContent(variationId: string, content: string): void {
    updateChapter((chapter) => ({
      ...chapter,
      variations: (chapter.variations ?? []).map((variation) =>
        variation.id === variationId && variation.status === "open"
          ? {
              ...variation,
              content: refreshVariationContentFormatting(
                variation.content,
                variation.baseContent || chapter.content,
                content.trim()
              ),
            }
          : variation
      ),
    }));
    setStatus("Variation updated — review your edits in compare view");
  }

  function dismissCompareVariation(variationId: string): void {
    updateChapter((chapter) => ({
      ...chapter,
      variations: (chapter.variations ?? []).map((variation) =>
        variation.id === variationId && variation.status === "open"
          ? { ...variation, status: "dismissed" }
          : variation
      ),
    }));
    setActiveCompareVariationId(null);
  }

  function applyCompareVariation(
    variationId: string,
    mergedContent: string,
    _hunks: ParagraphDiffHunk[]
  ): void {
    flushManuscriptDraft();
    updateChapter((chapter) => {
      const variation = (chapter.variations ?? []).find((entry) => entry.id === variationId);
      const withMarks = buildAppliedChapterContentFromMerge(
        mergedContent,
        chapter.content,
        variation?.content ?? mergedContent
      );
      return {
        ...chapter,
        content: withMarks,
        variations: (chapter.variations ?? []).map((entry) =>
          entry.id === variationId ? { ...entry, status: "applied" } : entry
        ),
      };
    });
    bumpManuscriptReload();
    setActiveCompareVariationId(null);
    setStatus("Applied selected variation changes — use Edit > Undo to revert");
    if (bookRef.current?.id) {
      void trackDesktopActivation({
        event: "activation.variation_applied",
        bookId: bookRef.current.id,
        chapterId: activeChapterIdRef.current || null,
      });
    }
  }

  function moveChapterRelative(
    sourceId: string,
    targetId: string,
    position: ChapterReorderPosition
  ): void {
    if (!book) return;
    const snapshotChapters: ChatBookChapter[] = [...book.chapters]
      .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id))
      .map((chapter, index) => ({
        id: chapter.id,
        title: chapter.title,
        order: chapter.order ?? index + 1,
        content: chapterPlainTextForSync(chapter.content),
      }));
    const result = reorderChaptersById(snapshotChapters, sourceId, targetId, position);
    if (result.error) {
      setStatus(result.error);
      return;
    }
    const source = snapshotChapters.find((chapter) => chapter.id === sourceId);
    const target = snapshotChapters.find((chapter) => chapter.id === targetId);
    applyChapterReorder(result.chapters.map((chapter) => chapter.id));
    if (source && target) {
      setStatus(`Moved "${source.title}" ${position} "${target.title}"`);
    } else {
      setStatus("Chapter order updated");
    }
  }

  function moveChapterByDrop(targetId: string): void {
    if (!dragChapterId || dragChapterId === targetId) return;
    updateBook((current) => {
      const sourceIndex = current.chapters.findIndex((chapter) => chapter.id === dragChapterId);
      const targetIndex = current.chapters.findIndex((chapter) => chapter.id === targetId);
      if (sourceIndex < 0 || targetIndex < 0) return current;
      const chapters = [...current.chapters];
      const [item] = chapters.splice(sourceIndex, 1);
      chapters.splice(targetIndex, 0, item);
      return {
        ...current,
        chapters: withSpineOrderAndNumbers(chapters),
      };
    });
    setDragChapterId(null);
    setStatus("Chapter order updated");
  }

  async function confirmLibraryOverride(): Promise<void> {
    if (!book || libraryOverrideBusy) return;
    setLibraryOverrideBusy(true);
    setLibraryOverrideError(null);
    try {
      const snapshot = bookRef.current ?? book;
      const result = await pullRemoteOverrideLocal(snapshot);
      if (result.error) {
        setLibraryOverrideError(userFacingError(result.error, "open"));
        setStatus(userFacingError(result.error, "open"));
        return;
      }
      const next = ensureBookShape(result.book);
      bookRef.current = next;
      setBook(next);
      bumpManuscriptReload();
      dirtyRef.current = true;
      setDirty(true);
      setActiveChapterId((currentId) =>
        next.chapters.some((ch) => ch.id === currentId) ? currentId : (next.chapters[0]?.id ?? "")
      );
      setShowLibraryOverrideModal(false);
      setLibraryOverrideError(null);
      flushRecoverySave();
      setStatus("Replaced with your library copy. Save to update the file on disk.");
    } catch (error) {
      setLibraryOverrideError(userFacingError(error, "open"));
      setStatus(userFacingError(error, "open"));
    } finally {
      setLibraryOverrideBusy(false);
    }
  }

  function markLocalDiskAuthoritative(): void {
    preferLocalDiskOverCloudRef.current = true;
  }

  function clearLocalDiskAuthoritativeIfSynced(error?: string, pushed?: boolean): void {
    if (!error && pushed) {
      preferLocalDiskOverCloudRef.current = false;
    }
  }

  /** Write book metadata (e.g. sync revs) without flipping the unsaved pill or re-pushing to cloud. */
  async function persistBookQuietly(next: Book): Promise<void> {
    const id = typeof next.id === "string" ? next.id.trim() : "";
    if (!id) return;
    try {
      if (isWebRuntime()) {
        await cacheWebBookDraft(id, next);
        await writeDesktopBackup(next);
        return;
      }
      const path = filePathRef.current?.trim() || null;
      if (path) {
        await window.desktopV2Api.saveBookAtPath({ book: next, filePath: path });
      }
      await writeDesktopBackup(next);
    } catch {
      /* best-effort; next explicit save will retry */
    }
  }

  /**
   * Apply sync result to React state. Rev-only advances persist quietly so they
   * don't block the next pull via the dirty / pushOnly guards.
   */
  async function applySyncResultToState(
    current: Book,
    result: Awaited<ReturnType<typeof autoSyncBook>>,
    options?: { reloadManuscript?: boolean; markDirtyOnMerge?: boolean; treatAsClean?: boolean }
  ): Promise<Book> {
    const reloadManuscript = options?.reloadManuscript ?? true;
    const markDirtyOnMerge = options?.markDirtyOnMerge ?? false;
    const treatAsClean = options?.treatAsClean ?? false;

    // `result.book` was merged from the snapshot taken before the round trip. If
    // generated output or an edit landed while it was in flight, applying it now
    // would silently discard that work, so keep local state and re-push instead.
    if (bookRef.current && bookRef.current !== current) {
      dirtyRef.current = true;
      setDirty(true);
      scheduleCloudPushRef.current("deferred");
      return bookRef.current;
    }

    const next = ensureBookShape(result.book);
    const bodyChanged = activeChapterBodyChanged(current, next, activeChapterIdRef.current);
    const revOnly = Boolean(result.pushed && !result.merged && !result.seeded);

    if (result.merged || result.seeded || result.pushed) {
      bookRef.current = next;
      setBook(next);
      if (reloadManuscript && bodyChanged) bumpManuscriptReload();
      if (treatAsClean) {
        dirtyRef.current = false;
        setDirty(false);
        await persistBookQuietly(next);
      } else if (markDirtyOnMerge || result.merged) {
        setDirty(true);
      } else if (revOnly || result.seeded) {
        dirtyRef.current = false;
        setDirty(false);
        await persistBookQuietly(next);
      }
    }
    return next;
  }

  function clearAutosaveTimer(): void {
    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
  }

  function clearCloudPushTimer(): void {
    if (cloudPushTimerRef.current) {
      window.clearTimeout(cloudPushTimerRef.current);
      cloudPushTimerRef.current = null;
    }
  }

  /**
   * Tier B — push local book to the library/DB. Silent on success; status only on failure.
   */
  async function pushLibraryQuietly(): Promise<void> {
    if (!cloudSaveEnabled) return;
    if (autoSyncInFlightRef.current || autosaveInFlightRef.current) {
      pendingCloudPushRef.current = true;
      return;
    }
    if (aiWriteBusyRef.current) {
      pendingCloudPushRef.current = true;
      return;
    }
    flushManuscriptDraft();
    flushBookUi();
    const current = bookRef.current;
    const bookId = current?.id?.trim() || "";
    if (!current || !bookId) return;
    if (manuscriptEditorRef.current?.hasPendingDraft()) {
      pendingCloudPushRef.current = true;
      scheduleAutosaveRef.current("content");
      return;
    }

    // Prose goes through the durable per-chapter outbox; the book push below
    // only carries structure.
    await bookChapterSync.flushAll(current);

    autoSyncInFlightRef.current = true;
    markLocalDiskAuthoritative();
    try {
      const result = await autoSyncBook(current, { pushOnly: true });
      clearLocalDiskAuthoritativeIfSynced(result.error, result.pushed);
      if (result.error) {
        setStatus(userFacingError(result.error, "sync"));
        return;
      }
      if (result.pushed) {
        await applySyncResultToState(current, result, { reloadManuscript: false });
      }
      if (dirtyRef.current || (manuscriptEditorRef.current?.hasPendingDraft() ?? false)) {
        dirtyRef.current = true;
        setDirty(true);
        pendingCloudPushRef.current = true;
        return;
      }
      // Success stays quiet — Just Write.
    } catch (error) {
      setStatus(userFacingError(error, "sync"));
    } finally {
      autoSyncInFlightRef.current = false;
      if (pendingCloudPushRef.current) {
        pendingCloudPushRef.current = false;
        scheduleCloudPushRef.current("deferred");
      }
    }
  }

  function scheduleCloudPush(kind: "immediate" | "deferred" = "deferred"): void {
    if (!cloudSaveEnabled) return;
    clearCloudPushTimer();
    if (kind === "immediate") {
      void pushLibraryQuietly();
      return;
    }
    cloudPushTimerRef.current = window.setTimeout(() => {
      cloudPushTimerRef.current = null;
      void pushLibraryQuietly();
    }, CLOUD_PUSH_DEBOUNCE_MS);
  }

  scheduleCloudPushRef.current = scheduleCloudPush;

  /**
   * Tier A — persist open document to local disk / IDB cache.
   * Silent: no Saving badge, no footer churn. Schedules deferred library push after success.
   */
  async function autosaveDocument(): Promise<void> {
    if (autosaveInFlightRef.current || autoSyncInFlightRef.current) {
      pendingAutosaveRef.current = true;
      return;
    }
    if (aiWriteBusyRef.current) {
      pendingAutosaveRef.current = true;
      return;
    }

    flushManuscriptDraft();
    flushBookUi();
    const current = bookRef.current;
    if (!current) return;
    const path = filePathRef.current?.trim() || null;
    const bookId = current.id?.trim() || "";
    const canDisk = Boolean(path);
    const canCloud = cloudSaveEnabled && Boolean(bookId);
    if (!canDisk && !canCloud) return;

    autosaveInFlightRef.current = true;
    // Snapshot is flushed; only NEW edits during the write should keep Unsaved.
    dirtyRef.current = false;
    markLocalDiskAuthoritative();
    try {
      let next = current;
      if (path) {
        const saved = await window.desktopV2Api.saveBookAtPath({ book: current, filePath: path });
        next = ensureBookShape({
          ...current,
          ...(saved.persistedBookId ? { id: saved.persistedBookId } : {}),
        });
        bookRef.current = next;
        // Only re-render App if the book id was assigned/changed by disk persist.
        if (saved.persistedBookId && saved.persistedBookId !== current.id) {
          setBook(next);
        }
        await window.desktopV2Api.clearRecovery();
      } else if (isWebRuntime() && bookId) {
        await cacheWebBookDraft(bookId, current);
      }
      await writeDesktopBackup(next);

      flushManuscriptDraft();
      const editsDuringWrite =
        dirtyRef.current || (manuscriptEditorRef.current?.hasPendingDraft() ?? false);
      if (editsDuringWrite) {
        dirtyRef.current = true;
        pendingAutosaveRef.current = true;
        return;
      }

      dirtyRef.current = false;
      setDirty(false);

      if (canCloud) {
        scheduleCloudPush("deferred");
      } else {
        preferLocalDiskOverCloudRef.current = false;
      }
    } catch (error) {
      setStatus(userFacingError(error, "save"));
    } finally {
      autosaveInFlightRef.current = false;
      if (pendingAutosaveRef.current) {
        pendingAutosaveRef.current = false;
        // Re-debounce instead of immediately re-running — keeps typing smooth.
        scheduleAutosave("content");
      }
    }
  }

  function scheduleAutosave(kind: "immediate" | "content"): void {
    if (kind === "immediate") {
      clearAutosaveTimer();
      void autosaveDocument();
      return;
    }
    clearAutosaveTimer();
    autosaveTimerRef.current = window.setTimeout(() => {
      autosaveTimerRef.current = null;
      void autosaveDocument();
    }, LOCAL_CACHE_DEBOUNCE_MS);
  }

  scheduleAutosaveRef.current = scheduleAutosave;

  async function applyAutoSyncToBook(
    localBook: Book,
    options?: {
      markDirtyOnMerge?: boolean;
      reloadManuscript?: boolean;
      pushOnly?: boolean;
      forcePull?: boolean;
      treatAsClean?: boolean;
    }
  ): Promise<{ book: Book; detail: string; error?: string }> {
    const markDirtyOnMerge = options?.markDirtyOnMerge ?? false;
    const reloadManuscript = options?.reloadManuscript ?? true;
    flushManuscriptDraft();
    const pushOnly = options?.forcePull
      ? false
      : (options?.pushOnly ?? preferLocalDiskOverCloudRef.current) || manuscriptHasUnsavedEdits();
    const syncResult = await autoSyncBook(localBook, { pushOnly });
    const next = await applySyncResultToState(localBook, syncResult, {
      reloadManuscript,
      markDirtyOnMerge,
      treatAsClean: options?.treatAsClean,
    });
    clearLocalDiskAuthoritativeIfSynced(syncResult.error, syncResult.pushed);
    if (syncResult.error) {
      return {
        book: next,
        detail: userFacingError(syncResult.error, "sync"),
        error: syncResult.error,
      };
    }
    if (!syncResult.pushed && !syncResult.merged) {
      return { book: next, detail: "" };
    }
    if (isAccountPrimaryRuntime()) {
      return { book: next, detail: "" };
    }
    if (pushOnly) {
      return { book: next, detail: "Pushed local changes to your library." };
    }
    if (syncResult.merged) {
      return { book: next, detail: "Merged newer chapters from your library and synced." };
    }
    return { book: next, detail: "Also synced to your library." };
  }

  async function saveProject(saveAs = false): Promise<void> {
    clearAutosaveTimer();
    clearCloudPushTimer();
    flushManuscriptDraft();
    flushBookUi();
    flushRecoverySave();
    const currentBook = bookRef.current;
    if (!currentBook) return;
    setSaving(true);
    dirtyRef.current = false;
    try {
      const result = await window.desktopV2Api.saveBook(currentBook, saveAs);
      if (!result.canceled) {
        setFilePath(result.filePath ?? null);
        const merged = ensureBookShape({
          ...currentBook,
          ...(result.persistedBookId ? { id: result.persistedBookId } : {}),
        });
        setBook(merged);
        bookRef.current = merged;
        if (result.filePath && merged.id?.trim()) {
          rememberCloudBookLocalPath(merged.id.trim(), result.filePath);
          if (saveAs || result.saveAs) {
            try {
              await registerDesktopLocalCopy(merged.id.trim(), result.filePath);
            } catch {
              // local save succeeded; cloud path registration is best-effort
            }
          }
        }
        await window.desktopV2Api.clearRecovery();

        flushManuscriptDraft();
        const editsDuringWrite =
          dirtyRef.current || (manuscriptEditorRef.current?.hasPendingDraft() ?? false);
        if (editsDuringWrite) {
          dirtyRef.current = true;
          setDirty(true);
          setSaving(false);
          scheduleAutosaveRef.current("content");
          return;
        }

        dirtyRef.current = false;
        setDirty(false);
        setSaving(false);
        let detail = "";
        if (cloudSaveEnabled) {
          try {
            // Just wrote authoritative local state — push only; never pull stale remote over it.
            markLocalDiskAuthoritative();
            const synced = await applyAutoSyncToBook(merged, { pushOnly: true });
            detail = synced.detail;
            if (synced.book !== merged) {
              setBook(synced.book);
              bookRef.current = synced.book;
            }
          } catch (error) {
            detail = ` ${userFacingError(error, "sync")}`;
          }
          if (dirtyRef.current || (manuscriptEditorRef.current?.hasPendingDraft() ?? false)) {
            dirtyRef.current = true;
            setDirty(true);
            scheduleAutosaveRef.current("content");
            return;
          }
        }
        if (detail) {
          const looksLikeProblem =
            /couldn't|could not|didn't reach|try again|sign in|offline|connection dropped/i.test(detail);
          setStatus(looksLikeProblem ? detail : `Saved.${detail.startsWith(" ") ? detail : ` ${detail}`}`);
        }
        else if (isAccountPrimaryRuntime()) setStatus(navigator.onLine ? "Saved" : "Saved. Offline — will sync.");
        await writeDesktopBackup(bookRef.current ?? merged);
      }
    } finally {
      setSaving(false);
    }
  }

  function openOpenFromCloudDialog(): void {
    void (async () => {
      try {
        if (isWebRuntime()) {
          await cloudAuthDesktopV2.ensureSpaSession();
        } else {
          await cloudAuthDesktopV2.restoreSessionFromStorage();
        }
      } catch {
        /* fall through to signed-in check */
      }
      if (!isCloudOpenAvailable()) {
        setStatus(isAccountPrimaryRuntime() ? "Sign in to open your bookshelf." : "Sign in to open books from your library.");
        if (bookRef.current) {
          setWorkbenchView("settings");
        }
        return;
      }
      setShowOpenFromCloud(true);
    })();
  }

  async function openBookFromCloudById(bookId: string): Promise<void> {
    setOpeningCloudBookId(bookId);
    setStatus(isAccountPrimaryRuntime() ? "Opening book…" : "Loading book from your library…");
    try {
      let next = ensureBookShape(await fetchCloudBookDesktop(bookId));
      const disk = await openCloudBookToDisk(next, {
        session: { localBookId: bookRef.current?.id, localFilePath: filePath },
      });
      if (!disk.ok) {
        if ('canceled' in disk && disk.canceled) {
          setStatus(isAccountPrimaryRuntime() ? "Open canceled" : "Open from cloud canceled");
        } else {
          const msg = 'error' in disk ? disk.error : 'Unknown error';
          setStatus(isAccountPrimaryRuntime() ? userFacingError(msg, "open") : `Could not open from cloud: ${userFacingError(msg, "open")}`);
        }
        return;
      }
      if (disk.persistedBookId) {
        next = ensureBookShape({ ...next, id: disk.persistedBookId });
      }
      bookRef.current = next;
      setBook(next);
      setFilePath(disk.filePath);
      setActiveChapterId(next.chapters[0]?.id ?? "");
      setDirty(false);
      await window.desktopV2Api.clearRecovery();
      setShowOpenFromCloud(false);
      bumpManuscriptReload();
      setStatus(
        disk.linkedExisting
          ? `Opened “${next.title || "Untitled"}”${isAccountPrimaryRuntime() ? "" : " from your library (local copy on this computer)"}`
          : `Opened “${next.title || "Untitled"}”${isAccountPrimaryRuntime() ? "" : " from your library"}`
      );
      await writeDesktopBackup(next);
    } catch (error) {
      setStatus(userFacingError(error, "open"));
    } finally {
      setOpeningCloudBookId(null);
    }
  }

  async function reloadProjectFromDisk(): Promise<void> {
    const fp = filePath?.trim();
    if (!fp) {
      setStatus("Reload from File — open a saved .sbk project first");
      return;
    }
    try {
      const payload = await window.desktopV2Api.openBookAtPath(fp);
      if (!payload?.book) {
        setStatus("Could not reload project from disk");
        return;
      }
      const preferredChapterId = activeChapterId;
      const next = ensureBookShape(payload.book);
      markLocalDiskAuthoritative();
      bookRef.current = next;
      setBook(next);
      setFilePath(payload.filePath ?? fp);
      const chapterIds = new Set(next.chapters.map((chapter) => chapter.id));
      setActiveChapterId(chapterIds.has(preferredChapterId) ? preferredChapterId : (next.chapters[0]?.id ?? ""));
      setDirty(false);
      await window.desktopV2Api.clearRecovery();
      bumpManuscriptReload();
      let detail = "";
      if (cloudSaveEnabled && next.id?.trim()) {
        const synced = await applyAutoSyncToBook(next, { pushOnly: true, reloadManuscript: false });
        detail = synced.detail;
      }
      setStatus(`Reloaded “${next.title || "Untitled"}” from disk — unsaved edits were discarded${detail}`);
    } catch (error) {
      setStatus(userFacingError(error, "open"));
    }
  }

  async function openProject(): Promise<void> {
    if (isWebRuntime()) {
      openOpenFromCloudDialog();
      return;
    }
    const payload = await window.desktopV2Api.openBook();
    if (!payload) return;
    const next = ensureBookShape(payload.book);
    markLocalDiskAuthoritative();
    bookRef.current = next;
    setBook(next);
    setFilePath(payload.filePath ?? null);
    if (payload.filePath && next.id?.trim()) {
      rememberCloudBookLocalPath(next.id.trim(), payload.filePath);
    }
    setActiveChapterId(next.chapters[0]?.id ?? "");
    setDirty(false);
    await window.desktopV2Api.clearRecovery();
    bumpManuscriptReload();
    setStatus("Project opened");
    if (cloudSaveEnabled && next.id) {
      try {
        const synced = await applyAutoSyncToBook(next, { pushOnly: true, reloadManuscript: false });
        if (synced.detail.trim()) {
          const looksLikeProblem =
            /couldn't|could not|didn't reach|try again|sign in|offline|connection dropped/i.test(
              synced.detail
            );
          setStatus(looksLikeProblem ? synced.detail : `Project opened. ${synced.detail}`);
        }
      } catch {
        // opening still succeeded
      }
    }
  }

  async function createNewProject(options?: CreateBookOptions): Promise<void> {
    if (isAccountPrimaryRuntime()) {
      if (!cloudAuthDesktopV2.isSignedIn()) {
        setStatus("Sign in to create a book.");
        return;
      }
      setStatus("Creating book…");
      try {
        const payload = await window.desktopV2Api.createNewBook(options);
        const next = ensureBookShape(payload.book);
        const bookId = next.id?.trim() || "";
        if (!bookId) {
          setStatus("Could not create a book id.");
          return;
        }
        bookRef.current = next;
        setBook(next);
        setFilePath(cloudVirtualPath(bookId));
        setActiveChapterId(next.chapters[0]?.id ?? "");
        setDirty(false);
        await cacheWebBookDraft(bookId, next);
        await writeDesktopBackup(next);
        bumpManuscriptReload();

        const online = typeof navigator === "undefined" || navigator.onLine;
        if (online) {
          try {
            await cloudAuthDesktopV2.syncBook(next);
            await upsertBookshelfSummary({
              id: bookId,
              title: next.title || "Untitled",
              author: next.author || "",
              updatedAt: next.updatedAt || new Date().toISOString(),
              chapterCount: next.chapters.length,
            });
            setStatus("New book created");
          } catch (error) {
            await upsertBookshelfSummary({
              id: bookId,
              title: next.title || "Untitled",
              author: next.author || "",
              updatedAt: next.updatedAt || new Date().toISOString(),
              chapterCount: next.chapters.length,
              pendingSync: true,
            });
            setStatus(userFacingError(error, "create"));
          }
          return;
        }

        await upsertBookshelfSummary({
          id: bookId,
          title: next.title || "Untitled",
          author: next.author || "",
          updatedAt: next.updatedAt || new Date().toISOString(),
          chapterCount: next.chapters.length,
          pendingSync: true,
        });
        setStatus("New book saved on this device. It will sync when you're back online.");
      } catch (error) {
        setStatus(userFacingError(error, "create"));
      }
      return;
    }
    const payload = await window.desktopV2Api.createNewBook(options);
    const next = ensureBookShape(payload.book);
    bookRef.current = next;
    setBook(next);
    setFilePath(null);
    setActiveChapterId(next.chapters[0]?.id ?? "");
    setDirty(false);
    setStatus("New project created");
  }

  async function importFromBackupBytes(bytes: Uint8Array): Promise<void> {
    if (isAccountPrimaryRuntime() && !cloudAuthDesktopV2.isSignedIn()) {
      setStatus("Sign in to import a backup.");
      return;
    }
    const imported = ensureBookShape(await loadBookFromBackupBytes(bytes));
    const bookId = imported.id?.trim() || "";
    if (!bookId) {
      setStatus("That backup has no book id.");
      return;
    }
    bookRef.current = imported;
    setBook(imported);
    setFilePath(isWebRuntime() ? cloudVirtualPath(bookId) : filePathRef.current);
    setActiveChapterId(imported.chapters[0]?.id ?? "");
    setDirty(true);
    await cacheWebBookDraft(bookId, imported);
    await writeDesktopBackup(imported);
    await upsertBookshelfSummary({
      id: bookId,
      title: imported.title || "Untitled",
      author: imported.author || "",
      updatedAt: imported.updatedAt || new Date().toISOString(),
      chapterCount: imported.chapters.length,
      pendingSync: typeof navigator !== "undefined" && !navigator.onLine,
    });
    setShowOpenFromCloud(false);
    bumpManuscriptReload();
    if (cloudAuthDesktopV2.isSignedIn() && (typeof navigator === "undefined" || navigator.onLine)) {
      try {
        const synced = await applyAutoSyncToBook(imported, { pushOnly: true, reloadManuscript: false });
        setStatus(`Imported “${imported.title || "Untitled"}”.${synced.detail ? ` ${synced.detail}` : ""}`);
        return;
      } catch (error) {
        setStatus(`Imported “${imported.title || "Untitled"}”. ${userFacingError(error, "import")}`);
        return;
      }
    }
    setStatus(`Imported “${imported.title || "Untitled"}”. It will sync when you're back online.`);
  }

  function handleCloudAuthChange(signedIn: boolean): void {
    setCloudSaveEnabled(signedIn);
    if (!signedIn && isAccountPrimaryRuntime()) {
      bookRef.current = null;
      setBook(null);
      setFilePath(null);
      setActiveChapterId("");
      setDirty(false);
      setStatus("Signed out");
    }
  }

  async function runStoryCraft(): Promise<void> {
    if (!activeChapter || !book) return;
    flushManuscriptDraft();
    clearChapterStoryCraftComments();
    setStoryCraftBusy(true);
    setActiveTab("storyCraft");
    setWorkbenchView("book");
    setShowAssistantPanel(true);
    setAssistantPanelTab("actions");
    setStatus("Running Story Craft analysis…");

    const placeholder = storyCraftAnalyzingPlaceholder(activeChapter.id, activeChapter.title);
    updateBook((current) => {
      const assistant = getAssistantState(current);
      assistant.storyCraftByChapter[activeChapter.id] = placeholder;
      return withAssistantState(current, assistant);
    });

    const chapterForAnalysis =
      bookRef.current?.chapters.find((c) => c.id === activeChapter.id) ?? activeChapter;
    const chapterText = chapterPlainTextForSync(chapterForAnalysis.content).trim();
    if (!chapterText) {
      const errFb = storyCraftErrorFeedback(activeChapter.id, activeChapter.title, "Chapter has no text to analyze.");
      updateBook((current) => {
        const assistant = getAssistantState(current);
        assistant.storyCraftByChapter[activeChapter.id] = errFb;
        return withAssistantState(current, assistant);
      });
      setStatus("Add manuscript text before running Story Craft.");
      setStoryCraftBusy(false);
      return;
    }

    const maxChars = 120_000;
    const trimmedText =
      chapterText.length > maxChars ? `${chapterText.slice(0, maxChars)}\n\n[…truncated for analysis…]` : chapterText;

    const previousPromises = collectPreviousPromisesForChapter(book, getAssistantState(book), activeChapter);
    const themesRaw = themesMotifsRosterAsString(
      getThemesFromBookMetadata((bookRef.current ?? book).metadata as Record<string, unknown>),
      getMotifsFromBookMetadata((bookRef.current ?? book).metadata as Record<string, unknown>),
      getSymbolsFromBookMetadata((bookRef.current ?? book).metadata as Record<string, unknown>)
    ).trim();
    const themes = themesRaw
      ? themesRaw.slice(0, 6000)
      : activeChapter.tabData.themesMotifs.trim()
        ? activeChapter.tabData.themesMotifs.trim().slice(0, 6000)
        : undefined;
    const allChaptersInOrder = storyCraftUsesFullManuscript(analysisModel)
      ? buildAllChaptersForStoryCraft(bookRef.current ?? book, activeChapter.id)
      : undefined;

    try {
      const storyPayload: Record<string, unknown> = {
        chapterText: trimmedText,
        chapterId: activeChapter.id,
        chapterTitle: activeChapter.title,
        provider: analysisProvider,
        model: analysisModel,
        masterPrompt: getMasterPrompt(),
        themes,
        previousPromises: previousPromises.length ? previousPromises : undefined,
        ...(allChaptersInOrder && allChaptersInOrder.length > 1
          ? { allChaptersInOrder }
          : {}),
      };
      const api = window.desktopV2Api;
      let feedback: DesktopStoryCraftFeedback;
      if (typeof api.storyCraftExtract === "function") {
        feedback = await api.storyCraftExtract(storyPayload);
      } else if (typeof api.chatComplete === "function") {
        const res = await api.chatComplete({
          prompt: "\u200b",
          context: "",
          chapterTitle: activeChapter.title,
          scope: "chapter",
          provider: chatProvider,
          model: chatModel,
          storyCraftExtractPayload: storyPayload,
        });
        const fb = res.storyCraftFeedback;
        if (!fb) {
          throw new Error(
            "Story Craft bridge returned no data. Quit Electron fully, then run npm run electron:build in apps/desktop-v2 and launch again."
          );
        }
        feedback = fb;
      } else {
        throw new Error(
          "Desktop preload is missing Story Craft APIs. Quit Electron fully, run npm run electron:build in apps/desktop-v2, then launch again."
        );
      }
      updateBook((current) => {
        const assistant = getAssistantState(current);
        assistant.storyCraftByChapter[activeChapter.id] = feedback;
        return withAssistantState(current, assistant);
      });
      setStatus("Story Craft analysis is ready");
      void trackDesktopActivation({
        event: "activation.story_craft_run",
        bookId: book.id,
        chapterId: activeChapter.id,
      });
    } catch (error) {
      const msg = userFacingError(error, "generic");
      const errFb = storyCraftErrorFeedback(activeChapter.id, activeChapter.title, msg);
      updateBook((current) => {
        const assistant = getAssistantState(current);
        assistant.storyCraftByChapter[activeChapter.id] = errFb;
        return withAssistantState(current, assistant);
      });
      setStatus(userFacingError(error, "generic"));
    } finally {
      setStoryCraftBusy(false);
    }
  }

  function applyStoryCraft(target: "draft" | "notes"): void {
    if (!activeChapter || !activeStoryCraft) return;
    if (storyCraftBusy || activeStoryCraft.summary === "Analyzing…" || activeStoryCraft.summary.startsWith("Analysis failed")) return;
    const bullets =
      activeStoryCraft.checklist.length > 0
        ? activeStoryCraft.checklist.map((c) => `- ${c.suggestion}`).join("\n")
        : `- ${activeStoryCraft.summary}`.trim();
    if (target === "draft") {
      updateChapter((chapter) => {
        const body = chapterContentToPlainText(chapter.content).trim();
        const appended = [body, "Revision goals", bullets].filter(Boolean).join("\n\n");
        return {
          ...chapter,
          content: serializeChapterContent(plainTextToChapterDoc(appended)),
        };
      });
      bumpManuscriptReload();
      setStatus("Story Craft checklist added to manuscript");
      return;
    }
    updateChapter((chapter) => {
      const currentNotes =
        typeof chapter.metadata.customFields?.assistantNotes === "string"
          ? chapter.metadata.customFields.assistantNotes.trim()
          : "";
      return {
        ...chapter,
        metadata: {
          ...chapter.metadata,
          customFields: {
            ...chapter.metadata.customFields,
            assistantNotes: [currentNotes, "Story Craft revision checklist", bullets]
              .filter(Boolean)
              .join("\n\n"),
          },
        },
      };
    });
    setStatus("Story Craft checklist added to revision notes");
  }

  function toggleStoryCraftChecklistItem(itemId: string, completed: boolean): void {
    const chapterId = activeChapterId;
    updateBook((current) => {
      const assistant = getAssistantState(current);
      const fb = assistant.storyCraftByChapter[chapterId];
      if (!fb) return current;
      const nextChecklist = fb.checklist.map((item) =>
        item.id === itemId
          ? { ...item, isCompleted: completed, completedAt: completed ? nowIso() : undefined }
          : item
      );
      const nextFb = { ...fb, checklist: nextChecklist, lastUpdated: nowIso() };
      assistant.storyCraftByChapter[chapterId] = nextFb;
      return withAssistantState(current, assistant);
    });
    setStatus("Story Craft checklist updated");
  }

  function updateStoryCraftChecklistItem(
    itemId: string,
    patch: { category?: string; suggestion?: string }
  ): void {
    const chapterId = activeChapterId;
    updateBook((current) => {
      const assistant = getAssistantState(current);
      const fb = assistant.storyCraftByChapter[chapterId];
      if (!fb) return current;
      const nextChecklist = fb.checklist.map((item) => {
        if (item.id !== itemId) return item;
        return {
          ...item,
          category: patch.category !== undefined ? patch.category.trim() || "general" : item.category,
          suggestion: patch.suggestion !== undefined ? patch.suggestion.trim() : item.suggestion,
        };
      });
      assistant.storyCraftByChapter[chapterId] = { ...fb, checklist: nextChecklist, lastUpdated: nowIso() };
      return withAssistantState(current, assistant);
    });
    setStatus("Story Craft checklist updated");
  }

  function addStoryCraftChecklistItem(): void {
    const chapterId = activeChapterId;
    updateBook((current) => {
      const assistant = getAssistantState(current);
      const fb = assistant.storyCraftByChapter[chapterId];
      if (!fb) return current;
      assistant.storyCraftByChapter[chapterId] = {
        ...fb,
        checklist: [...fb.checklist, createStoryCraftChecklistItem()],
        lastUpdated: nowIso(),
      };
      return withAssistantState(current, assistant);
    });
    setStatus("Story Craft checklist item added");
  }

  function deleteStoryCraftChecklistItem(itemId: string): void {
    const chapterId = activeChapterId;
    updateBook((current) => {
      const assistant = getAssistantState(current);
      const fb = assistant.storyCraftByChapter[chapterId];
      if (!fb) return current;
      assistant.storyCraftByChapter[chapterId] = {
        ...fb,
        checklist: fb.checklist.filter((item) => item.id !== itemId),
        lastUpdated: nowIso(),
      };
      return withAssistantState(current, assistant);
    });
    setStatus("Story Craft checklist item removed");
  }

  function deleteStoryCraftForActiveChapter(): void {
    const ch = book?.chapters.find((c) => c.id === activeChapterId);
    if (!ch) return;
    if (!window.confirm(`Delete story craft analysis for ${ch.title}?`)) return;
    updateBook((current) => {
      const assistant = getAssistantState(current);
      delete assistant.storyCraftByChapter[ch.id];
      return withAssistantState(current, assistant);
    });
    setStatus("Story Craft analysis removed");
  }

  async function generateSummary(): Promise<void> {
    if (!activeChapter || summaryBusy) return;
    const targetChapter = activeChapter;
    const chapterText = chapterPlainTextForSync(targetChapter.content).trim();
    if (!chapterText) {
      setStatus("Add manuscript text before generating a chapter brief.");
      return;
    }
    const summaryApi = window.desktopV2Api.chapterSummaryGenerate;
    if (typeof summaryApi !== "function") {
      setStatus("Chapter Brief generator is unavailable. Restart the desktop app to load the latest build.");
      return;
    }

    const roster = getCharacterRoster();
    const characterContext = roster
      .map((character) => {
        const aliases = character.aliases.length
          ? ` (aliases: ${character.aliases.join(", ")})`
          : "";
        return `- ${character.name}${aliases}: ${character.description || "No book description yet."}`;
      })
      .join("\n");

    setSummaryBusy(true);
    setStatus(`Generating chapter brief with ${analysisModel}…`);
    try {
      const result = await summaryApi({
        chapterText:
          chapterText.length > 120_000
            ? `${chapterText.slice(0, 120_000)}\n\n[…truncated for analysis…]`
            : chapterText,
        chapterTitle: targetChapter.title,
        bookTitle: book?.title,
        characterContext: characterContext || undefined,
        provider: analysisProvider,
        model: analysisModel,
      });
      updateBook((current) => ({
        ...current,
        chapters: current.chapters.map((chapter) =>
          chapter.id === targetChapter.id
            ? {
                ...chapter,
                tabData: {
                  ...chapter.tabData,
                  summary: result.summary,
                },
              }
            : chapter
        ),
      }));
      setActiveTab("summary");
      setStatus(`Chapter brief generated with ${result.model}`);
    } catch (error) {
      setStatus(userFacingError(error, "generate"));
    } finally {
      setSummaryBusy(false);
    }
  }

  function getCharacterRoster(current: Book = bookRef.current ?? book!): BookCharacter[] {
    return getCharactersFromBookMetadata((current.metadata ?? {}) as Record<string, unknown>);
  }

  function getCharacterRemovals(current: Book = bookRef.current ?? book!): ReturnType<typeof getCharacterRemovalsFromMetadata> {
    return getCharacterRemovalsFromMetadata((current.metadata ?? {}) as Record<string, unknown>);
  }

  function applyCharacterRoster(current: Book, roster: BookCharacter[]): Book {
    const sorted = sortCharactersMajorFirst(roster);
    const meta = withCharactersInMetadata(
      (current.metadata ?? {}) as Record<string, unknown>,
      sorted
    ) as Book["metadata"];
    const chapters = current.chapters.map((chapter) => ({
      ...chapter,
      tabData: {
        ...chapter.tabData,
        characters: chapterCharacterTabText(sorted, chapter.id),
      },
    }));
    return { ...current, metadata: meta, chapters };
  }

  function updateCharacterInRoster(id: string, patch: Partial<BookCharacter>): void {
    updateBook((current) => {
      const roster = getCharacterRoster(current).map((c) => {
        if (c.id !== id) return c;
        const merged = normalizeBookCharacter({
          ...c,
          ...patch,
          id: c.id,
          updatedAt: nowIso(),
        });
        return merged ?? { ...c, ...patch, updatedAt: nowIso() };
      });
      return applyCharacterRoster(current, roster);
    });
  }

  function requestCharacterRemoval(character: BookCharacter): void {
    setPendingCharacterRemoval(character);
  }

  function removeCharacterFromRoster(id: string, reason: string): void {
    const trimmedReason = reason.trim();
    if (trimmedReason.length < 3) return;
    updateBook((current) => {
      const roster = getCharacterRoster(current);
      const target = roster.find((c) => c.id === id);
      if (!target) return current;
      const nextRoster = roster.filter((c) => c.id !== id);
      const removals = [
        createCharacterRemovalRecord(target, trimmedReason),
        ...getCharacterRemovals(current),
      ];
      const meta = withCharacterRemovalsInMetadata(
        withCharactersInMetadata((current.metadata ?? {}) as Record<string, unknown>, nextRoster),
        removals
      ) as Book["metadata"];
      const chapters = current.chapters.map((chapter) => ({
        ...chapter,
        tabData: {
          ...chapter.tabData,
          characters: chapterCharacterTabText(nextRoster, chapter.id),
        },
      }));
      return { ...current, metadata: meta, chapters };
    });
    if (selectedCharacterId === id) setSelectedCharacterId(null);
    setPendingCharacterRemoval(null);
    setStatus(`Removed character and saved reason for future extract`);
  }

  /** @deprecated use removeCharacterFromRoster */
  function deleteCharacterFromRoster(id: string): void {
    const target = getCharacterRoster().find((c) => c.id === id);
    if (target) requestCharacterRemoval(target);
  }

  function addCharacterToRoster(role: CharacterRole, chapterId?: string): void {
    const id = generateCharacterId();
    const now = nowIso();
    const appearanceChapterId = chapterId ?? activeChapterId;
    const character: BookCharacter = {
      id,
      name: role === "major" ? "New major character" : "New minor character",
      aliases: [],
      description: "",
      role,
      firstAppearanceChapterId: appearanceChapterId || undefined,
      appearances: appearanceChapterId
        ? [
            {
              chapterId: appearanceChapterId,
              ...(role === "major" ? { levers: createBlankLevers() } : {}),
            },
          ]
        : [],
      updatedAt: now,
    };
    updateBook((current) => applyCharacterRoster(current, [...getCharacterRoster(current), character]));
    setSelectedCharacterId(id);
    setStatus(`Added ${role} character`);
  }

  function openCharacterInGuide(id: string): void {
    setSelectedCharacterId(id);
    setWorkbenchView("characters");
  }

  async function runCharacterExtraction(): Promise<void> {
    if (!activeChapter || !book) return;
    flushManuscriptDraft();
    setCharacterExtractBusy(true);
    setActiveTab("characters");
    setWorkbenchView("book");
    setStatus("Extracting characters…");

    const chapterForAnalysis =
      bookRef.current?.chapters.find((c) => c.id === activeChapter.id) ?? activeChapter;
    const chapterText = chapterPlainTextForSync(chapterForAnalysis.content).trim();
    if (!chapterText) {
      setStatus("Add manuscript text before extracting characters.");
      setCharacterExtractBusy(false);
      return;
    }

    const maxChars = 120_000;
    const trimmedText =
      chapterText.length > maxChars ? `${chapterText.slice(0, maxChars)}\n\n[…truncated for analysis…]` : chapterText;

    try {
      const existingCharacters = getCharacterRoster(bookRef.current ?? book);
      const characterRemovals = getCharacterRemovals(bookRef.current ?? book);
      const payload: Record<string, unknown> = {
        chapterText: trimmedText,
        chapterId: activeChapter.id,
        chapterTitle: activeChapter.title,
        existingCharacters,
        characterRemovals,
        provider: analysisProvider,
        model: analysisModel,
        masterPrompt: getMasterPrompt(),
      };
      const api = window.desktopV2Api;
      if (typeof api.characterExtract !== "function") {
        throw new Error("Character extract is unavailable. Rebuild the Electron app (preload).");
      }
      const result = await api.characterExtract(payload);
      updateBook((current) => {
        const merged = mergeExtractedCharactersIntoRoster({
          roster: getCharacterRoster(current),
          extracted: result.characters,
          chapterId: activeChapter.id,
          removals: getCharacterRemovals(current),
        });
        return applyCharacterRoster(current, merged);
      });
      const majorCount = result.characters.filter((c) => c.role === "major").length;
      const minorCount = result.characters.length - majorCount;
      setStatus(
        `Extracted ${result.characters.length} character${result.characters.length === 1 ? "" : "s"} (${majorCount} major, ${minorCount} minor)`
      );
    } catch (err) {
      setStatus(userFacingError(err, "generic"));
    } finally {
      setCharacterExtractBusy(false);
    }
  }

  async function runWholeBookCharacterExtraction(): Promise<void> {
    if (!book || wholeBookCharacterBusy) return;
    flushManuscriptDraft();
    const current = bookRef.current ?? book;
    const manuscriptChapters = [...current.chapters]
      .sort((a, b) => a.order - b.order)
      .map((chapter) => ({
        chapterId: chapter.id,
        title: chapter.title,
        order: chapter.order,
        text: chapterPlainTextForSync(chapter.content).trim(),
      }))
      .filter((chapter) => chapter.text.length > 0);
    if (manuscriptChapters.length === 0) {
      setStatus("Add manuscript text before analyzing whole-book characters.");
      return;
    }
    if (
      !window.confirm(
        "Analyze the full manuscript to build or refresh the holistic character roster? This is normally run once after the book has substantial content."
      )
    ) {
      return;
    }

    // Keep every chapter in context while bounding very large manuscripts.
    const totalBudget = 800_000;
    const perChapterBudget = Math.max(3_000, Math.floor(totalBudget / manuscriptChapters.length));
    const boundedChapters = manuscriptChapters.map((chapter) => ({
      ...chapter,
      text:
        chapter.text.length > perChapterBudget
          ? `${chapter.text.slice(0, perChapterBudget)}\n\n[…chapter truncated for whole-book analysis…]`
          : chapter.text,
    }));

    setWholeBookCharacterBusy(true);
    setStatus(`Analyzing ${boundedChapters.length} chapters for the holistic character roster…`);
    try {
      const payload: Record<string, unknown> = {
        mode: "book",
        chapterText: "Whole-book character analysis",
        chapterId: "__whole-book__",
        chapterTitle: current.title,
        allChaptersInOrder: boundedChapters,
        existingCharacters: getCharacterRoster(current),
        characterRemovals: getCharacterRemovals(current),
        provider: analysisProvider,
        model: analysisModel,
        masterPrompt: getMasterPrompt(),
      };
      const api = window.desktopV2Api;
      if (typeof api.characterExtract !== "function") {
        throw new Error("Character extract is unavailable. Rebuild the Electron app (preload).");
      }
      const result = await api.characterExtract(payload);
      updateBook((latest) => {
        const merged = mergeHolisticCharactersIntoRoster({
          roster: getCharacterRoster(latest),
          extracted: result.characters,
          validChapterIds: latest.chapters.map((chapter) => chapter.id),
          removals: getCharacterRemovals(latest),
        });
        return applyCharacterRoster(latest, merged);
      });
      const firstMajor = result.characters.find((character) => character.role === "major");
      if (firstMajor) {
        const matched = getCharacterRoster(bookRef.current ?? current).find(
          (character) => character.name === firstMajor.name
        );
        if (matched) setSelectedCharacterId(matched.id);
      }
      setStatus(
        `Whole-book character analysis complete: ${result.characters.length} character${
          result.characters.length === 1 ? "" : "s"
        } reviewed.`
      );
    } catch (error) {
      setStatus(userFacingError(error, "generic"));
    } finally {
      setWholeBookCharacterBusy(false);
    }
  }

  /** @deprecated name kept for menu / Actions wiring — now LLM character extract */
  function runEntityExtraction(): void {
    void runCharacterExtraction();
  }

  function getLocationRoster(current: Book = bookRef.current ?? book!): BookLocation[] {
    return getLocationsFromBookMetadata((current.metadata ?? {}) as Record<string, unknown>);
  }

  function getLocationRelationships(
    current: Book = bookRef.current ?? book!
  ): LocationRelationship[] {
    return getLocationRelationshipsFromMetadata((current.metadata ?? {}) as Record<string, unknown>);
  }

  function getLocationRemovals(
    current: Book = bookRef.current ?? book!
  ): ReturnType<typeof getLocationRemovalsFromMetadata> {
    return getLocationRemovalsFromMetadata((current.metadata ?? {}) as Record<string, unknown>);
  }

  function applyLocationRoster(
    current: Book,
    roster: BookLocation[],
    relationships?: LocationRelationship[]
  ): Book {
    const sorted = sortLocations(roster);
    const nextRelationships = relationships ?? getLocationRelationships(current);
    const meta = withLocationsInMetadata(
      (current.metadata ?? {}) as Record<string, unknown>,
      sorted,
      nextRelationships
    ) as Book["metadata"];
    const chapters = current.chapters.map((chapter) => ({
      ...chapter,
      tabData: {
        ...chapter.tabData,
        locations: chapterLocationTabText(sorted, chapter.id),
      },
    }));
    return { ...current, metadata: meta, chapters };
  }

  function updateLocationInRoster(id: string, patch: Partial<BookLocation>): void {
    updateBook((current) => {
      const roster = getLocationRoster(current).map((loc) => {
        if (loc.id !== id) return loc;
        const merged = normalizeBookLocation({
          ...loc,
          ...patch,
          id: loc.id,
          updatedAt: nowIso(),
        });
        return merged ?? { ...loc, ...patch, updatedAt: nowIso() };
      });
      return applyLocationRoster(current, roster);
    });
  }

  function updateLocationRelationships(next: LocationRelationship[]): void {
    updateBook((current) => applyLocationRoster(current, getLocationRoster(current), next));
  }

  function requestLocationRemoval(location: BookLocation): void {
    setPendingLocationRemoval(location);
  }

  function removeLocationFromRoster(id: string, reason: string): void {
    const trimmedReason = reason.trim();
    if (trimmedReason.length < 3) return;
    updateBook((current) => {
      const roster = getLocationRoster(current);
      const target = roster.find((loc) => loc.id === id);
      if (!target) return current;
      const nextRoster = roster
        .filter((loc) => loc.id !== id)
        .map((loc) =>
          loc.parentLocationId === id ? { ...loc, parentLocationId: undefined } : loc
        );
      const relationships = getLocationRelationships(current).filter(
        (rel) => rel.fromLocationId !== id && rel.toLocationId !== id
      );
      const removals = [
        createLocationRemovalRecord(target, trimmedReason),
        ...getLocationRemovals(current),
      ];
      const meta = withLocationRemovalsInMetadata(
        withLocationsInMetadata(
          (current.metadata ?? {}) as Record<string, unknown>,
          nextRoster,
          relationships
        ),
        removals
      ) as Book["metadata"];
      const chapters = current.chapters.map((chapter) => ({
        ...chapter,
        tabData: {
          ...chapter.tabData,
          locations: chapterLocationTabText(nextRoster, chapter.id),
        },
      }));
      return { ...current, metadata: meta, chapters };
    });
    if (selectedLocationId === id) setSelectedLocationId(null);
    setPendingLocationRemoval(null);
    setStatus(`Removed location and saved reason for future extract`);
  }

  function addLocationToRoster(chapterId?: string): void {
    const id = generateLocationId();
    const now = nowIso();
    const appearanceChapterId = chapterId ?? activeChapterId;
    const location: BookLocation = {
      id,
      name: "New location",
      aliases: [],
      type: "other",
      description: "",
      firstAppearanceChapterId: appearanceChapterId || undefined,
      appearances: appearanceChapterId ? [{ chapterId: appearanceChapterId }] : [],
      updatedAt: now,
    };
    updateBook((current) => applyLocationRoster(current, [...getLocationRoster(current), location]));
    setSelectedLocationId(id);
    setStatus("Added location");
  }

  function openLocationInGuide(id: string): void {
    setSelectedLocationId(id);
    setWorkbenchView("locations");
  }

  async function runLocationExtraction(): Promise<void> {
    if (!activeChapter || !book) return;
    flushManuscriptDraft();
    setLocationExtractBusy(true);
    setActiveTab("locations");
    setWorkbenchView("book");
    setStatus("Extracting locations…");

    const chapterForAnalysis =
      bookRef.current?.chapters.find((c) => c.id === activeChapter.id) ?? activeChapter;
    const chapterText = chapterPlainTextForSync(chapterForAnalysis.content).trim();
    if (!chapterText) {
      setStatus("Add manuscript text before extracting locations.");
      setLocationExtractBusy(false);
      return;
    }

    const maxChars = 120_000;
    const trimmedText =
      chapterText.length > maxChars ? `${chapterText.slice(0, maxChars)}\n\n[…truncated for analysis…]` : chapterText;

    try {
      const existingLocations = getLocationRoster(bookRef.current ?? book);
      const locationRemovals = getLocationRemovals(bookRef.current ?? book);
      const payload: Record<string, unknown> = {
        chapterText: trimmedText,
        chapterId: activeChapter.id,
        chapterTitle: activeChapter.title,
        existingLocations,
        locationRemovals,
        provider: analysisProvider,
        model: analysisModel,
        masterPrompt: getMasterPrompt(),
      };
      const api = window.desktopV2Api;
      if (typeof api.locationExtract !== "function") {
        throw new Error("Location extract is unavailable. Rebuild the Electron app (preload).");
      }
      const result = await api.locationExtract(payload);
      updateBook((current) => {
        const merged = mergeExtractedLocationsIntoRoster({
          roster: getLocationRoster(current),
          extracted: result.locations,
          chapterId: activeChapter.id,
          removals: getLocationRemovals(current),
        });
        const relationships = mergeExtractedRelationships({
          relationships: getLocationRelationships(current),
          extracted: result.relationships ?? [],
          roster: merged,
        });
        return applyLocationRoster(current, merged, relationships);
      });
      setStatus(
        `Extracted ${result.locations.length} location${result.locations.length === 1 ? "" : "s"}${
          result.relationships?.length
            ? ` and ${result.relationships.length} relationship${
                result.relationships.length === 1 ? "" : "s"
              }`
            : ""
        }`
      );
    } catch (err) {
      setStatus(userFacingError(err, "generic"));
    } finally {
      setLocationExtractBusy(false);
    }
  }

  async function runWholeBookLocationExtraction(): Promise<void> {
    if (!book || wholeBookLocationBusy) return;
    flushManuscriptDraft();
    const current = bookRef.current ?? book;
    const manuscriptChapters = [...current.chapters]
      .sort((a, b) => a.order - b.order)
      .map((chapter) => ({
        chapterId: chapter.id,
        title: chapter.title,
        order: chapter.order,
        text: chapterPlainTextForSync(chapter.content).trim(),
      }))
      .filter((chapter) => chapter.text.length > 0);
    if (manuscriptChapters.length === 0) {
      setStatus("Add manuscript text before analyzing whole-book locations.");
      return;
    }
    if (
      !window.confirm(
        "Analyze the full manuscript to build or refresh the holistic location gazetteer? This is normally run once after the book has substantial content."
      )
    ) {
      return;
    }

    const totalBudget = 800_000;
    const perChapterBudget = Math.max(3_000, Math.floor(totalBudget / manuscriptChapters.length));
    const boundedChapters = manuscriptChapters.map((chapter) => ({
      ...chapter,
      text:
        chapter.text.length > perChapterBudget
          ? `${chapter.text.slice(0, perChapterBudget)}\n\n[…chapter truncated for whole-book analysis…]`
          : chapter.text,
    }));

    setWholeBookLocationBusy(true);
    setStatus(`Analyzing ${boundedChapters.length} chapters for the holistic location gazetteer…`);
    try {
      const payload: Record<string, unknown> = {
        mode: "book",
        chapterText: "Whole-book location analysis",
        chapterId: "__whole-book__",
        chapterTitle: current.title,
        allChaptersInOrder: boundedChapters,
        existingLocations: getLocationRoster(current),
        locationRemovals: getLocationRemovals(current),
        provider: analysisProvider,
        model: analysisModel,
        masterPrompt: getMasterPrompt(),
      };
      const api = window.desktopV2Api;
      if (typeof api.locationExtract !== "function") {
        throw new Error("Location extract is unavailable. Rebuild the Electron app (preload).");
      }
      const result = await api.locationExtract(payload);
      updateBook((latest) => {
        const merged = mergeHolisticLocationsIntoRoster({
          roster: getLocationRoster(latest),
          extracted: result.locations,
          validChapterIds: latest.chapters.map((chapter) => chapter.id),
          removals: getLocationRemovals(latest),
        });
        const relationships = mergeExtractedRelationships({
          relationships: getLocationRelationships(latest),
          extracted: result.relationships ?? [],
          roster: merged,
        });
        return applyLocationRoster(latest, merged, relationships);
      });
      const first = result.locations[0];
      if (first) {
        const matched = getLocationRoster(bookRef.current ?? current).find(
          (location) => location.name === first.name
        );
        if (matched) setSelectedLocationId(matched.id);
      }
      setStatus(
        `Whole-book location analysis complete: ${result.locations.length} location${
          result.locations.length === 1 ? "" : "s"
        } reviewed${
          result.relationships?.length
            ? `, ${result.relationships.length} relationship${
                result.relationships.length === 1 ? "" : "s"
              }`
            : ""
        }.`
      );
    } catch (error) {
      setStatus(userFacingError(error, "generic"));
    } finally {
      setWholeBookLocationBusy(false);
    }
  }

  function getTimelineRoster(current: Book = bookRef.current ?? book!): BookTimelineBeat[] {
    return getTimelineBeatsFromBookMetadata((current.metadata ?? {}) as Record<string, unknown>);
  }

  function getTimelineIssues(
    current: Book = bookRef.current ?? book!
  ): ReturnType<typeof getTimelineIssuesFromBookMetadata> {
    return getTimelineIssuesFromBookMetadata((current.metadata ?? {}) as Record<string, unknown>);
  }

  function getTimelineRemovals(
    current: Book = bookRef.current ?? book!
  ): ReturnType<typeof getTimelineRemovalsFromMetadata> {
    return getTimelineRemovalsFromMetadata((current.metadata ?? {}) as Record<string, unknown>);
  }

  function applyTimelineRoster(
    current: Book,
    roster: BookTimelineBeat[],
    issues?: ReturnType<typeof getTimelineIssuesFromBookMetadata>,
    removals?: ReturnType<typeof getTimelineRemovalsFromMetadata>
  ): Book {
    const sorted = sortBeatsChronologically(roster);
    const meta = withTimelineInMetadata(
      (current.metadata ?? {}) as Record<string, unknown>,
      sorted,
      issues,
      removals
    ) as Book["metadata"];
    const chapters = current.chapters.map((chapter) => ({
      ...chapter,
      tabData: {
        ...chapter.tabData,
        timeline: chapterTimelineTabText(sorted, chapter.id),
      },
    }));
    return { ...current, metadata: meta, chapters };
  }

  function updateTimelineBeatInRoster(id: string, patch: Partial<BookTimelineBeat>): void {
    updateBook((current) => {
      const roster = getTimelineRoster(current).map((beat) =>
        beat.id === id ? { ...beat, ...patch, updatedAt: nowIso() } : beat
      );
      return applyTimelineRoster(current, roster);
    });
  }

  function requestTimelineBeatRemoval(beat: BookTimelineBeat): void {
    setPendingTimelineBeatRemoval(beat);
  }

  function removeTimelineBeatFromRoster(id: string, reason: string): void {
    updateBook((current) => {
      const roster = getTimelineRoster(current);
      const target = roster.find((beat) => beat.id === id);
      if (!target) return current;
      const nextRoster = roster.filter((beat) => beat.id !== id);
      const removals = [
        createTimelineRemovalRecord(target, reason),
        ...getTimelineRemovals(current),
      ];
      const meta = withTimelineRemovalsInMetadata(
        withTimelineInMetadata(
          (current.metadata ?? {}) as Record<string, unknown>,
          nextRoster,
          getTimelineIssues(current)
        ),
        removals
      ) as Book["metadata"];
      const chapters = current.chapters.map((chapter) => ({
        ...chapter,
        tabData: {
          ...chapter.tabData,
          timeline: chapterTimelineTabText(nextRoster, chapter.id),
        },
      }));
      return { ...current, metadata: meta, chapters };
    });
    if (selectedTimelineBeatId === id) setSelectedTimelineBeatId(null);
    setPendingTimelineBeatRemoval(null);
    setStatus("Timeline beat removed");
  }

  function addTimelineBeatToRoster(chapterId?: string): void {
    const id = generateTimelineBeatId();
    const chId = chapterId ?? activeChapterId ?? "";
    updateBook((current) => {
      const beat: BookTimelineBeat = {
        id,
        summary: "New major beat",
        when: "",
        sortKey: "",
        datePrecision: "unknown",
        beatKind: "plot",
        narrativePosition: "present",
        chapterId: chId,
        chapterIds: chId ? [chId] : [],
        updatedAt: nowIso(),
      };
      return applyTimelineRoster(current, [...getTimelineRoster(current), beat]);
    });
    setSelectedTimelineBeatId(id);
    setStatus("Added timeline beat");
  }

  function openTimelineBeatInGuide(id: string): void {
    setSelectedTimelineBeatId(id);
    setWorkbenchView("timeline");
  }

  async function runTimelineExtraction(): Promise<void> {
    if (!activeChapter || !book) return;
    flushManuscriptDraft();
    setTimelineExtractBusy(true);
    setActiveTab("timeline");
    setWorkbenchView("book");
    setStatus("Extracting timeline beats…");

    const chapterForAnalysis =
      bookRef.current?.chapters.find((c) => c.id === activeChapter.id) ?? activeChapter;
    const chapterText = chapterPlainTextForSync(chapterForAnalysis.content).trim();
    if (!chapterText) {
      setStatus("Add manuscript text before extracting timeline.");
      setTimelineExtractBusy(false);
      return;
    }

    const maxChars = 120_000;
    const trimmedText =
      chapterText.length > maxChars
        ? `${chapterText.slice(0, maxChars)}\n\n[…truncated for analysis…]`
        : chapterText;

    try {
      const existingBeats = getTimelineRoster(bookRef.current ?? book);
      const timelineRemovals = getTimelineRemovals(bookRef.current ?? book);
      const payload: Record<string, unknown> = {
        chapterText: trimmedText,
        chapterId: activeChapter.id,
        chapterTitle: activeChapter.title,
        existingBeats,
        timelineRemovals,
        provider: analysisProvider,
        model: analysisModel,
        masterPrompt: getMasterPrompt(),
      };
      const api = window.desktopV2Api;
      if (typeof api.timelineExtract !== "function") {
        throw new Error("Timeline extract is unavailable. Rebuild the Electron app (preload).");
      }
      const result = await api.timelineExtract(payload);
      updateBook((current) => {
        const merged = mergeExtractedBeatsIntoRoster({
          roster: getTimelineRoster(current),
          extracted: result.beats,
          chapterId: activeChapter.id,
          removals: getTimelineRemovals(current),
        });
        return applyTimelineRoster(current, merged);
      });
      setStatus(
        result.beats.length === 0
          ? "No major timeline beats found in this chapter."
          : `Extracted ${result.beats.length} major beat${result.beats.length === 1 ? "" : "s"}`
      );
    } catch (err) {
      setStatus(userFacingError(err, "generic"));
    } finally {
      setTimelineExtractBusy(false);
    }
  }

  async function runWholeBookTimelineExtraction(): Promise<void> {
    if (!book || wholeBookTimelineBusy) return;
    flushManuscriptDraft();
    const current = bookRef.current ?? book;
    const manuscriptChapters = [...current.chapters]
      .sort((a, b) => a.order - b.order)
      .map((chapter) => ({
        chapterId: chapter.id,
        title: chapter.title,
        order: chapter.order,
        text: chapterPlainTextForSync(chapter.content).trim(),
      }))
      .filter((chapter) => chapter.text.length > 0);
    if (manuscriptChapters.length === 0) {
      setStatus("Add manuscript text before analyzing whole-book timeline.");
      return;
    }
    if (
      !window.confirm(
        "Analyze the full manuscript to build or refresh the major-beat timeline and timing issues? This is normally run once after the book has substantial content."
      )
    ) {
      return;
    }

    const totalBudget = 800_000;
    const perChapterBudget = Math.max(3_000, Math.floor(totalBudget / manuscriptChapters.length));
    const boundedChapters = manuscriptChapters.map((chapter) => ({
      ...chapter,
      text:
        chapter.text.length > perChapterBudget
          ? `${chapter.text.slice(0, perChapterBudget)}\n\n[…chapter truncated for whole-book analysis…]`
          : chapter.text,
    }));

    setWholeBookTimelineBusy(true);
    setStatus(`Analyzing ${boundedChapters.length} chapters for major timeline beats…`);
    try {
      const payload: Record<string, unknown> = {
        mode: "book",
        chapterText: "Whole-book timeline analysis",
        chapterId: "__whole-book__",
        chapterTitle: current.title,
        allChaptersInOrder: boundedChapters,
        existingBeats: getTimelineRoster(current),
        timelineRemovals: getTimelineRemovals(current),
        provider: analysisProvider,
        model: analysisModel,
        masterPrompt: getMasterPrompt(),
      };
      const api = window.desktopV2Api;
      if (typeof api.timelineExtract !== "function") {
        throw new Error("Timeline extract is unavailable. Rebuild the Electron app (preload).");
      }
      const result = await api.timelineExtract(payload);
      updateBook((latest) => {
        const merged = mergeHolisticBeatsIntoRoster({
          roster: getTimelineRoster(latest),
          extracted: result.beats,
          validChapterIds: latest.chapters.map((chapter) => chapter.id),
          removals: getTimelineRemovals(latest),
        });
        const issues = mergeExtractedIssuesIntoRoster({
          issues: getTimelineIssues(latest),
          extracted: result.issues ?? [],
          beats: merged,
          replace: true,
        });
        return applyTimelineRoster(latest, merged, issues);
      });
      const first = result.beats[0];
      if (first) {
        const matched = getTimelineRoster(bookRef.current ?? current).find(
          (beat) => beat.summary === first.summary
        );
        if (matched) setSelectedTimelineBeatId(matched.id);
      }
      const issueCount = result.issues?.length ?? 0;
      setStatus(
        `Whole-book timeline analysis complete: ${result.beats.length} beat${
          result.beats.length === 1 ? "" : "s"
        }${issueCount ? `, ${issueCount} timing issue${issueCount === 1 ? "" : "s"}` : ""}.`
      );
    } catch (error) {
      setStatus(userFacingError(error, "generic"));
    } finally {
      setWholeBookTimelineBusy(false);
    }
  }

  function getThemeRoster(current: Book = bookRef.current ?? book!): BookTheme[] {
    return getThemesFromBookMetadata((current.metadata ?? {}) as Record<string, unknown>);
  }

  function getMotifRoster(current: Book = bookRef.current ?? book!): BookMotif[] {
    return getMotifsFromBookMetadata((current.metadata ?? {}) as Record<string, unknown>);
  }

  function getSymbolRoster(current: Book = bookRef.current ?? book!): BookSymbol[] {
    return getSymbolsFromBookMetadata((current.metadata ?? {}) as Record<string, unknown>);
  }

  function getThemeRemovals(
    current: Book = bookRef.current ?? book!
  ): ReturnType<typeof getThemeRemovalsFromMetadata> {
    return getThemeRemovalsFromMetadata((current.metadata ?? {}) as Record<string, unknown>);
  }

  function getMotifRemovals(
    current: Book = bookRef.current ?? book!
  ): ReturnType<typeof getMotifRemovalsFromMetadata> {
    return getMotifRemovalsFromMetadata((current.metadata ?? {}) as Record<string, unknown>);
  }

  function getSymbolRemovals(
    current: Book = bookRef.current ?? book!
  ): ReturnType<typeof getSymbolRemovalsFromMetadata> {
    return getSymbolRemovalsFromMetadata((current.metadata ?? {}) as Record<string, unknown>);
  }

  function applyThemesMotifsRoster(
    current: Book,
    themes: BookTheme[],
    motifs: BookMotif[],
    symbols: BookSymbol[]
  ): Book {
    const sortedThemes = sortThemesMajorFirst(themes);
    const sortedMotifs = sortByName(motifs);
    const sortedSymbols = sortByName(symbols);
    const meta = withThemesMotifsInMetadata(
      (current.metadata ?? {}) as Record<string, unknown>,
      sortedThemes,
      sortedMotifs,
      sortedSymbols
    ) as Book["metadata"];
    const chapters = current.chapters.map((chapter) => ({
      ...chapter,
      tabData: {
        ...chapter.tabData,
        themesMotifs: chapterThemesMotifsTabText(
          sortedThemes,
          sortedMotifs,
          sortedSymbols,
          chapter.id
        ),
      },
    }));
    return { ...current, metadata: meta, chapters };
  }

  function updateThemeInRoster(id: string, patch: Partial<BookTheme>): void {
    updateBook((current) => {
      const themes = getThemeRoster(current).map((theme) => {
        if (theme.id !== id) return theme;
        const merged = normalizeBookTheme({
          ...theme,
          ...patch,
          id: theme.id,
          updatedAt: nowIso(),
        });
        return merged ?? { ...theme, ...patch, updatedAt: nowIso() };
      });
      return applyThemesMotifsRoster(
        current,
        themes,
        getMotifRoster(current),
        getSymbolRoster(current)
      );
    });
  }

  function updateMotifInRoster(id: string, patch: Partial<BookMotif>): void {
    updateBook((current) => {
      const motifs = getMotifRoster(current).map((motif) => {
        if (motif.id !== id) return motif;
        const merged = normalizeBookMotif({
          ...motif,
          ...patch,
          id: motif.id,
          updatedAt: nowIso(),
        });
        return merged ?? { ...motif, ...patch, updatedAt: nowIso() };
      });
      return applyThemesMotifsRoster(
        current,
        getThemeRoster(current),
        motifs,
        getSymbolRoster(current)
      );
    });
  }

  function updateSymbolInRoster(id: string, patch: Partial<BookSymbol>): void {
    updateBook((current) => {
      const symbols = getSymbolRoster(current).map((symbol) => {
        if (symbol.id !== id) return symbol;
        const merged = normalizeBookSymbol({
          ...symbol,
          ...patch,
          id: symbol.id,
          updatedAt: nowIso(),
        });
        return merged ?? { ...symbol, ...patch, updatedAt: nowIso() };
      });
      return applyThemesMotifsRoster(
        current,
        getThemeRoster(current),
        getMotifRoster(current),
        symbols
      );
    });
  }

  function requestThemeRemoval(theme: BookTheme): void {
    setPendingThemeRemoval({ kind: "theme", theme });
  }

  function requestMotifRemoval(motif: BookMotif): void {
    setPendingThemeRemoval({ kind: "motif", motif });
  }

  function requestSymbolRemoval(symbol: BookSymbol): void {
    setPendingThemeRemoval({ kind: "symbol", symbol });
  }

  function removeThemeEntityFromRoster(reason: string): void {
    const pending = pendingThemeRemoval;
    if (!pending) return;
    const trimmedReason = reason.trim();
    if (trimmedReason.length < 3) return;

    updateBook((current) => {
      if (pending.kind === "theme" && pending.theme) {
        const themes = getThemeRoster(current).filter((t) => t.id !== pending.theme!.id);
        const removals = [
          createThemeRemovalRecord(pending.theme, trimmedReason),
          ...getThemeRemovals(current),
        ];
        let next = applyThemesMotifsRoster(
          current,
          themes,
          getMotifRoster(current),
          getSymbolRoster(current)
        );
        next = {
          ...next,
          metadata: withThemeRemovalsInMetadata(
            (next.metadata ?? {}) as Record<string, unknown>,
            removals
          ) as Book["metadata"],
        };
        return next;
      }
      if (pending.kind === "motif" && pending.motif) {
        const motifs = getMotifRoster(current).filter((m) => m.id !== pending.motif!.id);
        const removals = [
          createMotifRemovalRecord(pending.motif, trimmedReason),
          ...getMotifRemovals(current),
        ];
        let next = applyThemesMotifsRoster(
          current,
          getThemeRoster(current),
          motifs,
          getSymbolRoster(current)
        );
        next = {
          ...next,
          metadata: withMotifRemovalsInMetadata(
            (next.metadata ?? {}) as Record<string, unknown>,
            removals
          ) as Book["metadata"],
        };
        return next;
      }
      if (pending.kind === "symbol" && pending.symbol) {
        const symbols = getSymbolRoster(current).filter((s) => s.id !== pending.symbol!.id);
        const removals = [
          createSymbolRemovalRecord(pending.symbol, trimmedReason),
          ...getSymbolRemovals(current),
        ];
        let next = applyThemesMotifsRoster(
          current,
          getThemeRoster(current),
          getMotifRoster(current),
          symbols
        );
        next = {
          ...next,
          metadata: withSymbolRemovalsInMetadata(
            (next.metadata ?? {}) as Record<string, unknown>,
            removals
          ) as Book["metadata"],
        };
        return next;
      }
      return current;
    });

    if (
      selectedThemeRef &&
      pending.kind === selectedThemeRef.kind &&
      ((pending.theme && pending.theme.id === selectedThemeRef.id) ||
        (pending.motif && pending.motif.id === selectedThemeRef.id) ||
        (pending.symbol && pending.symbol.id === selectedThemeRef.id))
    ) {
      setSelectedThemeRef(null);
    }
    setPendingThemeRemoval(null);
    setStatus(`Removed ${pending.kind} and saved reason for future extract`);
  }

  function addThemeEntityToRoster(kind: ThemeKind, chapterId?: string): void {
    const appearanceChapterId = chapterId ?? activeChapterId;
    const now = nowIso();
    if (kind === "theme") {
      const id = generateThemeId();
      const theme: BookTheme = {
        id,
        name: "New theme",
        type: "minor",
        description: "",
        evolutionNotes: "",
        firstAppearanceChapterId: appearanceChapterId || undefined,
        appearances: appearanceChapterId ? [{ chapterId: appearanceChapterId }] : [],
        updatedAt: now,
      };
      updateBook((current) =>
        applyThemesMotifsRoster(
          current,
          [...getThemeRoster(current), theme],
          getMotifRoster(current),
          getSymbolRoster(current)
        )
      );
      setSelectedThemeRef({ kind: "theme", id });
      setStatus("Added theme");
      return;
    }
    if (kind === "motif") {
      const id = generateMotifId();
      const motif: BookMotif = {
        id,
        name: "New motif",
        description: "",
        firstAppearanceChapterId: appearanceChapterId || undefined,
        appearances: appearanceChapterId ? [{ chapterId: appearanceChapterId }] : [],
        updatedAt: now,
      };
      updateBook((current) =>
        applyThemesMotifsRoster(
          current,
          getThemeRoster(current),
          [...getMotifRoster(current), motif],
          getSymbolRoster(current)
        )
      );
      setSelectedThemeRef({ kind: "motif", id });
      setStatus("Added motif");
      return;
    }
    const id = generateSymbolId();
    const symbol: BookSymbol = {
      id,
      name: "New symbol",
      meaning: "",
      firstAppearanceChapterId: appearanceChapterId || undefined,
      appearances: appearanceChapterId ? [{ chapterId: appearanceChapterId }] : [],
      updatedAt: now,
    };
    updateBook((current) =>
      applyThemesMotifsRoster(
        current,
        getThemeRoster(current),
        getMotifRoster(current),
        [...getSymbolRoster(current), symbol]
      )
    );
    setSelectedThemeRef({ kind: "symbol", id });
    setStatus("Added symbol");
  }

  function openThemeEntityInGuide(id: string, kind: ThemeKind): void {
    setSelectedThemeRef({ kind, id });
    setWorkbenchView("themes");
  }

  async function runThemeExtraction(): Promise<void> {
    if (!activeChapter || !book) return;
    flushManuscriptDraft();
    setThemeExtractBusy(true);
    setActiveTab("themesMotifs");
    setWorkbenchView("book");
    setStatus("Extracting themes & motifs…");

    const chapterForAnalysis =
      bookRef.current?.chapters.find((c) => c.id === activeChapter.id) ?? activeChapter;
    const chapterText = chapterPlainTextForSync(chapterForAnalysis.content).trim();
    if (!chapterText) {
      setStatus("Add manuscript text before extracting themes.");
      setThemeExtractBusy(false);
      return;
    }

    const maxChars = 120_000;
    const trimmedText =
      chapterText.length > maxChars
        ? `${chapterText.slice(0, maxChars)}\n\n[…truncated for analysis…]`
        : chapterText;

    try {
      const current = bookRef.current ?? book;
      const payload: Record<string, unknown> = {
        chapterText: trimmedText,
        chapterId: activeChapter.id,
        chapterTitle: activeChapter.title,
        existingThemes: getThemeRoster(current),
        existingMotifs: getMotifRoster(current),
        existingSymbols: getSymbolRoster(current),
        themeRemovals: getThemeRemovals(current),
        motifRemovals: getMotifRemovals(current),
        symbolRemovals: getSymbolRemovals(current),
        provider: analysisProvider,
        model: analysisModel,
        masterPrompt: getMasterPrompt(),
      };
      const api = window.desktopV2Api;
      if (typeof api.themeExtract !== "function") {
        throw new Error("Theme extract is unavailable. Rebuild the Electron app (preload).");
      }
      const result = await api.themeExtract(payload);
      updateBook((latest) => {
        const themes = mergeExtractedThemesIntoRoster({
          roster: getThemeRoster(latest),
          extracted: result.themes,
          chapterId: activeChapter.id,
          removals: getThemeRemovals(latest),
        });
        const motifs = mergeExtractedMotifsIntoRoster({
          roster: getMotifRoster(latest),
          extracted: result.motifs,
          chapterId: activeChapter.id,
          removals: getMotifRemovals(latest),
        });
        const symbols = mergeExtractedSymbolsIntoRoster({
          roster: getSymbolRoster(latest),
          extracted: result.symbols,
          chapterId: activeChapter.id,
          removals: getSymbolRemovals(latest),
        });
        return applyThemesMotifsRoster(latest, themes, motifs, symbols);
      });
      const total = result.themes.length + result.motifs.length + result.symbols.length;
      setStatus(
        total === 0
          ? "No themes, motifs, or symbols found in this chapter."
          : `Extracted ${result.themes.length} theme${result.themes.length === 1 ? "" : "s"}, ${result.motifs.length} motif${result.motifs.length === 1 ? "" : "s"}, ${result.symbols.length} symbol${result.symbols.length === 1 ? "" : "s"}`
      );
    } catch (err) {
      setStatus(userFacingError(err, "generic"));
    } finally {
      setThemeExtractBusy(false);
    }
  }

  async function runWholeBookThemeExtraction(): Promise<void> {
    if (!book || wholeBookThemeBusy) return;
    flushManuscriptDraft();
    const current = bookRef.current ?? book;
    const manuscriptChapters = [...current.chapters]
      .sort((a, b) => a.order - b.order)
      .map((chapter) => ({
        chapterId: chapter.id,
        title: chapter.title,
        order: chapter.order,
        text: chapterPlainTextForSync(chapter.content).trim(),
      }))
      .filter((chapter) => chapter.text.length > 0);
    if (manuscriptChapters.length === 0) {
      setStatus("Add manuscript text before analyzing whole-book themes.");
      return;
    }
    if (
      !window.confirm(
        "Analyze the full manuscript to build or refresh themes, motifs, and symbols? This is normally run once after the book has substantial content."
      )
    ) {
      return;
    }

    const totalBudget = 800_000;
    const perChapterBudget = Math.max(3_000, Math.floor(totalBudget / manuscriptChapters.length));
    const boundedChapters = manuscriptChapters.map((chapter) => ({
      ...chapter,
      text:
        chapter.text.length > perChapterBudget
          ? `${chapter.text.slice(0, perChapterBudget)}\n\n[…chapter truncated for whole-book analysis…]`
          : chapter.text,
    }));

    setWholeBookThemeBusy(true);
    setStatus(`Analyzing ${boundedChapters.length} chapters for themes & motifs…`);
    try {
      const payload: Record<string, unknown> = {
        mode: "book",
        chapterText: "Whole-book theme analysis",
        chapterId: "__whole-book__",
        chapterTitle: current.title,
        allChaptersInOrder: boundedChapters,
        existingThemes: getThemeRoster(current),
        existingMotifs: getMotifRoster(current),
        existingSymbols: getSymbolRoster(current),
        themeRemovals: getThemeRemovals(current),
        motifRemovals: getMotifRemovals(current),
        symbolRemovals: getSymbolRemovals(current),
        provider: analysisProvider,
        model: analysisModel,
        masterPrompt: getMasterPrompt(),
      };
      const api = window.desktopV2Api;
      if (typeof api.themeExtract !== "function") {
        throw new Error("Theme extract is unavailable. Rebuild the Electron app (preload).");
      }
      const result = await api.themeExtract(payload);
      const validChapterIds = current.chapters.map((chapter) => chapter.id);
      updateBook((latest) => {
        const themes = mergeHolisticThemesIntoRoster({
          roster: getThemeRoster(latest),
          extracted: result.themes,
          validChapterIds,
          removals: getThemeRemovals(latest),
        });
        const motifs = mergeHolisticMotifsIntoRoster({
          roster: getMotifRoster(latest),
          extracted: result.motifs,
          validChapterIds,
          removals: getMotifRemovals(latest),
        });
        const symbols = mergeHolisticSymbolsIntoRoster({
          roster: getSymbolRoster(latest),
          extracted: result.symbols,
          validChapterIds,
          removals: getSymbolRemovals(latest),
        });
        return applyThemesMotifsRoster(latest, themes, motifs, symbols);
      });
      const first =
        result.themes[0] ?? result.motifs[0] ?? result.symbols[0] ?? null;
      if (first && result.themes[0]) {
        const matched = getThemeRoster(bookRef.current ?? current).find(
          (t) => t.name === result.themes[0]!.name
        );
        if (matched) setSelectedThemeRef({ kind: "theme", id: matched.id });
      }
      setStatus(
        `Whole-book theme analysis complete: ${result.themes.length} theme${
          result.themes.length === 1 ? "" : "s"
        }, ${result.motifs.length} motif${result.motifs.length === 1 ? "" : "s"}, ${result.symbols.length} symbol${
          result.symbols.length === 1 ? "" : "s"
        }.`
      );
    } catch (error) {
      setStatus(userFacingError(error, "generic"));
    } finally {
      setWholeBookThemeBusy(false);
    }
  }

  function clearChapterStoryCraftComments(): void {
    if (!activeChapter) return;
    const aiComments = storyCraftComments(activeChapter.comments);
    if (!aiComments.length) return;

    const editor = getManuscriptEditor();
    if (editor) {
      for (const comment of aiComments) {
        removeCommentMarkFromEditorByCommentId(editor, comment.id);
      }
    }

    updateChapter((chapter) => ({
      ...chapter,
      comments: withoutStoryCraftComments(chapter.comments),
    }));
  }

  function addChapterComment(
    text: string,
    type = "note",
    opts?: {
      anchor?: { from: number; to: number; excerpt?: string };
      blockId?: string | null;
      parentCommentId?: string | null;
    }
  ): void {
    if (!activeChapter) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    const id = `comment-${Date.now()}`;
    const editor = getManuscriptEditor();
    const liveCapture = !opts?.anchor ? captureCommentSelection(editor) : null;
    const anchor = opts?.anchor ?? liveCapture?.anchor;
    let blockId: string | null | undefined = opts?.blockId;
    if (blockId === undefined) {
      blockId = liveCapture?.blockId ?? null;
      if (!blockId && anchor && editor) {
        blockId = blockIdAtPos(editor.state.doc, anchor.from);
      }
    }
    const excerpt = anchor?.excerpt?.trim() || undefined;
    const normalizedType = normalizeDesktopCommentType(type);
    const authorUserId = cloudAuthDesktopV2.getUserId();
    const parentCommentId = opts?.parentCommentId
      ? flattenReplyParentId(opts.parentCommentId, activeChapter.comments)
      : null;
    const parent = parentCommentId
      ? activeChapter.comments.find((c) => c.id === parentCommentId)
      : null;
    const inheritedBlockId = parentCommentId ? parent?.blockId ?? blockId : blockId;
    updateChapter((chapter) => ({
      ...chapter,
      comments: [
        ...chapter.comments,
        {
          id,
          text: trimmed,
          type: parentCommentId ? "note" : normalizedType,
          resolved: false,
          createdBy: "user",
          authorUserId: authorUserId ?? undefined,
          targetText: parentCommentId ? undefined : excerpt,
          isStale: false,
          createdAt: nowIso(),
          parentCommentId,
          blockId: inheritedBlockId ?? null,
          anchor: parentCommentId ? undefined : anchor,
        },
      ],
    }));
    if (!parentCommentId) {
      const editorForMark = getManuscriptEditor();
      if (editorForMark && anchor && anchor.to > anchor.from) {
        applyCommentMarkToRange(editorForMark, id, normalizedType, anchor.from, anchor.to);
      }
    }
    setSelectedChapterCommentId(parentCommentId ?? id);
    setAssistantPanelTab("comments");
    setShowAssistantPanel(true);
    setStatus(
      parentCommentId
        ? "Reply added"
        : anchor
          ? "Comment added at current manuscript selection"
          : "Comment added"
    );
  }

  function updateChapterCommentText(commentId: string, nextText: string): void {
    if (!activeChapter) return;
    const trimmed = nextText.trim();
    if (!trimmed) return;
    updateChapter((chapter) => ({
      ...chapter,
      comments: chapter.comments.map((c) => (c.id === commentId ? { ...c, text: trimmed } : c)),
    }));
    setStatus("Comment updated");
  }

  function toggleChapterCommentResolved(commentId: string): void {
    if (!activeChapter) return;
    const existing = activeChapter.comments.find((c) => c.id === commentId);
    if (!existing || existing.parentCommentId) return; // resolve roots only
    const willResolve = !existing.resolved;
    updateChapter((chapter) => ({
      ...chapter,
      comments: chapter.comments.map((c) => {
        if (c.id === commentId) return { ...c, resolved: willResolve };
        // Mirror resolve onto replies for display consistency
        if (willResolve && c.parentCommentId === commentId) return { ...c, resolved: true };
        if (!willResolve && c.parentCommentId === commentId) return { ...c, resolved: false };
        return c;
      }),
    }));
    const editor = getManuscriptEditor();
    if (editor && willResolve) {
      removeCommentMarkFromEditorByCommentId(editor, commentId);
    }
    if (willResolve && selectedChapterCommentId === commentId) {
      setSelectedChapterCommentId(null);
    }
    setStatus("Comment status updated");
  }

  function deleteChapterComment(commentId: string): void {
    if (!activeChapter) return;
    const target = activeChapter.comments.find((c) => c.id === commentId);
    const editor = getManuscriptEditor();
    if (editor) {
      if (target && !target.parentCommentId) {
        removeCommentMarkFromEditorByCommentId(editor, commentId);
      }
    }
    if (selectedChapterCommentId === commentId) {
      setSelectedChapterCommentId(null);
    }
    updateChapter((chapter) => ({
      ...chapter,
      comments: removeCommentOrThread(chapter.comments, commentId),
    }));
    setStatus("Comment deleted");
  }

  function jumpToChapterComment(commentId: string): void {
    if (!activeChapter) return;
    const comment = activeChapter.comments.find((item) => item.id === commentId);
    if (!comment) return;
    const rootId =
      flattenReplyParentId(comment.parentCommentId, activeChapter.comments) ??
      (comment.parentCommentId ? comment.parentCommentId : comment.id);
    const jumpComment =
      activeChapter.comments.find((c) => c.id === rootId && !c.parentCommentId) ?? comment;
    setSelectedChapterCommentId(rootId);
    setWorkbenchView("book");
    setShowAssistantPanel(true);
    setAssistantPanelTab("comments");
    setActiveTab("manuscript");
    window.setTimeout(() => {
      const editor = getManuscriptEditor();
      if (!editor) {
        setStatus("Unable to jump: manuscript editor not ready");
        return;
      }
      const selection = resolveCommentSelection(editor, jumpComment);
      if (!selection) {
        setStatus("Could not find that passage in the manuscript. The text may have changed.");
        return;
      }
      editor.chain().focus().setTextSelection(selection).run();
      scrollAndFlashChapterComment(editor, selection.from, selection.to);
      setStatus("Jumped to comment in manuscript");
    }, 100);
  }

  function pushChatMessage(message: ChatMessage, threadId: string): void {
    updateBook((current) => {
      const assistant = getAssistantState(current);
      const next = appendChatMessage(assistant, threadId, message);
      return withAssistantState(current, mergeChatIntoAssistant(assistant, next));
    });
  }

  function selectChatThread(threadId: string): void {
    updateBook((current) => {
      const assistant = getAssistantState(current);
      if (!assistant.chatThreads.some((t) => t.id === threadId)) return current;
      return withAssistantState(current, mergeChatIntoAssistant(assistant, { ...assistant, activeChatThreadId: threadId }));
    });
    chatStickToBottomRef.current = false;
  }

  function startNewChatThread(): void {
    const thread = createEmptyChatThread();
    updateBook((current) => {
      const assistant = getAssistantState(current);
      const next = addChatThread(assistant, thread);
      return withAssistantState(current, mergeChatIntoAssistant(assistant, next));
    });
    setChatDraft("");
    setUploadedChatImage(null);
    chatStickToBottomRef.current = true;
  }

  function archiveActiveChat(): void {
    const threadId = assistantState?.activeChatThreadId;
    if (!threadId) return;
    updateBook((current) => {
      const assistant = getAssistantState(current);
      const next = archiveChatThread(assistant, threadId);
      return withAssistantState(current, mergeChatIntoAssistant(assistant, next));
    });
    setChatDraft("");
    setUploadedChatImage(null);
    chatStickToBottomRef.current = false;
    setStatus("Chat archived");
  }

  function restoreArchivedChat(threadId: string): void {
    updateBook((current) => {
      const assistant = getAssistantState(current);
      const next = restoreChatThread(assistant, threadId);
      return withAssistantState(current, mergeChatIntoAssistant(assistant, next));
    });
    chatStickToBottomRef.current = false;
    setStatus("Chat restored");
  }

  function clearActiveChat(): void {
    const threadId = assistantState?.activeChatThreadId;
    if (!threadId) return;
    updateBook((current) => {
      const assistant = getAssistantState(current);
      const next = clearChatThread(assistant, threadId);
      return withAssistantState(current, mergeChatIntoAssistant(assistant, next));
    });
    setStatus("Chat cleared");
  }

  function buildChatBookSnapshot(): ChatBookSnapshot | null {
    if (!book) return null;
    const chapters: ChatBookChapter[] = book.chapters.map((ch, index) => ({
      id: ch.id,
      title: ch.title,
      order: ch.order ?? index + 1,
      content: chapterPlainTextForSync(ch.content),
    }));
    return {
      title: book.title,
      chapters,
      researchAssets: getResearchState((book.metadata ?? {}) as Record<string, unknown>).assets.map((asset) => ({
        id: asset.id,
        kind: asset.kind,
        title: asset.title,
        extractedText: asset.extractedText,
        notes: asset.notes,
        tags: asset.tags,
        sourceUrl: asset.sourceUrl,
      })),
    };
  }

  function buildChatContext(
    prefetchedChapters: ChatBookChapter[],
    contextChapter = activeChapter,
    versionContextBlock?: string | null
  ): string {
    if (!book || !contextChapter) return "";
    const ordered = [...book.chapters].sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
    const activeIndex = ordered.findIndex((c) => c.id === contextChapter.id);
    const parts = [
      `Book title: ${book.title}`,
      `Total chapters: ${ordered.length}`,
      `Chapter catalog:\n${buildChapterCatalog(
        ordered.map((ch, i) => ({
          id: ch.id,
          title: ch.title,
          order: ch.order ?? i + 1,
          content: "",
        }))
      )}`,
    ];

    if (versionContextBlock) {
      parts.push(versionContextBlock);
    }

    if (prefetchedChapters.length > 0) {
      const nums = prefetchedChapters.map((ch) => {
        const idx = ordered.findIndex((c) => c.id === ch.id);
        return idx >= 0 ? idx + 1 : ch.order;
      });
      parts.push(
        `Chapters pre-loaded for this request (full text below): ${nums.join(", ")}. Use get_chapters if you need additional chapters. Use move_chapter to reorder chapters when asked.`
      );
    } else {
      parts.push(
        "Only the current chapter manuscript is loaded below. Call get_chapters with chapter numbers, ranges, or titles when the user asks about other chapters. Use move_chapter to reorder chapters when asked."
      );
    }

    parts.push(
      `## Current chapter (${activeIndex >= 0 ? activeIndex + 1 : contextChapter.order}): "${contextChapter.title}"`,
      `Word count: ${countWords(contextChapter.content)}`,
      contextChapter.content
    );

    const snapshotChapters: ChatBookChapter[] = ordered.map((ch, i) => ({
      id: ch.id,
      title: ch.title,
      order: ch.order ?? i + 1,
      content: chapterPlainTextForSync(ch.content),
    }));
    for (const ch of prefetchedChapters) {
      if (ch.id === contextChapter.id) continue;
      parts.push(formatChaptersForAssistantContext([ch], snapshotChapters));
    }

    return parts.join("\n\n");
  }

  async function resolveAttachedVersionContextBlock(): Promise<string | null> {
    if (!attachedChatVersion || !book) return null;
    const meta = book.metadata as Record<string, unknown>;
    const currentId = currentVersionIdFromMetadata(meta);
    const isOpen = !currentId || currentId === attachedChatVersion.id;

    if (isOpen) {
      const ordered = [...book.chapters].sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
      const excerptsByOrder: Record<number, string> = {};
      for (const ch of ordered.slice(0, 12)) {
        excerptsByOrder[ch.order] = excerptForChat(chapterPlainTextForSync(ch.content));
      }
      return formatAttachedVersionContext({
        version: attachedChatVersion,
        isOpenManuscript: true,
        chapters: ordered.map((ch) => ({
          title: ch.title,
          order: ch.order,
          kind: ch.kind,
          chapterNumber: ch.chapterNumber,
          wordCount: countWords(ch.content),
        })),
        excerptsByOrder,
      });
    }

    try {
      if (!cloudAuthDesktopV2.isSignedIn()) {
        return formatAttachedVersionContext({
          version: attachedChatVersion,
          isOpenManuscript: false,
          chapters: [],
        });
      }
      const bookId = typeof book.id === "string" ? book.id.trim() : "";
      if (!bookId) {
        return formatAttachedVersionContext({
          version: attachedChatVersion,
          isOpenManuscript: false,
          chapters: [],
        });
      }
      const catalog = await cloudAuthDesktopV2.getBookVersionCatalog(bookId, attachedChatVersion.id);
      return formatAttachedVersionContext({
        version: {
          id: catalog.id,
          name: catalog.name || attachedChatVersion.name,
          status: catalog.status || attachedChatVersion.status,
        },
        isOpenManuscript: false,
        chapters: catalog.chapters.map((ch) => ({
          title: ch.title,
          order: ch.order,
          kind: ch.kind,
          chapterNumber: ch.chapterNumber,
          wordCount: ch.wordCount,
        })),
      });
    } catch {
      return formatAttachedVersionContext({
        version: attachedChatVersion,
        isOpenManuscript: false,
        chapters: [],
      });
    }
  }

  function getChatHistoryTurns(messages: ChatMessage[]): ChatTurn[] {
    return messages.slice(-20).map((message) => ({ role: message.role, content: message.content }));
  }

  function beginPanelResize(side: "left" | "right", clientX: number): void {
    setResizingPanel(side);
    setResizingOriginX(clientX);
    setResizingStartWidth(side === "left" ? leftPanelWidth : rightPanelWidth);
  }

  function resetPanelWidth(side: "left" | "right"): void {
    if (side === "left") setLeftPanelWidth(DEFAULT_LEFT_PANEL_WIDTH);
    else setRightPanelWidth(DEFAULT_RIGHT_PANEL_WIDTH);
  }

  function handleChatImageUpload(file: File | null): void {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setStatus("Only image uploads are supported in chat.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = typeof reader.result === "string" ? reader.result : "";
      if (!dataUrl) return;
      setUploadedChatImage({ name: file.name, mimeType: file.type, dataUrl });
      setStatus(`Attached image: ${file.name}`);
    };
    reader.readAsDataURL(file);
  }

  function openVariationCompare(variationId: string): void {
    setActiveCompareVariationId(variationId);
    setActiveTab("manuscript");
  }

  function startVariationChat(): void {
    setAssistantPanelTab("chat");
    setChatDraft(CHAPTER_VARIATION_START_PROMPT);
    window.requestAnimationFrame(() => chatInputRef.current?.focus());
  }

  async function generateVariationFromChat(): Promise<void> {
    await requestChapterVariation(CHAPTER_VARIATION_GENERATE_PROMPT, { source: "chat" });
  }

  async function generateVariationFromStoryCraftChecklist(): Promise<void> {
    if (!activeChapter || !activeStoryCraft || variationBusy || storyCraftBusy || chatBusy) return;
    const instructions = buildStoryCraftChecklistVariationInstructions(activeStoryCraft, {
      onlyIncomplete: true,
    });
    if (!instructions) {
      setStatus(
        "No open Story Craft checklist items — run analysis or uncheck completed items to include them."
      );
      return;
    }
    await requestChapterVariation(instructions, { source: "storyCraft", includeFullManuscript: true });
  }

  async function cancelGenerationRequest(requestId: string): Promise<void> {
    const cancel = window.desktopV2Api.cancelGeneration;
    if (typeof cancel !== "function") return;
    await cancel(requestId).catch(() => ({ canceled: false }));
  }

  function stopChatGeneration(): void {
    const requestId = activeChatRequestRef.current;
    if (!requestId) return;
    activeChatRequestRef.current = null;
    setChatBusy(false);
    setStatus("Assistant response stopped");
    void cancelGenerationRequest(requestId);
  }

  function stopVariationGeneration(): void {
    const active = activeVariationRequestRef.current;
    if (!active) return;
    activeVariationRequestRef.current = null;
    setVariationBusy(false);
    setStatus("Variation generation stopped");
    void cancelGenerationRequest(active.requestId);
  }

  async function steerChapterVariation(direction: string): Promise<void> {
    const steer = direction.trim();
    const active = activeVariationRequestRef.current;
    if (!steer || !active) return;
    activeVariationRequestRef.current = null;
    await cancelGenerationRequest(active.requestId);
    setVariationBusy(false);
    await generateChapterVariationRequest(
      `${active.instructions}\n\nAdditional direction from the author:\n${steer}`,
      active.options,
      active.reason,
      true
    );
  }

  function requestChapterVariation(
    instructions?: string,
    options?: VariationGenerationOptions
  ): void {
    if (!activeChapter || variationBusy || chatBusy) return;
    const resolvedInstructions =
      instructions?.trim() ||
      chatDraft.trim() ||
      CHAPTER_VARIATION_GENERATE_PROMPT;
    setPendingVariationReason({
      kind: "generate",
      instructions: resolvedInstructions,
      options,
    });
  }

  async function generateChapterVariationRequest(
    instructions: string,
    options: VariationGenerationOptions | undefined,
    reason: string,
    replacingActive = false
  ): Promise<void> {
    if (!activeChapter || chatBusy || (variationBusy && !replacingActive)) return;
    flushManuscriptDraft();
    const contextChapter =
      bookRef.current?.chapters.find((chapter) => chapter.id === activeChapter.id) ?? activeChapter;
    const resolvedInstructions = instructions.trim() || CHAPTER_VARIATION_GENERATE_PROMPT;
    const contextTurns = getChatHistoryTurns(activeChats).slice(-10);
    const chapterComments = unresolvedCommentsForVariation(contextChapter.comments ?? []);
    const currentBook = bookRef.current;
    const allChaptersInOrder =
      options?.includeFullManuscript && currentBook && storyCraftUsesFullManuscript(analysisModel)
        ? buildAllChaptersForVariation(currentBook, contextChapter.id)
        : undefined;
    const variationPromptKind =
      options?.source === "storyCraft" ? "variationStoryCraft" : "variation";
    const requestId = generationRequestId("variation");

    setAssistantPanelTab("variations");
    activeVariationRequestRef.current = {
      requestId,
      instructions: resolvedInstructions,
      reason,
      options,
    };
    setVariationBusy(true);
    setStatus(replacingActive ? "Steering chapter variation…" : "Generating chapter variation…");

    try {
      const response = await window.desktopV2Api.createChapterVariation({
        requestId,
        chapterId: contextChapter.id,
        chapterTitle: contextChapter.title,
        chapterContent: chapterPlainTextForDiff(contextChapter.content),
        instructions: resolvedInstructions,
        provider: analysisProvider,
        model: analysisModel,
        masterPrompt: resolvePromptFor(variationPromptKind),
        contextTurns,
        chapterComments: chapterComments.length ? chapterComments : undefined,
        ...(allChaptersInOrder && allChaptersInOrder.length > 1 ? { allChaptersInOrder } : {}),
      });
      if (activeVariationRequestRef.current?.requestId !== requestId) return;
      applyChatBookMutations([response.mutation], {
        prompt: resolvedInstructions,
        model: response.model,
        reason,
        variationSource: options?.source ?? "manual",
      });
      setStatus("Chapter variation ready — review changes in the compare view");
    } catch (error) {
      if (
        activeVariationRequestRef.current?.requestId === requestId &&
        !isGenerationAbort(error)
      ) {
        setStatus(userFacingError(error, "generate"));
      }
    } finally {
      if (activeVariationRequestRef.current?.requestId === requestId) {
        activeVariationRequestRef.current = null;
        setVariationBusy(false);
      }
    }
  }

  async function sendChatMessage(overridePrompt?: string): Promise<void> {
    const draftPrompt = overridePrompt ?? chatDraft;
    if (!activeChapter || chatBusy || (!draftPrompt.trim() && !uploadedChatImage)) return;
    flushManuscriptDraft();
    const threadId = assistantState?.activeChatThreadId;
    if (!threadId) return;
    const requestId = generationRequestId("chat");

    const contextChapter =
      bookRef.current?.chapters.find((c) => c.id === activeChapter.id) ?? activeChapter;
    const prompt = draftPrompt.trim() || "Please analyze the attached image in chapter context.";
    const priorTurns = getChatHistoryTurns(activeChats);
    const attachNotes: string[] = [];
    if (uploadedChatImage) attachNotes.push(`(Attached image: ${uploadedChatImage.name})`);
    if (attachedChatVersion) {
      attachNotes.push(`(Attached version: ${displayVersionLabel(attachedChatVersion)})`);
    }
    const userMessage: ChatMessage = {
      id: `chat-${Date.now()}-user`,
      role: "user",
      content: attachNotes.length ? `${prompt}\n\n${attachNotes.join("\n")}` : prompt,
      scope: "chapter",
      chapterId: contextChapter.id,
      createdAt: nowIso(),
    };
    if (!overridePrompt) setChatDraft("");
    chatStickToBottomRef.current = true;
    pushChatMessage(userMessage, threadId);
    activeChatRequestRef.current = requestId;
    setChatBusy(true);
    setStatus("Assistant is drafting a response...");
    const bookSnapshot = buildChatBookSnapshot();
    const prefetched =
      bookSnapshot ? resolveChaptersFromRequest(prompt, bookSnapshot.chapters) : [];
    try {
      const versionBlock = await resolveAttachedVersionContextBlock();
      const plain = chapterPlainTextForSync(contextChapter.content);
      const codexBit = bookRef.current
        ? buildCodexContextSnippet(bookRef.current, { chapterText: plain, limit: 8 })
        : "";
      const storyCraftBit = formatStoryCraftForChat(
        assistantState?.storyCraftByChapter[contextChapter.id] ?? null
      );

      const craftAreasForSearch = STORY_CRAFT_AREA_IDS.filter((key) => {
        const row = assistantState?.storyCraftByChapter[contextChapter.id]?.assessment?.[key];
        return row && typeof row.score === "number" && row.score > 0 && row.score < 4;
      }) as StoryCraftAreaId[];
      const resourceHits = await searchWritingResourcesForChat({
        query: prompt,
        craftAreas: craftAreasForSearch.length ? craftAreasForSearch : undefined,
        limit: 5,
      }).catch(() => []);
      const resourcesBit = formatWritingResourcesForChat(resourceHits);
      if (activeChatRequestRef.current !== requestId) return;

      const response = await window.desktopV2Api.chatComplete({
        requestId,
        prompt,
        context: [
          buildChatContext(prefetched, contextChapter, versionBlock),
          codexBit,
          storyCraftBit,
          resourcesBit,
          formatResearchCatalogForChat(
            getResearchState((bookRef.current?.metadata ?? {}) as Record<string, unknown>).assets
          ),
          formatCitationLibraryForChat(
            getCitationState((bookRef.current?.metadata ?? {}) as Record<string, unknown>).items
          ),
        ]
          .filter(Boolean)
          .join("\n\n"),
        chapterTitle: contextChapter.title,
        scope: "chapter",
        provider: chatProvider,
        model: chatModel,
        history: priorTurns,
        bookSnapshot,
        activeChapterId: contextChapter.id,
        masterPrompt: resolvePromptFor("chat"),
        image: uploadedChatImage
          ? {
              mimeType: uploadedChatImage.mimeType,
              dataUrl: uploadedChatImage.dataUrl,
              name: uploadedChatImage.name,
            }
          : undefined,
      });
      if (activeChatRequestRef.current !== requestId) return;
      applyChatBookMutations(response.mutations, { prompt, model: response.model });
      pushChatMessage(
        {
          id: `chat-${Date.now()}-assistant`,
          role: "assistant",
          content: response.message || "I could not generate a response. Please try again.",
          scope: "chapter",
          chapterId: contextChapter.id,
          createdAt: nowIso(),
        },
        threadId
      );
      setStatus(`Assistant response ready (${response.provider} · ${response.model})`);
      setUploadedChatImage(null);
      setAttachedChatVersion(null);
    } catch (error) {
      if (activeChatRequestRef.current === requestId && !isGenerationAbort(error)) {
        console.error("[assistant] chatComplete failed", error);
        pushChatMessage(
          {
            id: `chat-${Date.now()}-assistant-error`,
            role: "assistant",
            content: userFacingError(error, "generate"),
            scope: "chapter",
            chapterId: contextChapter.id,
            createdAt: nowIso(),
          },
          threadId
        );
        setStatus("Assistant request failed");
      }
    } finally {
      if (activeChatRequestRef.current === requestId) {
        activeChatRequestRef.current = null;
        setChatBusy(false);
      }
    }
  }

  function handleMenuAction(action: string): void {
    const needsChapterContext =
      action.startsWith("chapter.") || action.startsWith("edit.") || action.startsWith("tools.");
    if (needsChapterContext && (!book || !activeChapter)) {
      return;
    }

    if (action === "file.newProject") setNewBookPickerOpen(true);
    if (action === "file.open" || action === "file.import") {
      if (isWebRuntime()) {
        openOpenFromCloudDialog();
      } else {
        void openProject();
      }
    }
    if (action === "file.openFromCloud") openOpenFromCloudDialog();
    if (action === "file.reloadFromDisk") {
      if (isWebRuntime()) {
        setStatus("Reload from disk is only available in the desktop app.");
        return;
      }
      void reloadProjectFromDisk();
    }
    if (action === "edit.undo") {
      if (activeTab === "manuscript" && undoManuscript()) return;
      if (document.queryCommandSupported("undo")) {
        document.execCommand("undo");
      }
      return;
    }
    if (action === "edit.redo") {
      if (activeTab === "manuscript" && redoManuscript()) return;
      if (document.queryCommandSupported("redo")) {
        document.execCommand("redo");
      }
      return;
    }
    if (action === "file.save") {
      if (!book) return;
      void saveProject(false);
    }
    if (action === "file.saveAs") {
      if (!book) return;
      // Web has no Save As dialog — sync/save to the cloud library path.
      void saveProject(isWebRuntime() ? false : true);
    }
    if (action === "file.exportDocx") {
      if (!book) return;
      window.desktopV2Api.exportDocx(book).then((result) => {
        if (!result.canceled) {
          setStatus(`DOCX exported to ${result.filePath}`);
          void trackDesktopActivation({
            event: "activation.export_done",
            bookId: book.id,
            metadata: { format: "docx" },
          });
        }
      });
    }
    if (action === "file.exportPdf") {
      if (!book) return;
      void (async () => {
        const authToken = cloudAuthDesktopV2.isSignedIn()
          ? await cloudAuthDesktopV2.getValidAccessToken().catch(() => undefined)
          : undefined;
        const result = await window.desktopV2Api.exportPdf({ book, authToken: authToken ?? undefined });
        if (result.upgrade) {
          setStatus(result.error || "PDF export requires an Author or Editor plan.");
          return;
        }
        if (result.error) {
          setStatus(result.error);
          return;
        }
        if (!result.canceled) {
          setStatus(`PDF exported to ${result.filePath}`);
          void trackDesktopActivation({
            event: "activation.export_done",
            bookId: book.id,
            metadata: { format: "pdf" },
          });
        }
      })();
    }
    if (action === "edit.find" || action === "edit.replace") {
      setWorkbenchView("book");
      setFindReplaceOpen(true);
      return;
    }
    if (action === "view.toggleChapters") {
      setWorkbenchView("book");
      setShowChaptersPanel((value) => !value);
    }
    if (action === "view.toggleAssistant") {
      setWorkbenchView("book");
      setShowAssistantPanel((value) => !value);
    }
    if (action === "view.focusMode") {
      setNarratorMode(false);
      setFocusMode((value) => !value);
    }
    if (action === "view.narratorMode") {
      setFocusMode(false);
      setNarratorMode((value) => !value);
      setWorkbenchView("book");
    }
    if (action === "view.zoomIn") setZoomPercent((value) => Math.min(140, value + 10));
    if (action === "view.zoomOut") setZoomPercent((value) => Math.max(80, value - 10));
    if (action === "view.resetLayout") {
      setWorkbenchView("book");
      setShowChaptersPanel(true);
      setShowAssistantPanel(true);
      setFocusMode(false);
      setNarratorMode(false);
      setZoomPercent(100);
      setLeftPanelWidth(DEFAULT_LEFT_PANEL_WIDTH);
      setRightPanelWidth(DEFAULT_RIGHT_PANEL_WIDTH);
      setStatus("Layout reset");
    }
    if (action === "chapter.new") {
      if (!book) return;
      const chapter = createChapter();
      updateBook((current) => ({
        ...current,
        chapters: withSpineOrderAndNumbers([...current.chapters, chapter]),
      }));
      setActiveChapterId(chapter.id);
      setStatus("New chapter added");
    }
    if (action === "chapter.duplicate") {
      if (!book || !activeChapter) return;
      const duplicate = createChapter(`${activeChapter.title} (copy)`, activeChapter.kind ?? "chapter");
      duplicate.content = activeChapter.content;
      duplicate.caption = activeChapter.caption ?? "";
      duplicate.tabData = { ...activeChapter.tabData };
      duplicate.metadata = { ...activeChapter.metadata, tags: [...activeChapter.metadata.tags] };
      updateBook((current) => ({
        ...current,
        chapters: withSpineOrderAndNumbers([...current.chapters, duplicate]),
      }));
      setActiveChapterId(duplicate.id);
      setStatus("Chapter duplicated");
    }
    if (action === "chapter.delete") {
      if (!book || !activeChapter) return;
      if (book.chapters.length <= 1) return;
      deleteSpineItem(activeChapter.id);
    }
    if (action === "chapter.moveUp") {
      if (!activeChapter) return;
      reorderChapters(activeChapter.id, -1);
    }
    if (action === "chapter.moveDown") {
      if (!activeChapter) return;
      reorderChapters(activeChapter.id, 1);
    }
    if (action === "chapter.markDraft" || action === "chapter.markReview" || action === "chapter.markFinal") {
      if (!activeChapter) return;
      const nextStatus: ChapterStatus =
        action === "chapter.markFinal" ? "final" : action === "chapter.markReview" ? "in_review" : "draft";
      updateChapter((chapter) => ({ ...chapter, metadata: { ...chapter.metadata, status: nextStatus } }));
      setStatus(`Chapter marked ${statusLabel(nextStatus).toLowerCase()}`);
    }
    if (action === "tools.runStoryCraft") {
      void runStoryCraft();
    }
    if (action === "tools.openChat") {
      setWorkbenchView("book");
      setShowAssistantPanel(true);
      setAssistantPanelTab("chat");
      chatInputRef.current?.focus();
    }
    if (action === "tools.generateSummary") generateSummary();
    if (action === "tools.extractEntities") runEntityExtraction();
    if (action === "help.keyboardShortcuts") setShowShortcutHelp(true);
    if (action === "help.sendFeedback") setShowFeedback(true);
    if (action === "help.about") setStatus("JustWrite - Writing workspace");
    if (action === "file.settings") setWorkbenchView("settings");
  }

  const handleMenuActionRef = useRef(handleMenuAction);
  handleMenuActionRef.current = handleMenuAction;

  useEffect(() => {
    bookRef.current = book;
    // Catches the book mutations that go through `setBook` directly (open,
    // pull, AI variation, import) rather than through `persistBookUpdate`.
    bookChapterSync.enqueueChangedChapters(book);
  }, [book]);

  useEffect(() => {
    bookChapterSync.setListeners({
      onSynced: (info) => {
        const current = bookRef.current;
        if (!current) return;
        // Metadata only — deliberately not `setBook`, so an acknowledgement
        // never re-renders the editor out from under the author.
        bookRef.current = {
          ...current,
          chapters: current.chapters.map((chapter) =>
            chapter.id === info.chapterId
              ? {
                  ...chapter,
                  rev: info.rev,
                  baseRev: info.rev,
                  metadata: {
                    ...chapter.metadata,
                    syncBase: { rev: info.rev, title: info.title, content: info.contentJson },
                  },
                }
              : chapter
          ),
        };
      },
      onStatus: (_chapterId, status, detail) => {
        if (status === "error" && detail) setStatus(userFacingError(detail, "sync"));
      },
    });
    return () => bookChapterSync.setListeners({});
  }, []);

  useEffect(() => {
    setAttachedChatVersion(null);
  }, [book?.id]);

  useEffect(() => {
    let cancelled = false;
    async function loadChatVersionOptions(): Promise<void> {
      if (!book) {
        if (!cancelled) setChatVersionOptions([]);
        return;
      }
      const fromMeta = versionsFromBookMetadata(book.metadata as Record<string, unknown>).map(
        (v) => ({ id: v.id, name: v.name, status: v.status })
      );
      if (!cancelled && fromMeta.length > 0) setChatVersionOptions(fromMeta);

      if (!cloudAuthDesktopV2.isSignedIn() || !book.id?.trim()) {
        if (!cancelled && fromMeta.length === 0) setChatVersionOptions([]);
        return;
      }
      try {
        const remote = await cloudAuthDesktopV2.listBookVersions(book.id);
        if (cancelled) return;
        if (remote.length > 0) {
          setChatVersionOptions(
            remote.map((v) => ({ id: v.id, name: v.name || "", status: v.status || "" }))
          );
        } else if (fromMeta.length === 0) {
          setChatVersionOptions([]);
        }
      } catch {
        if (!cancelled && fromMeta.length === 0) setChatVersionOptions([]);
      }
    }
    void loadChatVersionOptions();
    return () => {
      cancelled = true;
    };
  }, [book]);

  useEffect(() => {
    writePersisted(PANEL_STORAGE_KEYS.showChapters, showChaptersPanel);
  }, [showChaptersPanel]);

  useEffect(() => {
    writePersisted(PANEL_STORAGE_KEYS.showAssistant, showAssistantPanel);
  }, [showAssistantPanel]);

  useEffect(() => {
    let cancelled = false;
    void window.desktopV2Api?.getLlmUserKeys?.().then((keys) => {
      if (cancelled || !keys) return;
      // Saved keys stay on disk; drafts stay empty so Settings can show placeholders.
      if (keys.hasOpenai || keys.hasAnthropic) {
        setOpenaiApiKeyDraft("");
        setAnthropicApiKeyDraft("");
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (workbenchView !== "codex" || !book) return;
    if (getCodex(book).entries.length > 0) return;
    const characters = getCharactersFromBookMetadata((book.metadata ?? {}) as Record<string, unknown>);
    const locations = getLocationsFromBookMetadata((book.metadata ?? {}) as Record<string, unknown>);
    if (characters.length === 0 && locations.length === 0) return;
    const seeded = seedCodexFromRosters(
      {
        ...book,
        metadata: { ...(book.metadata as Record<string, unknown>) },
      },
      characters,
      locations
    );
    applyBookUpdates({ metadata: seeded.metadata });
  }, [workbenchView, book?.id]);

  useEffect(() => {
    writePersisted(PANEL_STORAGE_KEYS.leftWidth, leftPanelWidth);
  }, [leftPanelWidth]);

  useEffect(() => {
    writePersisted(PANEL_STORAGE_KEYS.rightWidth, rightPanelWidth);
  }, [rightPanelWidth]);

  useEffect(() => {
    selectChapterCommentFromEditorRef.current = (commentId) => {
      if (!commentId) {
        setSelectedChapterCommentId(null);
        return;
      }
      const chapter = book?.chapters.find((c) => c.id === activeChapterId) ?? activeChapter;
      const comments = chapter?.comments ?? [];
      const hit = comments.find((c) => c.id === commentId);
      const rootId = hit
        ? flattenReplyParentId(hit.parentCommentId, comments) ??
          (hit.parentCommentId ? hit.parentCommentId : hit.id)
        : commentId;
      setSelectedChapterCommentId(rootId);
      setWorkbenchView("book");
      setShowAssistantPanel(true);
      setAssistantPanelTab("comments");
      queueMicrotask(() => {
        const root = document.querySelector(".assistant-chapter-comments-list");
        if (!root) return;
        const safe =
          typeof CSS !== "undefined" && typeof CSS.escape === "function"
            ? CSS.escape(rootId)
            : rootId.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
        const el = root.querySelector(`[data-chapter-comment-id="${safe}"]`);
        if (el instanceof HTMLElement) {
          el.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }
      });
    };
  });

  useEffect(() => {
    if (!resizingPanel) return;
    const onMouseMove = (event: MouseEvent) => {
      const delta = event.clientX - resizingOriginX;
      if (resizingPanel === "left") {
        setLeftPanelWidth(clampLeftWidth(resizingStartWidth + delta));
      } else {
        setRightPanelWidth(clampRightWidth(resizingStartWidth - delta));
      }
    };
    const onMouseUp = () => setResizingPanel(null);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [resizingPanel, resizingOriginX, resizingStartWidth]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (event: MediaQueryListEvent) => setSystemPrefersDark(event.matches);
    setSystemPrefersDark(media.matches);
    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", onChange);
      return () => media.removeEventListener("change", onChange);
    }
    media.onchange = onChange;
    return () => {
      media.onchange = null;
    };
  }, []);

  useEffect(() => {
    const api = window.desktopV2Api;
    if (!api) {
      setSessionReady(true);
      setStatus("");
      return;
    }
    api.onRequestSave?.(() => void saveProject(false));
    api.onRequestExportDocx?.(() => {
      if (!bookRef.current) return;
      api.exportDocx(bookRef.current).then((result) => {
        if (!result.canceled) {
          setStatus(`DOCX exported to ${result.filePath}`);
          void trackDesktopActivation({
            event: "activation.export_done",
            bookId: bookRef.current?.id,
            metadata: { format: "docx" },
          });
        }
      });
    });
    api.onRequestExportPdf?.(() => {
      if (!bookRef.current) return;
      void (async () => {
        const book = bookRef.current!;
        const authToken = cloudAuthDesktopV2.isSignedIn()
          ? await cloudAuthDesktopV2.getValidAccessToken().catch(() => undefined)
          : undefined;
        const result = await api.exportPdf({ book, authToken: authToken ?? undefined });
        if (result.upgrade) {
          setStatus(result.error || "PDF export requires an Author or Editor plan.");
          return;
        }
        if (result.error) {
          setStatus(result.error);
          return;
        }
        if (!result.canceled) {
          setStatus(`PDF exported to ${result.filePath}`);
          void trackDesktopActivation({
            event: "activation.export_done",
            bookId: book.id,
            metadata: { format: "pdf" },
          });
        }
      })();
    });
    api.onOpenFeedback?.(() => setShowFeedback(true));
    api.onBookOpened?.((_event, payload) => {
      const next = ensureBookShape(payload.book);
      bookRef.current = next;
      setBook(next);
      setFilePath(payload.filePath ?? null);
      setActiveChapterId(next.chapters[0]?.id ?? "");
      setDirty(false);
      setStatus("Project opened");
    });
    const removeMenuListener = api.onMenuAction?.((_event: unknown, payload: { action?: string }) => {
      const menuAction = typeof payload?.action === "string" ? payload.action : "";
      if (!menuAction) return;
      handleMenuActionRef.current(menuAction);
    });
    void (async () => {
      if (isAccountPrimaryRuntime()) {
        let signedIn = false;
        try {
          if (await cloudAuthDesktopV2.isWebCloudConfigured()) {
            signedIn = isWebRuntime()
              ? await cloudAuthDesktopV2.ensureSpaSession()
              : await cloudAuthDesktopV2.restoreSessionFromStorage();
          }
        } catch {
          signedIn = cloudAuthDesktopV2.isSignedIn();
        }
        setCloudSaveEnabled(signedIn);
        if (!signedIn) {
          setStatus("");
          return;
        }
      }
      try {
        const recovery = await api.loadRecovery();
        if (recovery?.book) {
          let next = ensureBookShape(recovery.book);
          const fp = typeof recovery.filePath === "string" ? recovery.filePath.trim() : "";
          if (fp) {
            try {
              const diskPayload = await api.openBookAtPath(fp);
              if (diskPayload?.book) {
                next = mergeDiskBookWithRecoverySession(diskPayload.book, next);
              }
            } catch {
              // keep recovery-only book
            }
          }
          const rec = recovery as { activeChapterId?: string; activeTab?: string };
          const applyChapterSelection = (opened: typeof next) => {
            const chapterIds = new Set(opened.chapters.map((c) => c.id));
            const preferredChapterId =
              typeof rec.activeChapterId === "string" && chapterIds.has(rec.activeChapterId)
                ? rec.activeChapterId
                : (opened.chapters[0]?.id ?? "");
            setActiveChapterId(preferredChapterId);
          };
          if (isRecoverableTabId(rec.activeTab)) {
            setActiveTab(rec.activeTab);
          }
          const hasLocalWork = bookHasUnpushedEdits(next);
          preferLocalDiskOverCloudRef.current = hasLocalWork;
          bootPushOnlyRef.current = true;
          dirtyRef.current = hasLocalWork;
          bookRef.current = next;
          setBook(next);
          setFilePath(fp || (recovery.filePath ?? null));
          applyChapterSelection(next);
          setDirty(hasLocalWork);
          try {
            await persistBookQuietly(next);
            await api.saveRecovery({ book: next, filePath: fp || recovery.filePath || null });
          } catch {
            /* cache update is best-effort */
          }
          setStatus(`Opened “${next.title || "Untitled"}”`);
          return;
        }
      } catch {
        // fall through to last session
      }
      try {
        const last = await api.getLastSession();
        if (last?.filePath) {
          const diskPayload = await api.openBookAtPath(last.filePath);
          if (diskPayload?.book) {
            let next = ensureBookShape(diskPayload.book);
            const hasLocalWork = bookHasUnpushedEdits(next);
            preferLocalDiskOverCloudRef.current = hasLocalWork;
            bootPushOnlyRef.current = true;
            dirtyRef.current = hasLocalWork;
            bookRef.current = next;
            setBook(next);
            setFilePath(diskPayload.filePath ?? last.filePath);
            setActiveChapterId(next.chapters[0]?.id ?? "");
            setDirty(hasLocalWork);
            try {
              await persistBookQuietly(next);
            } catch {
              /* best-effort */
            }
            setStatus(`Reopened “${next.title || "Untitled"}”`);
            return;
          }
        }
      } catch {
        // no last session
      }
      setStatus("");
      return;
    })().finally(() => {
      setSessionReady(true);
    });
    return () => {
      removeMenuListener?.();
    };
  }, []);

  useEffect(() => {
    void (async () => {
      if (!(await cloudAuthDesktopV2.isWebCloudConfigured())) return;
      if (isWebRuntime()) {
        const ok = await cloudAuthDesktopV2.ensureSpaSession();
        setCloudSaveEnabled(ok);
        return;
      }
      const ok = await cloudAuthDesktopV2.restoreSessionFromStorage();
      setCloudSaveEnabled(ok);
    })();
  }, []);

  // Story Craft Chrome extension: hand off desktop access token when ?extensionConnect=1.
  useEffect(() => {
    if (!isWebRuntime()) return;

    async function emitExtensionToken(): Promise<void> {
      try {
        if (!cloudAuthDesktopV2.isSignedIn()) {
          await signInForCurrentRuntime();
          return;
        }
        const token = await cloudAuthDesktopV2.getValidAccessToken();
        window.postMessage(
          { source: "justwrite", type: "JW_EXTENSION_TOKEN", token },
          window.location.origin
        );
      } catch {
        /* ignore — user can retry Connect */
      }
    }

    function onMessage(event: MessageEvent) {
      if (event.source !== window) return;
      const data = event.data;
      if (!data || data.source !== "justwrite-extension" || data.type !== "JW_EXTENSION_REQUEST_TOKEN") {
        return;
      }
      void emitExtensionToken();
    }

    window.addEventListener("message", onMessage);
    const params = new URLSearchParams(window.location.search);
    if (params.get("extensionConnect") === "1") {
      void emitExtensionToken().then(() => {
        params.delete("extensionConnect");
        const next = `${window.location.pathname}${params.toString() ? `?${params}` : ""}${window.location.hash}`;
        window.history.replaceState({}, "", next);
      });
    }
    return () => window.removeEventListener("message", onMessage);
  }, []);

  function manuscriptHasUnsavedEdits(): boolean {
    return dirtyRef.current || (manuscriptEditorRef.current?.hasPendingDraft() ?? false);
  }

  function activeChapterBodyChanged(before: Book, after: Book, chapterId: string): boolean {
    const beforeCh = before.chapters.find((ch) => ch.id === chapterId);
    const afterCh = after.chapters.find((ch) => ch.id === chapterId);
    if (!beforeCh || !afterCh) return beforeCh !== afterCh;
    if (beforeCh.title !== afterCh.title) return true;
    return chapterContentBodyChanged(beforeCh.content, afterCh.content);
  }

  async function flushCloudPushToLibrary(): Promise<void> {
    if (
      !sessionReady ||
      !cloudSaveEnabled ||
      autoSyncInFlightRef.current ||
      autosaveInFlightRef.current ||
      storyCraftBusy ||
      chatBusy
    ) {
      return;
    }
    const current = bookRef.current;
    if (!current?.id?.trim()) return;
    flushManuscriptDraft();
    // Signing in or reopening: anything the outbox still holds ships first.
    await bookChapterSync.flushAll(current);
    autoSyncInFlightRef.current = true;
    try {
      // After open/remount, push this document first. Never pull over it.
      const pushOnly = bootPushOnlyRef.current || manuscriptHasUnsavedEdits();
      bootPushOnlyRef.current = false;
      if (!pushOnly) preferLocalDiskOverCloudRef.current = false;
      const result = await autoSyncBook(current, { pushOnly });
      clearLocalDiskAuthoritativeIfSynced(result.error, result.pushed);
      if (result.error) {
        setStatus(userFacingError(result.error, "sync"));
        return;
      }
      await applySyncResultToState(current, result, { markDirtyOnMerge: true });
      if (result.merged && !isAccountPrimaryRuntime()) {
        setStatus("Synced newer chapter changes from your library");
      }
      // Successful background push stays quiet — Just Write.
    } catch (error) {
      setStatus(userFacingError(error, "sync"));
    } finally {
      autoSyncInFlightRef.current = false;
    }
  }

  useEffect(() => {
    if (!sessionReady || !cloudSaveEnabled || !book?.id?.trim()) return;
    void flushCloudPushToLibrary();
  }, [sessionReady, cloudSaveEnabled, book?.id]);

  // Retry autosave deferred while an AI write was in flight.
  useEffect(() => {
    if (aiWriteBusy) return;
    if (!pendingAutosaveRef.current && !pendingCloudPushRef.current && !dirtyRef.current) return;
    if (pendingAutosaveRef.current || dirtyRef.current) {
      pendingAutosaveRef.current = false;
      scheduleAutosave("content");
    } else if (pendingCloudPushRef.current) {
      pendingCloudPushRef.current = false;
      scheduleCloudPush("deferred");
    }
  }, [aiWriteBusy]);

  useEffect(() => {
    const flushPersistOnLeave = () => {
      flushManuscriptDraft();
      flushBookUi();
      flushRecoverySave();
      // Get every changed chapter into IndexedDB before the window can go away.
      // The outbox drains on next launch even if the push does not finish here.
      void bookChapterSync.flushAll(bookRef.current);
      // Best-effort: kick local + library now when the tab/app backgrounds.
      if (dirtyRef.current || pendingAutosaveRef.current) {
        pendingAutosaveRef.current = false;
        clearAutosaveTimer();
        void autosaveDocument().then(() => {
          scheduleCloudPush("immediate");
        });
      } else {
        scheduleCloudPush("immediate");
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flushPersistOnLeave();
    };
    window.addEventListener("beforeunload", flushPersistOnLeave);
    // Safari and the Tauri webview can skip `beforeunload` entirely.
    window.addEventListener("pagehide", flushPersistOnLeave);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("beforeunload", flushPersistOnLeave);
      window.removeEventListener("pagehide", flushPersistOnLeave);
      document.removeEventListener("visibilitychange", onVisibility);
      clearAutosaveTimer();
      clearCloudPushTimer();
    };
  }, [flushManuscriptDraft, flushBookUi, flushRecoverySave]);

  useEffect(() => {
    if (!cloudSaveEnabled || !book?.id?.trim()) return;
    const intervalMs = 20_000;

    const runIdleLibraryPull = () => {
      if (
        manuscriptHasUnsavedEdits() ||
        (bookRef.current && bookHasUnpushedEdits(bookRef.current)) ||
        autoSyncInFlightRef.current ||
        autosaveInFlightRef.current ||
        aiWriteBusyRef.current ||
        document.visibilityState === "hidden"
      ) {
        return;
      }
      void (async () => {
        autoSyncInFlightRef.current = true;
        try {
          flushManuscriptDraft();
          const current = bookRef.current;
          if (!current?.id?.trim()) return;
          preferLocalDiskOverCloudRef.current = false;
          const result = await autoSyncBook(current, { pushOnly: false });
          clearLocalDiskAuthoritativeIfSynced(result.error, result.pushed);
          if (result.error) return;
          if (bookRef.current !== current) {
            // Work landed during the round trip; it wins and gets pushed next cycle.
            scheduleCloudPushRef.current("deferred");
            return;
          }
          await applySyncResultToState(current, result, { markDirtyOnMerge: true });
          if (result.merged) {
            setStatus("Synced newer chapter changes from your library");
          }
        } catch {
          // ignore background sync errors
        } finally {
          autoSyncInFlightRef.current = false;
        }
      })();
    };

    const timer = window.setInterval(runIdleLibraryPull, intervalMs);
    const onFocus = () => runIdleLibraryPull();
    const onVisibility = () => {
      if (document.visibilityState === "visible") runIdleLibraryPull();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    // Pull once shortly after open so web edits aren't stuck behind the first interval tick.
    const startup = window.setTimeout(runIdleLibraryPull, 2_500);
    return () => {
      window.clearInterval(timer);
      window.clearTimeout(startup);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [cloudSaveEnabled, book?.id]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const metaOrCtrl = event.metaKey || event.ctrlKey;
      if (metaOrCtrl && event.key === ",") {
        event.preventDefault();
        setWorkbenchView("settings");
        return;
      }
      if (metaOrCtrl && event.key.toLowerCase() === "z") {
        if (activeTab === "manuscript") {
          const handled = event.shiftKey ? redoManuscript() : undoManuscript();
          if (handled) {
            event.preventDefault();
            return;
          }
        }
      }
      if (!activeChapter) return;
      if (!isTextEditingTarget(event.target)) {
        if (metaOrCtrl && event.altKey && (event.key === "[" || event.key === "]")) {
          event.preventDefault();
          const delta = event.key === "]" ? 24 : -24;
          if (event.shiftKey && showAssistantPanel && !focusMode) {
            setRightPanelWidth((w) => clampRightWidth(w + delta));
          } else if (!event.shiftKey && showChaptersPanel && !focusMode) {
            setLeftPanelWidth((w) => clampLeftWidth(w + delta));
          }
          return;
        }
      }

      if (event.altKey && event.key === "ArrowUp") {
        event.preventDefault();
        reorderChapters(activeChapter.id, -1);
      }
      if (event.altKey && event.key === "ArrowDown") {
        event.preventDefault();
        reorderChapters(activeChapter.id, 1);
      }
      if (metaOrCtrl && event.key === "Enter" && showAssistantPanel) {
        event.preventDefault();
        void sendChatMessage();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeChapter, showAssistantPanel, chatDraft, chatBusy, showChaptersPanel, focusMode]);

  if ((isAccountPrimaryRuntime() && !cloudSaveEnabled) || !book || !activeChapter) {
    return (
      <>
        <WelcomeScreen
          loading={!sessionReady}
          statusMessage={status.trim() || undefined}
          onOpenBook={() => void openProject()}
          onNewProject={() => setNewBookPickerOpen(true)}
          onSelectCloudBook={(bookId) => void openBookFromCloudById(bookId)}
          cloudOpeningBookId={openingCloudBookId}
          onCloudAuthChange={handleCloudAuthChange}
          onImportBytes={(bytes) => importFromBackupBytes(bytes)}
        />
        <BookTypePickerDialog
          open={newBookPickerOpen}
          onOpenChange={setNewBookPickerOpen}
          onConfirm={(options) => void createNewProject(options)}
        />
      </>
    );
  }

  const resolvedTheme = resolveThemeDataAttribute(normalizeThemeMode(book.settings.theme), systemPrefersDark);

  const isBookView = workbenchView === "book";
  const hideSidePanels = !isBookView || narratorMode;
  const chromeHidden = focusMode || narratorMode;
  const visibleLeft = isBookView && showChaptersPanel && !chromeHidden;
  const visibleRight = isBookView && showAssistantPanel && !chromeHidden;
  const bookWordCount = totalWords(book);
  const activeChapterWordCount = activeChapter ? countWords(activeChapter.content) : 0;
  const activeChapterSharePercent =
    bookWordCount > 0 ? Math.round((activeChapterWordCount / bookWordCount) * 100) : 0;
  const characterRoster = getCharactersFromBookMetadata((book.metadata ?? {}) as Record<string, unknown>);
  const characterRemovals = getCharacterRemovalsFromMetadata((book.metadata ?? {}) as Record<string, unknown>);
  const locationRoster = getLocationsFromBookMetadata((book.metadata ?? {}) as Record<string, unknown>);
  const locationRelationships = getLocationRelationshipsFromMetadata(
    (book.metadata ?? {}) as Record<string, unknown>
  );
  const locationRemovals = getLocationRemovalsFromMetadata((book.metadata ?? {}) as Record<string, unknown>);
  const timelineRoster = getTimelineBeatsFromBookMetadata((book.metadata ?? {}) as Record<string, unknown>);
  const timelineIssues = getTimelineIssuesFromBookMetadata((book.metadata ?? {}) as Record<string, unknown>);
  const timelineRemovals = getTimelineRemovalsFromMetadata((book.metadata ?? {}) as Record<string, unknown>);
  const themeRoster = getThemesFromBookMetadata((book.metadata ?? {}) as Record<string, unknown>);
  const motifRoster = getMotifsFromBookMetadata((book.metadata ?? {}) as Record<string, unknown>);
  const symbolRoster = getSymbolsFromBookMetadata((book.metadata ?? {}) as Record<string, unknown>);
  const themeRemovals = getThemeRemovalsFromMetadata((book.metadata ?? {}) as Record<string, unknown>);
  const motifRemovals = getMotifRemovalsFromMetadata((book.metadata ?? {}) as Record<string, unknown>);
  const symbolRemovals = getSymbolRemovalsFromMetadata((book.metadata ?? {}) as Record<string, unknown>);
  const chapterOrderForCharacters = book.chapters.map((ch) => ({
    id: ch.id,
    order: ch.order,
    title: ch.title,
  }));

  return (
    <div
      data-theme={resolvedTheme}
      className="flex h-full min-h-full flex-col overflow-hidden bg-background text-foreground"
      style={{ fontSize: `${zoomPercent}%` }}
    >
      {narratorMode && activeChapter ? (
        <NarratorModePanel
          bookId={book.id ?? ""}
          bookMetadata={(book.metadata ?? {}) as Record<string, unknown>}
          chapter={activeChapter}
          characters={ensureCharacterColors(
            characterRoster,
            getNarrationFromBookMetadata((book.metadata ?? {}) as Record<string, unknown>).paletteId
          )}
          chapters={chapterOrderForCharacters}
          onExit={() => setNarratorMode(false)}
          onSelectChapter={setActiveChapterId}
          onBookMetadataChange={(metadata) =>
            applyBookUpdates({ metadata: metadata as Book["metadata"] })
          }
          onCharactersChange={(characters) => {
            applyBookUpdates({
              metadata: withCharactersInMetadata(
                (book.metadata ?? {}) as Record<string, unknown>,
                characters
              ) as Book["metadata"],
            });
          }}
        />
      ) : (
        <>
      <TopTitleBar
        bookTitle={book.title}
        onBookTitleChange={(title) => updateBook((current) => ({ ...current, title }))}
        filePath={filePath}
        saving={saving}
        syncHint={!online ? "Offline — will sync" : saving ? "Saving" : dirty ? "Unsaved" : "Saved"}
        onSave={() => void saveProject(false)}
        onOpenFeedback={() => setShowFeedback(true)}
        onOpen={openOpenFromCloudDialog}
        onNew={() => setNewBookPickerOpen(true)}
        canOpenFromCloud={cloudSaveEnabled}
        onOpenFromCloud={openOpenFromCloudDialog}
        canUseLibraryCopy={cloudSaveEnabled && Boolean(book.id?.trim())}
        onUseLibraryCopy={() => {
          flushBookUi();
          if (dirtyRef.current || manuscriptHasUnsavedEdits()) setDirty(true);
          setLibraryOverrideError(null);
          setShowLibraryOverrideModal(true);
        }}
      />

      {book.phase2?.pendingConflict && !PREMIER_SEAMLESS_SYNC ? (
          <div
          className="flex flex-wrap items-center gap-3 border-b border-border bg-muted/40 px-4 py-2 text-sm"
          role="status"
        >
          <span className="text-foreground">
            Sync conflict
            {book.phase2.pendingConflict.chapterTitle
              ? `: “${book.phase2.pendingConflict.chapterTitle}”`
              : ""}
            {book.phase2.pendingConflict.mergeJson
              ? " — review overlapping edits"
              : ""}
          </span>
          {book.phase2.pendingConflict.mergeJson ? (
            <Button
              type="button"
              size="sm"
              onClick={() => setShowConflictReview(true)}
            >
              Review edits
            </Button>
          ) : (
            <>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => {
                  applyBookUpdates({
                    phase2: {
                      ...book.phase2,
                      pendingConflict: null,
                    },
                  });
                  setStatus("Kept local version — conflict cleared");
                }}
              >
                Keep local
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  applyBookUpdates({
                    phase2: {
                      ...book.phase2,
                      pendingConflict: null,
                    },
                  });
                  setStatus(
                    "Pull remote: conflict cleared. Re-open from cloud if you need the remote chapter text."
                  );
                }}
              >
                Pull remote
              </Button>
            </>
          )}
        </div>
      ) : null}

      {!PREMIER_SEAMLESS_SYNC ? (
      <ConflictReview
        open={showConflictReview && Boolean(book.phase2?.pendingConflict?.mergeJson)}
        merge={(() => {
          const raw = book.phase2?.pendingConflict?.mergeJson;
          if (!raw) return null;
          try {
            return JSON.parse(raw) as ThreeWayMergeResult;
          } catch {
            return null;
          }
        })()}
        onClose={() => setShowConflictReview(false)}
        onApply={(resolutions: ChapterConflictResolution[]) => {
          const chapterId = book.phase2?.pendingConflict?.chapterId;
          if (!chapterId) return;
          const next = applyPendingConflictResolutions(book, chapterId, resolutions);
          applyBookUpdates({ chapters: next.chapters, phase2: next.phase2 });
          setShowConflictReview(false);
          setStatus("Conflict resolved — syncing merged chapter");
          setDirty(true);
        }}
        onKeepAllMine={() => {
          const chapterId = book.phase2?.pendingConflict?.chapterId;
          const merge = book.phase2?.pendingConflict?.mergeJson;
          if (!chapterId || !merge) return;
          let parsed: ThreeWayMergeResult;
          try {
            parsed = JSON.parse(merge) as ThreeWayMergeResult;
          } catch {
            return;
          }
          const resolutions = parsed.conflicts.map((h) => ({
            hunkId: h.id,
            choice: "mine" as const,
          }));
          const next = applyPendingConflictResolutions(book, chapterId, resolutions);
          applyBookUpdates({ chapters: next.chapters, phase2: next.phase2 });
          setShowConflictReview(false);
          setStatus("Kept local edits — conflict cleared");
          setDirty(true);
        }}
        onKeepAllTheirs={() => {
          const chapterId = book.phase2?.pendingConflict?.chapterId;
          const merge = book.phase2?.pendingConflict?.mergeJson;
          if (!chapterId || !merge) return;
          let parsed: ThreeWayMergeResult;
          try {
            parsed = JSON.parse(merge) as ThreeWayMergeResult;
          } catch {
            return;
          }
          const resolutions = parsed.conflicts.map((h) => ({
            hunkId: h.id,
            choice: "theirs" as const,
          }));
          const next = applyPendingConflictResolutions(book, chapterId, resolutions);
          applyBookUpdates({ chapters: next.chapters, phase2: next.phase2 });
          setShowConflictReview(false);
          setStatus("Kept remote edits — conflict cleared");
          setDirty(true);
        }}
      />
      ) : null}

      <WorkbenchLayout
        showActivityBar
        activityBar={
          <ActivityBar
            activeView={workbenchView}
            focusMode={chromeHidden}
            showAdminFeedback={isSysAdmin}
            visibleViews={bookTypeProfile.views}
            onSelectView={(view) => {
              setWorkbenchView(view);
              if (view === "planning" && book.id) {
                void trackDesktopActivation({
                  event: "activation.planning_opened",
                  bookId: book.id,
                });
              }
            }}
          />
        }
        visibleLeft={visibleLeft}
        visibleRight={visibleRight}
        hideSidePanels={hideSidePanels}
        focusMode={chromeHidden}
        leftPanelWidth={leftPanelWidth}
        rightPanelWidth={rightPanelWidth}
        resizingPanel={resizingPanel}
        leftAriaValueNow={leftPanelWidth}
        rightAriaValueNow={rightPanelWidth}
        onResizeMouseDown={(side, clientX) => beginPanelResize(side, clientX)}
        onResizeDoubleClick={(side) => resetPanelWidth(side)}
        leftAside={
          visibleLeft ? (
            <SidebarChaptersPanel
              chapterSearchRef={chapterSearchRef}
              chapterSearch={chapterSearch}
              onChapterSearchChange={setChapterSearch}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              tagFilter={tagFilter}
              onTagFilterChange={setTagFilter}
              allTags={allTags}
              visibleChapters={visibleChapters}
              activeChapterId={activeChapterId}
              onSelectChapter={setActiveChapterId}
              onCollapse={() => setShowChaptersPanel(false)}
              onAddChapter={() => {
                const chapter = createChapter();
                updateBook((current) => ({
                  ...current,
                  chapters: withSpineOrderAndNumbers([...current.chapters, chapter]),
                }));
                setActiveChapterId(chapter.id);
              }}
              onInsertRelative={(relativeId, position, kind) => {
                insertSpineItem({ relativeId, position, kind });
              }}
              onChangeKind={(chapterId, kind) => {
                changeSpineKind(chapterId, kind);
              }}
              onMoveChapter={setMoveChapterSourceId}
              onMoveRelative={(chapterId, direction) => reorderChapters(chapterId, direction)}
              onDeleteChapter={deleteSpineItem}
              setDragChapterId={setDragChapterId}
              onDropReorder={moveChapterByDrop}
              countWords={countWords}
              statusLabel={statusLabel}
              storyCraftAverages={storyCraftAverages}
              showSpineMatterInsert={workbenchPersona.showSpineMatterInsert}
              showScoreBlendControls={workbenchPersona.showScoreBlendControls}
              scoreBlend={scoreBlend}
              onScoreBlendChange={saveScoreBlend}
              bodyLabel={bookTypeProfile.bodyLabel}
              bodyLabelPlural={bookTypeProfile.bodyLabelPlural}
              numberBodyItems={bookTypeProfile.numberBodyItems}
              allowMultipleBodyItems={bookTypeProfile.allowMultipleBodyItems}
            />
          ) : (
            <SidebarChaptersRail onExpand={() => setShowChaptersPanel(true)} />
          )
        }
        center={
          workbenchView === "planning" && book ? (
            <PlanningPanel
              book={book}
              activeChapterId={activeChapterId || null}
              mode={planningMode}
              onModeChange={setPlanningMode}
              storyCraftAverages={storyCraftAverages}
              promiseDebtByChapter={promiseDebtByChapter}
              onSelectChapter={(chapterId) => {
                setActiveChapterId(chapterId);
                setWorkbenchView("book");
              }}
              onUpdateChapterSummary={(chapterId, summary) => {
                updateBook((current) => ({
                  ...current,
                  chapters: current.chapters.map((ch) =>
                    ch.id === chapterId
                      ? { ...ch, tabData: { ...ch.tabData, summary } }
                      : ch
                  ),
                }));
              }}
              onDetectScenes={(chapterId) => {
                updateBook((current) => ({
                  ...current,
                  chapters: current.chapters.map((ch) => {
                    if (ch.id !== chapterId) return ch;
                    return setChapterScenes(ch, splitScenesFromContent(ch.content));
                  }),
                }));
                setStatus("Scenes detected from chapter markers");
              }}
              onUpdateScene={(chapterId, sceneId, patch) => {
                updateBook((current) => ({
                  ...current,
                  chapters: current.chapters.map((ch) => {
                    if (ch.id !== chapterId) return ch;
                    return updateScene(ch, sceneId, patch);
                  }),
                }));
              }}
            />
          ) : workbenchView === "plot" && book ? (
            <PlotGridPanel
              book={book}
              onBookChange={(next) => {
                updateBook(() => next);
              }}
            />
          ) : workbenchView === "codex" && book ? (
            <CodexPanel
              book={book}
              onBookChange={applyBookUpdates}
              chapterText={
                activeChapter ? chapterPlainTextForSync(activeChapter.content) : undefined
              }
              onSyncFromRosters={() => {
                const beforeEntries = getCodex(book).entries;
                const beforeById = new Map(beforeEntries.map((e) => [e.id, e]));
                const beforeLen = beforeEntries.length;
                const seeded = seedCodexFromRosters(
                  {
                    ...book,
                    metadata: { ...(book.metadata as Record<string, unknown>) },
                  },
                  characterRoster,
                  locationRoster
                );
                applyBookUpdates({ metadata: seeded.metadata });
                const afterEntries = getCodex(seeded).entries;
                const added = Math.max(0, afterEntries.length - beforeLen);
                // Entries that existed before and were re-upserted from rosters count as updated.
                const rosterTouched = afterEntries.filter((e) => {
                  if (e.type !== "character" && e.type !== "location") return false;
                  return beforeById.has(e.id);
                }).length;
                const updated = Math.max(0, rosterTouched);
                return { added, updated };
              }}
              onInjectContext={(snippet) => {
                setChatDraft((prev) => (snippet ? `${snippet}\n\n${prev}` : prev));
                setWorkbenchView("book");
                setShowAssistantPanel(true);
                setAssistantPanelTab("chat");
              }}
            />
          ) : workbenchView === "audio" && book && activeChapter ? (
            <AudioPanel
              activeChapter={activeChapter}
              chapters={book.chapters}
              cloudEnabled={cloudSaveEnabled}
              bookId={book.id}
              audioSettings={bookAudioSettings}
              onSelectChapter={setActiveChapterId}
              onChapterVoiceChange={saveChapterAudioVoice}
              onListen={() => {
                void trackDesktopActivation({
                  event: "activation.listen_done",
                  bookId: book.id,
                  chapterId: activeChapter.id,
                });
              }}
            />
          ) : workbenchView === "formatter" && book ? (
            <PublishPanel
              book={book}
              authToken={window.localStorage.getItem("jw_desktop_cloud_access") ?? undefined}
              onBookChange={applyBookUpdates}
              onRequestTranslate={() => setTranslateDialogOpen(true)}
            />
          ) : workbenchView === "characters" && book ? (
            <CharactersGuidePanel
              roster={characterRoster}
              removals={characterRemovals}
              chapters={chapterOrderForCharacters}
              selectedId={selectedCharacterId}
              onSelect={setSelectedCharacterId}
              onUpdate={updateCharacterInRoster}
              onRequestRemove={requestCharacterRemoval}
              onAdd={(role) => addCharacterToRoster(role)}
              holisticBusy={wholeBookCharacterBusy}
              extractModel={analysisModel}
              onExtractModelChange={handleAnalysisModelChange}
              openAiModels={OPENAI_CHAT_MODELS_LIST}
              anthropicModels={ANTHROPIC_CHAT_MODELS_LIST}
              onAnalyzeWholeBook={() => void runWholeBookCharacterExtraction()}
              onOpenChapter={(chapterId) => {
                setActiveChapterId(chapterId);
                setActiveTab("characters");
                setWorkbenchView("book");
              }}
            />
          ) : workbenchView === "locations" && book ? (
            <LocationsGuidePanel
              roster={locationRoster}
              relationships={locationRelationships}
              removals={locationRemovals}
              chapters={chapterOrderForCharacters}
              selectedId={selectedLocationId}
              onSelect={setSelectedLocationId}
              onUpdate={updateLocationInRoster}
              onUpdateRelationships={updateLocationRelationships}
              onRequestRemove={requestLocationRemoval}
              onAdd={() => addLocationToRoster()}
              holisticBusy={wholeBookLocationBusy}
              extractModel={analysisModel}
              onExtractModelChange={handleAnalysisModelChange}
              openAiModels={OPENAI_CHAT_MODELS_LIST}
              anthropicModels={ANTHROPIC_CHAT_MODELS_LIST}
              onAnalyzeWholeBook={() => void runWholeBookLocationExtraction()}
              onOpenChapter={(chapterId) => {
                setActiveChapterId(chapterId);
                setActiveTab("locations");
                setWorkbenchView("book");
              }}
            />
          ) : workbenchView === "timeline" && book ? (
            <TimelineGuidePanel
              roster={timelineRoster}
              issues={timelineIssues}
              removals={timelineRemovals}
              chapters={chapterOrderForCharacters}
              selectedId={selectedTimelineBeatId}
              onSelect={setSelectedTimelineBeatId}
              onUpdate={updateTimelineBeatInRoster}
              onRequestRemove={requestTimelineBeatRemoval}
              onAdd={() => addTimelineBeatToRoster()}
              holisticBusy={wholeBookTimelineBusy}
              extractModel={analysisModel}
              onExtractModelChange={handleAnalysisModelChange}
              openAiModels={OPENAI_CHAT_MODELS_LIST}
              anthropicModels={ANTHROPIC_CHAT_MODELS_LIST}
              onAnalyzeWholeBook={() => void runWholeBookTimelineExtraction()}
              onOpenChapter={(chapterId) => {
                setActiveChapterId(chapterId);
                setActiveTab("timeline");
                setWorkbenchView("book");
              }}
            />
          ) : workbenchView === "themes" && book ? (
            <ThemesGuidePanel
              themes={themeRoster}
              motifs={motifRoster}
              symbols={symbolRoster}
              themeRemovals={themeRemovals}
              motifRemovals={motifRemovals}
              symbolRemovals={symbolRemovals}
              chapters={chapterOrderForCharacters}
              selected={selectedThemeRef}
              onSelect={setSelectedThemeRef}
              onUpdateTheme={updateThemeInRoster}
              onUpdateMotif={updateMotifInRoster}
              onUpdateSymbol={updateSymbolInRoster}
              onRequestRemoveTheme={requestThemeRemoval}
              onRequestRemoveMotif={requestMotifRemoval}
              onRequestRemoveSymbol={requestSymbolRemoval}
              onAdd={(kind) => addThemeEntityToRoster(kind)}
              busy={wholeBookThemeBusy}
              extractModel={analysisModel}
              onExtractModelChange={handleAnalysisModelChange}
              openAiModels={OPENAI_CHAT_MODELS_LIST}
              anthropicModels={ANTHROPIC_CHAT_MODELS_LIST}
              onAnalyzeWholeBook={() => void runWholeBookThemeExtraction()}
              onOpenChapter={(chapterId) => {
                setActiveChapterId(chapterId);
                setActiveTab("themesMotifs");
                setWorkbenchView("book");
              }}
            />
          ) : workbenchView === "research" && book ? (
            <ResearchPanel
              book={book}
              citationsEnabled={bookTypeProfile.citations}
              onBookChange={applyBookUpdates}
              onInsertCitation={
                bookTypeProfile.citations ? (citation) => insertCitationIntoManuscript(citation) : undefined
              }
              onInsertImageAsset={
                bookTypeProfile.inlineImages ? (asset) => insertImageAssetIntoManuscript(asset) : undefined
              }
            />
          ) : workbenchView === "beta" ? (
            <BetaReadersPanel bookId={book?.id?.trim() || null} signedIn={cloudAuthDesktopV2.isSignedIn()} />
          ) : workbenchView === "adminFeedback" ? (
            <AdminFeedbackPanel />
          ) : workbenchView === "settings" ? (
            <SettingsPanel
              onCloudAuthChange={handleCloudAuthChange}
              themeMode={normalizeThemeMode(book.settings.theme)}
              onThemeModeChange={(next) =>
                updateBook((current) => ({ ...current, settings: { ...current.settings, theme: next } }))
              }
              defaultChatModel={chatModel}
              onDefaultChatModelChange={(model, provider) => {
                setChatModel(model);
                setChatProvider(provider);
                writePersistedDefaultChatModel(model);
              }}
              persona={workbenchPersona.persona}
              onPersonaChange={workbenchPersona.setPersona}
              onOpenBookId={(bookId) => {
                setWorkbenchView("book");
                return openBookFromCloudById(bookId);
              }}
              audioSettings={bookAudioSettings}
              onAudioSettingsChange={saveBookAudioSettings}
              hasOpenBook={Boolean(book)}
              writingGoalWords={normalizeWritingGoalWords(book?.settings.writingGoalWords)}
              onWritingGoalChange={(n) =>
                updateBook((current) => ({
                  ...current,
                  settings: { ...current.settings, writingGoalWords: normalizeWritingGoalWords(n) },
                }))
              }
              bookType={bookTypeProfile.id}
              bookStructure={bookTypeProfile.structure}
              onBookTypeChange={(nextType, nextStructure) => {
                const nextProfile = profileFromBookMetadata({
                  bookType: nextType,
                  structure: nextStructure,
                });
                updateBook((current) => {
                  const meta: Record<string, unknown> = {
                    ...(current.metadata as Record<string, unknown>),
                    bookType: nextProfile.id,
                    structure: nextProfile.structure,
                  };
                  if (nextProfile.citationStyle && !meta.citationStyle) {
                    meta.citationStyle = nextProfile.citationStyle;
                  }
                  const withMeta = { ...current, metadata: meta as Book["metadata"] };
                  return nextProfile.citations ? applyGeneratedBibliography(withMeta) : withMeta;
                });
                setStatus(`Book type set to ${nextProfile.label}`);
              }}
              openaiApiKeyDraft={openaiApiKeyDraft}
              anthropicApiKeyDraft={anthropicApiKeyDraft}
              onOpenaiApiKeyChange={setOpenaiApiKeyDraft}
              onAnthropicApiKeyChange={setAnthropicApiKeyDraft}
              onSaveApiKeys={async () => {
                await window.desktopV2Api.setLlmUserKeys({
                  openaiApiKey: openaiApiKeyDraft,
                  anthropicApiKey: anthropicApiKeyDraft,
                });
                setApiKeysSaved(true);
                setOpenaiApiKeyDraft("");
                setAnthropicApiKeyDraft("");
              }}
              apiKeysSaved={apiKeysSaved}
            />
          ) : (
            <EditorWorkbench<TabId>
              tabs={editorTabItems}
              manuscriptTabId="manuscript"
              briefTabId="summary"
              briefPanel={
                activeChapter ? (
                  <ChapterBriefPanel
                    activeChapter={activeChapter}
                    busy={summaryBusy}
                    model={analysisModel}
                    onModelChange={handleAnalysisModelChange}
                    openAiModels={OPENAI_CHAT_MODELS_LIST}
                    anthropicModels={ANTHROPIC_CHAT_MODELS_LIST}
                    onGenerate={() => void generateSummary()}
                    onChange={(value) =>
                      updateChapter((chapter) => ({
                        ...chapter,
                        tabData: { ...chapter.tabData, summary: value },
                      }))
                    }
                  />
                ) : null
              }
              activeTab={activeTab}
              onTabChange={setActiveTab}
              activeChapter={activeChapter}
              manuscriptEditorRef={manuscriptEditorRef}
              manuscriptReloadToken={manuscriptReloadToken}
              manuscriptThemePreset={
                book
                  ? resolveThemeDataAttribute(normalizeThemeMode(book.settings.theme), systemPrefersDark)
                  : null
              }
              manuscriptEditable={activeTab === "manuscript"}
              selectedChapterCommentId={selectedChapterCommentId}
              onManuscriptCommentSelect={(commentId) => {
                selectChapterCommentFromEditorRef.current(commentId);
              }}
              onManuscriptDraftFlush={handleManuscriptDraftFlush}
              onManuscriptTyping={handleManuscriptTyping}
              onChapterTitleChange={(title) => updateChapter((chapter) => ({ ...chapter, title }))}
              onChapterCaptionChange={(caption) => updateChapter((chapter) => ({ ...chapter, caption }))}
              onChapterStatusChange={(status) =>
                updateChapter((chapter) => ({ ...chapter, metadata: { ...chapter.metadata, status } }))
              }
              onChapterTagsChange={(tags) =>
                updateChapter((chapter) => ({ ...chapter, metadata: { ...chapter.metadata, tags } }))
              }
              onTabFieldChange={(tabId, value) =>
                updateChapter((chapter) => ({
                  ...chapter,
                  tabData: { ...chapter.tabData, [tabId]: value },
                }))
              }
              onOpenAudio={() => setWorkbenchView("audio")}
              compareVariation={activeCompareVariation}
              compareBaseContent={activeChapter ? activeChapter.content : undefined}
              onCompareApply={(mergedContent, hunks) => {
                if (!activeCompareVariation) return;
                applyCompareVariation(activeCompareVariation.id, mergedContent, hunks);
              }}
              onCompareClose={closeVariationCompare}
              onCompareVariationContentChange={(content) => {
                if (!activeCompareVariation) return;
                updateChapterVariationContent(activeCompareVariation.id, content);
              }}
              onRequestComment={() => setCommentComposerOpen(true)}
              findReplaceOpen={findReplaceOpen}
              onFindReplaceOpenChange={setFindReplaceOpen}
              liveCollabYDoc={liveCollab.ydoc}
              liveCollabAwareness={liveCollab.awareness}
              liveCollabUser={liveCollab.caretUser}
              liveCollabStatus={liveCollab.enabled ? liveCollab.status : null}
              liveCollabSessionKey={liveCollab.sessionKey}
              allowCitations={bookTypeProfile.citations}
              allowImages={bookTypeProfile.inlineImages}
              citationOptions={getCitationState((book.metadata ?? {}) as Record<string, unknown>).items.map(
                (citation) => ({
                  id: citation.id,
                  label: formatInTextCitation(
                    citation,
                    getCitationState((book.metadata ?? {}) as Record<string, unknown>).style
                  ),
                })
              )}
              imageOptions={getResearchState((book.metadata ?? {}) as Record<string, unknown>)
                .assets.filter((asset) => asset.kind === "image")
                .map((asset) => ({
                  id: asset.id,
                  title: asset.title,
                  src: asset.previewDataUrl,
                }))}
              onInsertCitation={(citationId) => {
                const citation = getCitationState((book.metadata ?? {}) as Record<string, unknown>).items.find(
                  (item) => item.id === citationId
                );
                if (citation) insertCitationIntoManuscript(citation);
              }}
              onInsertImage={(assetId) => {
                const asset = getResearchState((book.metadata ?? {}) as Record<string, unknown>).assets.find(
                  (item) => item.id === assetId
                );
                if (asset) insertImageAssetIntoManuscript(asset);
              }}
              resolveImageSrc={(assetId) => {
                const asset = getResearchState((book.metadata ?? {}) as Record<string, unknown>).assets.find(
                  (item) => item.id === assetId
                );
                return asset?.previewDataUrl ?? null;
              }}
              storyCraftTabId="storyCraft"
              storyCraftPanel={
                activeChapter ? (
                  <StoryCraftChapterPanel
                    activeChapter={activeChapter}
                    feedback={activeStoryCraft}
                    busy={storyCraftBusy}
                    storyCraftModel={analysisModel}
                    onStoryCraftModelChange={handleAnalysisModelChange}
                    chatModelsOpenAi={OPENAI_CHAT_MODELS_LIST}
                    chatModelsAnthropic={ANTHROPIC_CHAT_MODELS_LIST}
                    onRunStoryCraft={() => void runStoryCraft()}
                    onDeleteStoryCraft={deleteStoryCraftForActiveChapter}
                    onToggleChecklistItem={toggleStoryCraftChecklistItem}
                    onUpdateChecklistItem={updateStoryCraftChecklistItem}
                    onAddChecklistItem={addStoryCraftChecklistItem}
                    onDeleteChecklistItem={deleteStoryCraftChecklistItem}
                    onOpenManuscript={() => setActiveTab("manuscript")}
                    onGenerateVariationFromChecklist={() => void generateVariationFromStoryCraftChecklist()}
                    variationBusy={variationBusy}
                    showEditorScore={workbenchPersona.showScoreBlendControls}
                    editorFeedback={editorFeedbackFromChapterFields(activeChapter.metadata?.customFields)}
                    canEditEditorScore={
                      workbenchPersona.showScoreBlendControls &&
                      (activeChapter.kind ?? "chapter") === "chapter"
                    }
                    onSaveEditorScore={saveEditorScoreForActiveChapter}
                  />
                ) : null
              }
              historyTabId="history"
              historyPanel={
                <ChapterChangeHistoryPanel
                  bookId={book?.id}
                  chapterId={activeChapter?.id}
                  active={activeTab === "history"}
                  onRestored={() => void confirmLibraryOverride()}
                  onRestoreLocalBody={restoreLocalChapterBody}
                />
              }
              charactersTabId="characters"
              charactersPanel={
                activeChapter ? (
                  <ChapterCharactersPanel
                    activeChapter={activeChapter}
                    chapters={chapterOrderForCharacters}
                    roster={characterRoster}
                    busy={characterExtractBusy}
                    extractModel={analysisModel}
                    onExtractModelChange={handleAnalysisModelChange}
                    openAiModels={OPENAI_CHAT_MODELS_LIST}
                    anthropicModels={ANTHROPIC_CHAT_MODELS_LIST}
                    onExtract={() => void runCharacterExtraction()}
                    onUpdateCharacter={updateCharacterInRoster}
                    onAddCharacter={(role) => addCharacterToRoster(role, activeChapter.id)}
                    onOpenInGuide={openCharacterInGuide}
                    onRequestRemove={requestCharacterRemoval}
                  />
                ) : null
              }
              locationsTabId="locations"
              locationsPanel={
                activeChapter ? (
                  <ChapterLocationsPanel
                    activeChapter={activeChapter}
                    roster={locationRoster}
                    busy={locationExtractBusy}
                    extractModel={analysisModel}
                    onExtractModelChange={handleAnalysisModelChange}
                    openAiModels={OPENAI_CHAT_MODELS_LIST}
                    anthropicModels={ANTHROPIC_CHAT_MODELS_LIST}
                    onExtract={() => void runLocationExtraction()}
                    onUpdateLocation={updateLocationInRoster}
                    onAddLocation={() => addLocationToRoster(activeChapter.id)}
                    onOpenInGuide={openLocationInGuide}
                    onRequestRemove={requestLocationRemoval}
                  />
                ) : null
              }
              timelineTabId="timeline"
              timelinePanel={
                activeChapter ? (
                  <ChapterTimelinePanel
                    activeChapter={activeChapter}
                    roster={timelineRoster}
                    busy={timelineExtractBusy}
                    extractModel={analysisModel}
                    onExtractModelChange={handleAnalysisModelChange}
                    openAiModels={OPENAI_CHAT_MODELS_LIST}
                    anthropicModels={ANTHROPIC_CHAT_MODELS_LIST}
                    onExtract={() => void runTimelineExtraction()}
                    onUpdateBeat={updateTimelineBeatInRoster}
                    onAddBeat={() => addTimelineBeatToRoster(activeChapter.id)}
                    onOpenInGuide={openTimelineBeatInGuide}
                    onRequestRemove={requestTimelineBeatRemoval}
                  />
                ) : null
              }
              themesTabId="themesMotifs"
              themesPanel={
                activeChapter ? (
                  <ChapterThemesPanel
                    activeChapter={activeChapter}
                    themes={themeRoster}
                    motifs={motifRoster}
                    symbols={symbolRoster}
                    busy={themeExtractBusy}
                    extractModel={analysisModel}
                    onExtractModelChange={handleAnalysisModelChange}
                    openAiModels={OPENAI_CHAT_MODELS_LIST}
                    anthropicModels={ANTHROPIC_CHAT_MODELS_LIST}
                    onExtract={() => void runThemeExtraction()}
                    onUpdateTheme={updateThemeInRoster}
                    onUpdateMotif={updateMotifInRoster}
                    onUpdateSymbol={updateSymbolInRoster}
                    onAdd={(kind) => addThemeEntityToRoster(kind, activeChapter.id)}
                    onOpenInGuide={openThemeEntityInGuide}
                    onRequestRemoveTheme={requestThemeRemoval}
                    onRequestRemoveMotif={requestMotifRemoval}
                    onRequestRemoveSymbol={requestSymbolRemoval}
                  />
                ) : null
              }
            />
          )
        }
        rightAside={
          visibleRight ? (
            <AssistantPanel
              activeChapter={activeChapter}
              assistantPanelTab={assistantPanelTab}
              onAssistantTabChange={setAssistantPanelTab}
              onCollapse={() => setShowAssistantPanel(false)}
              chatBusy={chatBusy}
              activeChats={activeChats}
              chatThreads={chatThreads}
              archivedChatThreads={archivedChatThreads}
              activeChatThreadId={assistantState?.activeChatThreadId ?? null}
              onSelectChatThread={selectChatThread}
              onRestoreChatThread={restoreArchivedChat}
              onNewChatThread={startNewChatThread}
              onArchiveChat={archiveActiveChat}
              chatStickToBottomRef={chatStickToBottomRef}
              contextChapterTitle={activeChapter?.title ?? null}
              chatDraft={chatDraft}
              onChatDraftChange={setChatDraft}
              onSendChat={() => void sendChatMessage()}
              onStopChat={stopChatGeneration}
              chatInputRef={chatInputRef}
              chatModel={chatModel}
              onChatModelChange={(model, provider) => {
                setChatModel(model as ChatModelValue);
                setChatProvider(provider);
                writePersistedDefaultChatModel(model as LlmModelValue);
              }}
              chatModelsOpenAi={OPENAI_CHAT_MODELS_LIST}
              chatModelsAnthropic={ANTHROPIC_CHAT_MODELS_LIST}
              storyCraftModel={analysisModel}
              onStoryCraftModelChange={handleAnalysisModelChange}
              uploadedChatImage={uploadedChatImage}
              onRemoveUploadedImage={() => setUploadedChatImage(null)}
              chatImageInputRef={chatImageInputRef}
              onChatImagePick={() => chatImageInputRef.current?.click()}
              onUploadChatImage={handleChatImageUpload}
              attachedChatVersion={attachedChatVersion}
              chatVersionOptions={chatVersionOptions}
              onAttachChatVersion={(versionId) => {
                const match = chatVersionOptions.find((v) => v.id === versionId);
                if (match) setAttachedChatVersion(match);
              }}
              onRemoveAttachedChatVersion={() => setAttachedChatVersion(null)}
              starterPrompts={CHAT_STARTER_PROMPTS as Record<string, string>}
              renderMarkdown={renderMarkdown}
              onClearChat={clearActiveChat}
              onAddComment={(text, type, opts) => addChapterComment(text, type, opts)}
              onUpdateComment={updateChapterCommentText}
              onToggleCommentResolved={toggleChapterCommentResolved}
              onJumpToComment={jumpToChapterComment}
              onDeleteComment={deleteChapterComment}
              getCommentStale={getCommentStale}
              selectedCommentId={selectedChapterCommentId}
              canEditComment={(comment) => canEditDesktopChapterComment(comment, currentCloudUserId)}
              canResolveComment={(comment) => canResolveDesktopChapterComment(comment, currentCloudUserId)}
              canDeleteComment={(comment) => canDeleteDesktopChapterComment(comment, currentCloudUserId)}
              onOpenSelectionComment={() => setCommentComposerOpen(true)}
              assistantNotes={assistantNotes}
              onAssistantNotesChange={(value) =>
                updateChapter((chapter) => ({
                  ...chapter,
                  metadata: {
                    ...chapter.metadata,
                    customFields: { ...chapter.metadata.customFields, assistantNotes: value },
                  },
                }))
              }
              runStoryCraft={() => void runStoryCraft()}
              generateSummary={() => void generateSummary()}
              runEntityExtraction={runEntityExtraction}
              activeStoryCraft={activeStoryCraft}
              storyCraftBusy={storyCraftBusy}
              summaryBusy={summaryBusy}
              applyStoryCraftDraft={() => applyStoryCraft("draft")}
              applyStoryCraftNotes={() => applyStoryCraft("notes")}
              onGenerateVariationFromStoryCraftChecklist={() => void generateVariationFromStoryCraftChecklist()}
              chapterVariations={activeChapter.variations ?? []}
              activeVariationId={activeCompareVariationId}
              onOpenVariationCompare={openVariationCompare}
              onDismissVariation={dismissCompareVariation}
              onStartVariationChat={startVariationChat}
              onGenerateVariationFromChat={() => void generateVariationFromChat()}
              onSaveChatToResearch={saveActiveChatToResearch}
              onRequestChapterVariation={(instructions) => void requestChapterVariation(instructions)}
              onStopVariation={stopVariationGeneration}
              onSteerVariation={(direction) => void steerChapterVariation(direction)}
              variationBusy={variationBusy}
              onSceneTool={(toolId: SceneToolId) => {
                if (!activeChapter) return;
                const editor = getManuscriptEditor();
                let selection = "";
                if (editor) {
                  const { from, to } = editor.state.selection;
                  if (to > from) {
                    try {
                      selection = editor.state.doc.textBetween(from, to, "\n", "\n").trim();
                    } catch {
                      selection = "";
                    }
                  }
                }
                const excerpt = chapterPlainTextForSync(activeChapter.content).slice(0, 4000);
                const storyCraftChecklist =
                  activeStoryCraft?.checklist
                    ?.map((c) => c.suggestion)
                    .filter((s) => typeof s === "string" && s.trim()) ?? [];
                void sendChatMessage(
                  buildSceneToolPrompt(toolId, {
                    selection,
                    chapterTitle: activeChapter.title,
                    chapterExcerpt: excerpt,
                    storyCraftChecklist,
                    storyCraftSummary: activeStoryCraft?.summary,
                  })
                );
                void trackDesktopActivation({
                  event: "activation.scene_tool_used",
                  bookId: book.id,
                  chapterId: activeChapter.id,
                  metadata: { toolId },
                });
              }}
            />
          ) : (
            <AssistantPanelRail onExpand={() => setShowAssistantPanel(true)} />
          )
        }
      />

      <footer className="flex flex-nowrap items-center gap-5 border-t border-border bg-muted/25 px-2.5 py-1.5 font-mono text-[0.72rem] text-(--status-foreground)">
        <span>
          {activeChapterWordCount.toLocaleString()} / {bookWordCount.toLocaleString()} words (
          {activeChapterSharePercent}%)
          {(() => {
            const goal = normalizeWritingGoalWords(book.settings.writingGoalWords);
            if (goal <= 0) return null;
            const pct = Math.min(100, Math.round((bookWordCount / goal) * 100));
            return (
              <>
                {" · "}
                {pct}% of {goal.toLocaleString()}-word goal
              </>
            );
          })()}
        </span>
        <span
          className={
            isStatusProblem(status)
              ? "ml-auto max-w-[min(60vw,40rem)] whitespace-normal wrap-break-word rounded-md border border-destructive/30 bg-destructive/10 px-2.5 py-0.5 text-right font-medium text-destructive"
              : isStatusNotice(status)
                ? "ml-auto max-w-[min(60vw,40rem)] whitespace-normal wrap-break-word rounded-md border border-border bg-muted px-2.5 py-0.5 text-right text-muted-foreground"
                : "ml-auto max-w-[min(42vw,28rem)] overflow-hidden text-ellipsis whitespace-nowrap text-right"
          }
          title={status}
        >
          {status}
        </span>
      </footer>

      <ManuscriptCommentToolbar
        editor={getManuscriptEditor()}
        comment={selectedChapterComment}
        canResolve={
          selectedChapterComment
            ? canResolveDesktopChapterComment(selectedChapterComment, currentCloudUserId)
            : false
        }
        canDelete={
          selectedChapterComment
            ? canDeleteDesktopChapterComment(selectedChapterComment, currentCloudUserId)
            : false
        }
        onResolve={() => {
          if (selectedChapterCommentId) toggleChapterCommentResolved(selectedChapterCommentId);
        }}
        onDelete={() => {
          if (selectedChapterCommentId) deleteChapterComment(selectedChapterCommentId);
        }}
      />

      <ManuscriptCommentComposer
        editor={getManuscriptEditor()}
        open={commentComposerOpen}
        onClose={() => setCommentComposerOpen(false)}
        onSubmit={({ text, type, anchor, blockId }) => {
          addChapterComment(text, type, { anchor, blockId });
        }}
      />

      <Dialog
        open={showFeedback}
        onOpenChange={(next) => {
          if (!next) setShowFeedback(false);
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Send feedback</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <span className="text-xs font-medium text-muted-foreground">Feedback type</span>
                <Select value={feedbackType} onValueChange={setFeedbackType}>
                  <SelectTrigger aria-label="Feedback type" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FEEDBACK_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <span className="text-xs font-medium text-muted-foreground">Rating (optional)</span>
                <Select
                  value={feedbackRating || "none"}
                  onValueChange={(value) => setFeedbackRating(value === "none" ? "" : value)}
                >
                  <SelectTrigger aria-label="Feedback rating" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No rating</SelectItem>
                    {FEEDBACK_RATING_OPTIONS.map((value) => (
                      <SelectItem key={value} value={String(value)}>
                        {value} / 5
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Textarea
              aria-label="Feedback message"
              placeholder="Share what worked or what needs improvement"
              value={feedbackMessage}
              onChange={(event) => setFeedbackMessage(event.target.value)}
            />
            <Input
              aria-label="Feedback email"
              placeholder="Email (optional)"
              value={feedbackEmail}
              onChange={(event) => setFeedbackEmail(event.target.value)}
            />
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={feedbackAttachScreenshot}
                onChange={(event) => setFeedbackAttachScreenshot(event.target.checked)}
              />
              Attach a screenshot of the app
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFeedback(false)}>
              Cancel
            </Button>
            <Button
              disabled={feedbackSubmitting || feedbackMessage.trim().length < 5}
              onClick={async () => {
                setFeedbackSubmitting(true);
                try {
                  let screenshotBase64: string | null = null;
                  if (feedbackAttachScreenshot) {
                    const captured = await window.desktopV2Api
                      .captureFeedbackScreenshot()
                      .catch(() => null);
                    screenshotBase64 = captured?.dataBase64 ?? null;
                  }
                  await window.desktopV2Api.submitFeedback({
                    feedbackEndpoint: book.feedbackEndpoint || "",
                    feedbackType,
                    message: feedbackMessage.trim(),
                    rating: feedbackRating || null,
                    email: feedbackEmail || null,
                    appArea: "desktop-v2",
                    pagePath: "/desktop-v2",
                    chapterId: activeChapter.id,
                    activeTab,
                    ...(screenshotBase64 ? { screenshotBase64 } : {}),
                  });
                  setShowFeedback(false);
                  setFeedbackType("general");
                  setFeedbackMessage("");
                  setFeedbackEmail("");
                  setFeedbackRating("");
                  setFeedbackAttachScreenshot(false);
                  setStatus("Feedback sent");
                } catch (error) {
                  setStatus(userFacingError(error, "generic"));
                } finally {
                  setFeedbackSubmitting(false);
                }
              }}
            >
              {feedbackSubmitting ? "Submitting…" : "Submit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BookshelfDialog
        open={showOpenFromCloud}
        onClose={() => {
          if (openingCloudBookId) return;
          setShowOpenFromCloud(false);
        }}
        onSelectBook={(bookId) => void openBookFromCloudById(bookId)}
        openingBookId={openingCloudBookId}
        onImportBytes={(bytes) => importFromBackupBytes(bytes)}
      />

      <BookTypePickerDialog
        open={newBookPickerOpen}
        onOpenChange={setNewBookPickerOpen}
        onConfirm={(options) => void createNewProject(options)}
      />

      {book ? (
        <TranslateBookDialog
          open={translateDialogOpen}
          book={book}
          analysisModel={analysisModel}
          onClose={() => setTranslateDialogOpen(false)}
          onStatus={setStatus}
          onBookChange={(nextBook) => {
            updateBook(() => nextBook);
            bumpManuscriptReload();
          }}
        />
      ) : null}

      {book ? (
        <MoveChapterDialog
          open={moveChapterSourceId !== null}
          sourceChapter={book.chapters.find((chapter) => chapter.id === moveChapterSourceId) ?? null}
          chapters={book.chapters}
          onClose={() => setMoveChapterSourceId(null)}
          onMove={(targetChapterId, position) => {
            if (!moveChapterSourceId) return;
            moveChapterRelative(moveChapterSourceId, targetChapterId, position);
          }}
        />
      ) : null}

      <VariationReasonDialog
        open={pendingVariationReason !== null}
        onCancel={() => setPendingVariationReason(null)}
        onConfirm={(reason) => {
          const pending = pendingVariationReason;
          setPendingVariationReason(null);
          if (!pending) return;
          if (pending.kind === "generate") {
            void generateChapterVariationRequest(
              pending.instructions,
              pending.options,
              reason
            );
            return;
          }
          applyChatBookMutations([pending.mutation], {
            ...pending.meta,
            reason,
          });
        }}
      />

      <RemoveEntityDialog
        open={pendingCharacterRemoval !== null}
        entityLabel="character"
        entityName={pendingCharacterRemoval?.name}
        reasonId="remove-character-reason"
        reasonPrompt="Why is this not a character?"
        placeholder='e.g. "Capitalized time phrase, not a person" or "Organization, not an individual"'
        description={
          <>
            Remove <strong>{pendingCharacterRemoval?.name}</strong> from the book roster. Your reason helps
            future character extract avoid re-adding them and improves later roster versions.
          </>
        }
        examples="Examples: not a person, duplicate of another character, scrape error, organization, place, or time reference."
        onClose={() => setPendingCharacterRemoval(null)}
        onConfirm={(reason) => {
          if (!pendingCharacterRemoval) return;
          removeCharacterFromRoster(pendingCharacterRemoval.id, reason);
        }}
      />

      <RemoveEntityDialog
        open={pendingLocationRemoval !== null}
        entityLabel="location"
        entityName={pendingLocationRemoval?.name}
        reasonId="remove-location-reason"
        reasonPrompt="Why is this not a significant location?"
        placeholder='e.g. "Incidental mention, not a setting" or "Character name, not a place"'
        description={
          <>
            Remove <strong>{pendingLocationRemoval?.name}</strong> from the book gazetteer. Your reason helps
            future location extract avoid re-adding it and improves later roster versions.
          </>
        }
        examples="Examples: not a place, duplicate of another location, scrape error, person, organization, or time reference."
        onClose={() => setPendingLocationRemoval(null)}
        onConfirm={(reason) => {
          if (!pendingLocationRemoval) return;
          removeLocationFromRoster(pendingLocationRemoval.id, reason);
        }}
      />

      <RemoveEntityDialog
        open={pendingTimelineBeatRemoval !== null}
        entityLabel="timeline beat"
        confirmLabel="Remove beat"
        entityName={pendingTimelineBeatRemoval?.summary}
        reasonId="remove-beat-reason"
        reasonPrompt="Why is this not a major beat?"
        placeholder='e.g. "Routine transition, not a pivot" or "Duplicate of another beat"'
        description={
          <>
            Remove <strong>{pendingTimelineBeatRemoval?.summary}</strong> from the book timeline. Your reason
            helps future extract avoid re-adding it.
          </>
        }
        examples="Examples: incidental time reference, duplicate, scrape error, or minor stage business."
        onClose={() => setPendingTimelineBeatRemoval(null)}
        onConfirm={(reason) => {
          if (!pendingTimelineBeatRemoval) return;
          removeTimelineBeatFromRoster(pendingTimelineBeatRemoval.id, reason);
        }}
      />

      <RemoveEntityDialog
        open={pendingThemeRemoval !== null}
        entityLabel={pendingThemeRemoval?.kind ?? "entry"}
        entityName={
          pendingThemeRemoval?.theme?.name ??
          pendingThemeRemoval?.motif?.name ??
          pendingThemeRemoval?.symbol?.name ??
          null
        }
        reasonId="remove-theme-entity-reason"
        placeholder='e.g. "Not present in the manuscript" or "Duplicate of another theme"'
        description={
          <>
            Remove{" "}
            <strong>
              {pendingThemeRemoval?.theme?.name ??
                pendingThemeRemoval?.motif?.name ??
                pendingThemeRemoval?.symbol?.name}
            </strong>{" "}
            from the book roster. Your reason helps future extract avoid re-adding it.
          </>
        }
        onClose={() => setPendingThemeRemoval(null)}
        onConfirm={(reason) => {
          removeThemeEntityFromRoster(reason);
        }}
      />

      {!isAccountPrimaryRuntime() ? (
      <AlertDialog
        open={showLibraryOverrideModal}
        onOpenChange={(next) => {
          if (next || libraryOverrideBusy) return;
          setLibraryOverrideError(null);
          setShowLibraryOverrideModal(false);
        }}
      >
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Replace with library copy?</AlertDialogTitle>
            <AlertDialogDescription>
              This will discard unsaved changes on this computer and reload the book from your online library.
              Chapter text, titles, order, and comments will match the library. Story Craft panels and assistant
              chat on this device are kept where possible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {dirty ? (
            <p className="rounded-sm border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              You have unsaved local edits. They cannot be recovered after you continue.
            </p>
          ) : null}
          {libraryOverrideError ? (
            <p className="rounded-sm border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {libraryOverrideError}
            </p>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={libraryOverrideBusy}
              onClick={() => setLibraryOverrideError(null)}
            >
              Cancel
            </AlertDialogCancel>
            <Button
              variant="default"
              className="bg-destructive text-white hover:bg-destructive/90"
              disabled={libraryOverrideBusy}
              onClick={() => void confirmLibraryOverride()}
            >
              {libraryOverrideBusy ? "Loading…" : "Replace local copy"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      ) : null}

      <Dialog
        open={showShortcutHelp}
        onOpenChange={(next) => {
          if (!next) setShowShortcutHelp(false);
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Keyboard shortcuts</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm [&_strong]:mt-1.5 [&_strong]:block [&>div:first-child_strong]:mt-0">
            <div>
              <strong>File</strong>
            </div>
            <div>Cmd/Ctrl+N — New project · Cmd/Ctrl+O — Open · Cmd/Ctrl+S — Save · Cmd/Ctrl+Shift+S — Save As</div>
            <div>Cmd/Ctrl+I — Import · Cmd/Ctrl+Shift+D — Export DOCX · Cmd/Ctrl+Shift+P — Export PDF</div>
            <div>
              <strong>Edit</strong>
            </div>
            <div>Cmd/Ctrl+F — Find · Cmd/Ctrl+Shift+H — Replace</div>
            <div>
              <strong>View</strong>
            </div>
            <div>Cmd/Ctrl+1 — Toggle chapters · Cmd/Ctrl+2 — Toggle assistant · Cmd/Ctrl+Shift+F — Focus mode · Cmd/Ctrl+Alt+N — Narrator mode</div>
            <div>Cmd/Ctrl+= / Cmd/Ctrl+- — Zoom · Cmd/Ctrl+0 — Reset layout · Ctrl/Cmd+Alt+[ / ] — Narrow/widen chapters · Shift+same — Assistant width</div>
            <div>
              <strong>Chapter</strong>
            </div>
            <div>Alt+↑ / Alt+↓ — Move chapter · Cmd/Ctrl+Shift+N — New · Cmd/Ctrl+Shift+U — Duplicate · Cmd/Ctrl+Backspace — Delete</div>
            <div>
              <strong>Tools</strong>
            </div>
            <div>Cmd/Ctrl+Shift+R — Story Craft · Cmd/Ctrl+Shift+C — Open chat · Cmd/Ctrl+Shift+M — Chapter Brief · Cmd/Ctrl+Shift+E — Extract characters</div>
          </div>
          <DialogFooter showCloseButton />
        </DialogContent>
      </Dialog>
        </>
      )}
    </div>
  );
}
